<template>
  <div class="m-page">
    <div v-if="!canAccess" class="m-no-access">
      <el-empty description="当前账号无库位查看权限，无法使用盘点导航" />
    </div>

    <template v-else>
      <p class="m-intro">
        按仓库进入各级库位；点「库存」打开清单。开启「盘点模式」可核对、填实盘、报溢/报损/移库（报溢报损须管理员审批后入账）；点「确认本库位已盘查」仅在本库位盖章（记录人与时间）。若现场仅有仓库料号而系统尚无该料号，请用「未知料号·申请入库」提交，由桌面端匹配材料身份后再正式入库。
      </p>
      <p v-if="canReviewApprovals" class="m-intro m-intro--link">
        <router-link :to="mPath('inventory-approvals')">待审批的出入库申请 →</router-link>
      </p>

      <div class="m-toolbar">
        <label class="m-label">仓库</label>
        <el-select
          v-model="selectedWarehouseId"
          placeholder="选择仓库"
          filterable
          class="m-toolbar-select"
          @change="handleWarehouseChange"
        >
          <el-option
            v-for="w in warehouseList"
            :key="w.location_id"
            :label="w.location_name"
            :value="w.location_id"
          />
        </el-select>
        <el-button class="m-toolbar-btn" :loading="loading" @click="refreshWarehouseLayout">刷新</el-button>
        <el-input
          v-model="locationJumpInput"
          clearable
          placeholder="库位编码或名称"
          class="m-jump-input"
          @clear="clearLocationJump"
          @keyup.enter="jumpToLocation"
        />
        <div class="m-jump-actions">
          <el-button
            type="primary"
            class="m-toolbar-btn"
            :loading="locationJumpLoading"
            @click="jumpToLocation"
          >
            跳转
          </el-button>
          <el-button
            v-if="locationJumpFilterId != null"
            class="m-toolbar-btn"
            link
            type="warning"
            @click="clearLocationJump"
          >
            取消筛选
          </el-button>
        </div>
      </div>

      <StockTakeRoundBar
        v-if="selectedWarehouseId"
        :open-round="openRound"
        :open-rounds="openRounds"
        :has-open-round="hasOpenRound"
        :can-edit="canManageRound"
        :can-admin-void-stamps="isStockTakeAdmin"
        :action-loading="roundActionLoading"
        :progress-text="mobileRoundProgressText"
        :scope-summary-fn="scopeSummary"
        @start="openMobileStartRound"
        @end="onMobileEndRound"
        @history="openMobileHistory"
        @void-before="openVoidBeforeDialog"
      />

      <el-dialog
        v-model="voidBeforeDialogVisible"
        title="批量作废盘点印记"
        width="92%"
        destroy-on-close
        append-to-body
        :z-index="5300"
        class="m-stock-take-front-dialog"
        modal-class="m-stock-take-front-overlay"
      >
        <el-alert
          type="warning"
          :closable="false"
          show-icon
          title="仅作废库位「已盘查」盖章，不清除材料「已盘」勾选。仅管理员可用。"
          style="margin-bottom: 12px"
        />
        <el-form label-position="top">
          <el-form-item label="印记时间早于" required>
            <el-date-picker
              v-model="voidBeforeForm.before"
              type="datetime"
              placeholder="选择截止时间"
              style="width: 100%"
              value-format="YYYY-MM-DD HH:mm:ss"
              teleported
              popper-class="m-stock-take-datepicker-popper"
            />
          </el-form-item>
          <el-form-item label="范围">
            <el-radio-group v-model="voidBeforeForm.scope">
              <el-radio value="warehouse">仅当前仓库</el-radio>
              <el-radio value="all">全部仓库</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="voidBeforeDialogVisible = false">取消</el-button>
          <el-button type="danger" :loading="voidBeforeLoading" @click="confirmVoidBefore">作废印记</el-button>
        </template>
      </el-dialog>

      <el-dialog v-model="mobileStartVisible" title="开启盘点" width="92%" destroy-on-close>
        <el-form label-position="top">
          <el-form-item label="轮次名称">
            <el-input v-model="mobileStartForm.name" placeholder="可选" maxlength="120" />
          </el-form-item>
          <el-form-item label="范围">
            <el-radio-group v-model="mobileStartForm.scope_mode">
              <el-radio value="warehouse">整仓</el-radio>
              <el-radio value="partial">指定区域/库位</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="mobileStartForm.scope_mode === 'partial'" label="选择范围" required>
            <el-select
              v-model="mobileStartForm.scope_location_ids"
              multiple
              filterable
              placeholder="可多选区域或库位"
              style="width: 100%"
            >
              <el-option
                v-for="loc in layoutData"
                :key="loc.location_id"
                :label="`${loc.location_name} (${loc.location_code || loc.location_id})`"
                :value="loc.location_id"
              />
            </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="mobileStartVisible = false">取消</el-button>
          <el-button type="primary" :loading="roundActionLoading" @click="confirmMobileStartRound">开启</el-button>
        </template>
      </el-dialog>

      <div class="m-crumbs">
        <span class="m-crumbs__label">当前：</span>
        <button
          v-for="(c, i) in browsePath"
          :key="`${c.id}-${i}`"
          type="button"
          class="m-crumb"
          @click="goBrowseCrumb(i)"
        >
          {{ c.name }}
        </button>
      </div>

      <div v-loading="loading" class="m-wh-list">
        <div
          v-for="item in folderChildrenWithMeta"
          :key="item.location_id"
          class="m-wh-cell"
          :class="{ 'has-children': item.hasChildren }"
          @click="onRowClick(item)"
        >
          <div class="m-wh-cell__main">
            <div class="m-wh-cell__title">
              <el-tag size="small" :type="tagType(item.location_type)">{{ typeText(item.location_type) }}</el-tag>
              <span class="m-wh-cell__name">{{ item.location_name }}</span>
            </div>
            <div class="m-wh-cell__code">{{ item.location_code || '—' }}</div>
            <div v-if="item.last_stock_take?.operator_name" class="m-wh-cell__stock-take">
              {{ item.last_stock_take.operator_name }} · {{ fmtStockTakeTime(item.last_stock_take.checked_at) }} 已盘查
            </div>
            <div class="m-wh-cell__inv">
              <span v-if="showMobileEmptyChar(item)" class="m-wh-cell__empty" aria-label="本库位无在库实物">空</span>
              <template v-else>
                <div v-if="invTotalsSummary(item)" class="m-wh-cell__inv-total">{{ invTotalsSummary(item) }}</div>
                <template v-if="cardInventoryLines(item).length">
                  <div
                    v-for="(ln, idx) in cardInventoryLines(item)"
                    :key="previewLineKey(ln, idx)"
                    class="m-wh-cell__inv-line"
                    :class="{
                      'm-wh-cell__inv-line--pending': isPreviewPendingIntake(ln),
                      'm-wh-cell__inv-line--rejected': isPreviewRejectedIntake(ln),
                      'm-wh-cell__inv-line--unchecked': isPreviewUncheckedStock(ln)
                    }"
                  >
                    <span class="m-wh-cell__inv-code"
                      >{{ ln.warehouse_manual_code || ln.material_identity_code || '—'
                      }}<ColorCardMark :row="ln"
                    /></span>
                    <span class="m-wh-cell__inv-dot">·</span>
                    <span class="m-wh-cell__inv-color">{{
                      isPreviewRejectedIntake(ln)
                        ? '已驳回'
                        : isPreviewPendingIntake(ln)
                          ? '待确认'
                          : previewLineColorText(ln)
                    }}</span>
                    <span class="m-wh-cell__inv-dot">·</span>
                    <span class="m-wh-cell__inv-qty"
                      >{{ fmtQty(ln.quantity) }}<MeterActualMark :row="ln" />码</span
                    >
                    <span class="m-wh-cell__inv-dot">·</span>
                    <span class="m-wh-cell__inv-rolls">{{ ln.branch_rolls }}支</span>
                    <span
                      v-if="overviewNotesHint(ln)"
                      class="m-wh-cell__inv-notes"
                      :title="overviewNotesHint(ln)"
                      >备</span
                    >
                    <span
                      v-if="isPreviewLineMarkedStockTaken(ln)"
                      class="m-wh-cell__st-tick"
                      title="移动端已勾选已盘"
                      >✓</span
                    >
                    <span
                      v-else-if="isPreviewRejectedIntake(ln)"
                      class="m-wh-cell__st-rejected"
                      title="申请已驳回，打开库存清单处理"
                      >驳回</span
                    >
                    <span
                      v-else-if="isPreviewPendingIntake(ln)"
                      class="m-wh-cell__st-pending"
                      title="未知料号入库申请待桌面确认"
                      >待</span
                    >
                  </div>
                  <div v-if="item.inventory_preview_truncated" class="m-wh-cell__inv-more">…还有明细未列出</div>
                </template>
                <template v-else-if="!invTotalsSummary(item)">{{ invHint(item) }}</template>
              </template>
            </div>
          </div>
          <div class="m-wh-cell__actions" @click.stop>
            <el-button v-if="item.hasChildren" size="small" type="primary" @click="enterFolder(item)">
              进入
            </el-button>
            <el-button v-if="item.hasChildren" size="small" @click="openInventory(item)">下级汇总</el-button>
            <el-button v-else type="success" size="small" @click="openInventory(item)">库存</el-button>
            <el-button
              v-if="canOpenRowInventory(item)"
              type="success"
              plain
              size="small"
              @click="openRowInventory(item)"
            >
              本排{{ rowKeyOfLoc(item) ? ` ${rowKeyOfLoc(item)}` : '' }}
            </el-button>
            <el-button
              v-if="canStampOnCard(item) && !isLocationStamped(item)"
              type="primary"
              size="small"
              :loading="cardStampLoadingId === item.location_id"
              @click="stampFromCard(item)"
            >
              确认已盘查
            </el-button>
            <span v-else-if="canStampOnCard(item) && isLocationStamped(item)" class="m-wh-cell__stamped">已盘查</span>
          </div>
        </div>

        <el-empty
          v-if="!loading && folderChildrenWithMeta.length === 0"
          :description="selectedWarehouseId ? '当前层级暂无库位' : '请选择仓库或输入库位编码搜索'"
        />
      </div>
    </template>

    <el-drawer
      v-model="invDrawerVisible"
      :title="invDrawerTitle"
      direction="btt"
      size="92%"
      class="m-inv-drawer"
      append-to-body
      destroy-on-close
      :close-on-click-modal="true"
      @closed="onDrawerClosed"
    >
      <div ref="invDrawerBodyRef" class="m-inv-drawer-body">
      <div v-if="invScopeNode" class="m-drawer-head">
        <p class="m-inv-scope">
          <template v-if="invScopeMode === 'row' && invScopeRowKey">
            本排 <strong>{{ invScopeRowKey }}</strong>
            · 共 {{ invSubtreeIds.length }} 个库位
          </template>
          <template v-else>
            {{ invScopeNode.location_code }} · {{ typeText(invScopeNode.location_type) }}
            <template v-if="invSubtreeIds.length > 1"> · 共 {{ invSubtreeIds.length }} 个位置</template>
          </template>
        </p>
        <p v-if="invScopeMode === 'row' && invScopeRowKey" class="m-inv-row-hint">
          清单已合并同排库位（如 {{ invScopeRowKey }}-0…），方便点数；盖「整排章」后各库位都会盖上，无需逐个盖。
          <el-button
            v-if="invScopeSeedLoc"
            link
            type="primary"
            size="small"
            @click="openInventory(invScopeSeedLoc)"
          >
            仅看当前库位
          </el-button>
        </p>
        <p v-else-if="canExpandCurrentToRow" class="m-inv-row-hint">
          <el-button link type="primary" size="small" @click="expandCurrentScopeToRow">
            扩大到本排 {{ rowKeyOfLoc(invScopeNode) }}（{{ rowSiblingCountOf(invScopeNode) }} 库位）
          </el-button>
        </p>
        <p v-if="invScopeLastStockTake?.operator_name" class="m-inv-last-take">
          {{ invScopeLastStockTake.operator_name }} · {{ fmtStockTakeTime(invScopeLastStockTake.checked_at) }} 已盘查
        </p>
        <div class="m-drawer-toolbar">
          <el-button size="small" plain :loading="invLoading" @click="refreshInventoryDrawer">
            刷新清单与版面
          </el-button>
        </div>
        <div class="m-st-mode">
          <span>盘点模式</span>
          <el-switch v-model="stockTakeMode" :disabled="!canUseStockTakePanel" />
        </div>
        <p v-if="stockTakeMode && !invScopeInOpenRound" class="m-warn">
          当前库位不在进行中的盘点范围内，请管理员按区域开启盘点后再盖章/勾选。
        </p>
        <p v-if="stockTakeMode && !canUseStockTakePanel" class="m-warn">
          无库存资料编辑或库存作业权限，仅可浏览，不能勾选盘点或保存盘点单
        </p>
        <p v-else-if="stockTakeMode && canSaveStockTake && !canAdjustStock" class="m-warn">
          当前账号可保存盘点单；报溢、报损、移库与新增库存需要「库存作业」权限
        </p>
        <div v-if="stockTakeMode && invScopeInOpenRound && (canSaveStockTake || canAdjustStock)" class="m-st-actions">
          <el-button
            v-if="canSaveStockTake"
            type="primary"
            size="small"
            :loading="savingSession"
            :disabled="!canStampCurrentScope"
            @click="saveStockTakeSession"
          >
            {{ stampActionLabel }}
            <template v-if="checkableInvCount">
              （{{ stampedInvCount }}/{{ checkableInvCount }}）
            </template>
          </el-button>
          <el-button
            v-if="canSaveStockTake && invScopeLastStockTake?.session_id && !isMultiLocationScope"
            type="danger"
            plain
            size="small"
            @click="voidCurrentScopeStamp"
          >
            作废本库位印记
          </el-button>
          <el-button
            v-if="canSaveStockTake"
            type="danger"
            size="small"
            :loading="reCountLoading"
            @click="reCountCurrentScope"
          >
            重盘
          </el-button>
          <el-button v-if="canAdjustStock" size="small" @click="addDialogOpen = true">新增本区库存</el-button>
          <el-button
            v-if="canSaveStockTake"
            type="warning"
            plain
            size="small"
            @click="openIntakeDialog"
          >
            未知料号·申请入库
          </el-button>
        </div>
        <p v-if="stockTakeMode" class="m-st-hint">
          「{{ stampActionLabel }}」须<strong>本范围全部材料勾选「已盘」</strong>，且<strong>无待处理的未知料号入库申请</strong>后才能盖章。
          <template v-if="isMultiLocationScope">
            整排/多库位盖章后，范围内<strong>每个库位</strong>都会留下盘查印记。
          </template>
          灰色行为待桌面确认的申请，计入合计但不计入「已盘」。实物不在本库位的，请先<strong>移库</strong>或<strong>报损</strong>。「未知料号·申请入库」不会在移动端直接增加库存。
        </p>
        <p v-if="stockTakeMode && invScopeInOpenRound && (scopeTotalQty > 0 || pendingIntakeCount)" class="m-st-summary">
          本范围合计 <strong>{{ fmtQty(scopeTotalQty) }}</strong> 码 · <strong>{{ scopeTotalRolls }}</strong> 支
          <template v-if="pendingIntakeCount">
            （含待入库申请 {{ pendingIntakeCount }} 条：{{ fmtQty(pendingIntakeQty) }} 码 · {{ pendingIntakeRolls }} 支）
          </template>
        </p>
        <p v-if="stockTakeMode && invScopeInOpenRound && pendingIntakeCount" class="m-warn">
          尚有 {{ pendingIntakeCount }} 条未知料号入库申请待桌面处理，处理完成前不可盖章。
        </p>
        <p v-if="stockTakeMode && invScopeInOpenRound && uncheckedInvRows.length" class="m-warn">
          尚有 {{ uncheckedInvRows.length }} 条未勾「已盘」：{{ uncheckedInvPreviewText }}。全部勾选后方可盖章。
        </p>
        <div class="m-inv-pane-tabs" role="tablist">
          <button
            type="button"
            class="m-inv-pane-tab"
            :class="{ active: invDrawerPane === 'list' }"
            role="tab"
            :aria-selected="invDrawerPane === 'list'"
            @click="invDrawerPane = 'list'"
          >
            对照清单
          </button>
          <button
            type="button"
            class="m-inv-pane-tab"
            :class="{ active: invDrawerPane === 'detail' }"
            role="tab"
            :aria-selected="invDrawerPane === 'detail'"
            @click="invDrawerPane = 'detail'"
          >
            盘点明细
          </button>
        </div>
        <p class="m-inv-pane-hint">点标签切换页面；整页上下滑动查看。清单点某一行进明细。</p>
      </div>

      <div class="m-inv-pane-swipe" :class="{ 'is-detail': invDrawerPane === 'detail' }">
        <!-- 对照清单：与库位卡片同口径；仓库料号按数字升序 -->
        <div
          v-show="invDrawerPane === 'list'"
          ref="invPaneSwipeRef"
          class="m-inv-pane m-inv-pane--list"
          v-loading="invLoading"
        >
          <p v-if="invRowsSorted.length" class="m-inv-list-total">
            合计 {{ fmtQty(scopeTotalQty) }}码 · {{ scopeTotalRolls }}支（{{ invRowsSorted.length }}种）
          </p>
          <div
            v-for="r in invRowsSorted"
            :key="invRowKey(r)"
            class="m-inv-list-line"
            :class="{
              'm-inv-list-line--pending': isPendingIntakeRow(r),
              'm-inv-list-line--rejected': isRejectedIntakeRow(r),
              'm-inv-list-line--unchecked': isOverviewUnchecked(r),
              'm-inv-list-line--checked': isOverviewChecked(r)
            }"
            @click="openDetailForRow(r)"
          >
            <span
              v-if="isMultiLocationScope && overviewLocationCode(r)"
              class="m-inv-list-line__loc"
              >{{ overviewLocationCode(r) }}</span
            >
            <span v-if="isMultiLocationScope && overviewLocationCode(r)" class="m-inv-list-line__dot"
              >·</span
            >
            <span class="m-inv-list-line__code">{{ overviewDisplayCode(r) }}</span>
            <span class="m-inv-list-line__dot">·</span>
            <span class="m-inv-list-line__color">{{ overviewDisplayColor(r) }}</span>
            <span class="m-inv-list-line__dot">·</span>
            <span class="m-inv-list-line__qty">{{ fmtQty(lineBook(r)) }}码</span>
            <span class="m-inv-list-line__dot">·</span>
            <span class="m-inv-list-line__rolls">{{ overviewDisplayRolls(r) }}支</span>
            <span
              v-if="overviewNotesHint(r)"
              class="m-inv-list-line__notes"
              :title="overviewNotesHint(r)"
              >备</span
            >
            <span v-if="isOverviewChecked(r)" class="m-inv-list-line__tick">✓</span>
            <span
              v-else-if="isRejectedIntakeRow(r)"
              class="m-inv-list-line__rejected"
              @click.stop="openRejectedIntakeDialog(r)"
              >（驳回）</span
            >
            <span v-else-if="isPendingIntakeRow(r)" class="m-inv-list-line__pending">待</span>
          </div>
          <el-empty v-if="!invLoading && invRowsSorted.length === 0" description="该范围内暂无库存记录" />
          <p v-if="invRowsSorted.length" class="m-inv-list-tip">点某一行进入「盘点明细」</p>
        </div>

        <!-- 盘点明细：单条翻页 -->
        <div v-show="invDrawerPane === 'detail'" class="m-inv-pane m-inv-pane--detail">
        <div v-if="invRowsSorted.length > 0" class="m-inv-pager">
          <el-button size="small" plain class="m-inv-back-list" @click="invDrawerPane = 'list'">
            ← 对照清单
          </el-button>
          <el-select
            v-model="invJumpKey"
            filterable
            placeholder="跳转到料号"
            size="small"
            class="m-inv-pager__select"
          >
            <el-option
              v-for="(r, idx) in invRowsSorted"
              :key="invRowKey(r)"
              :label="invJumpOptionLabel(r, idx)"
              :value="invRowKey(r)"
            />
          </el-select>
          <div class="m-inv-pager__nav">
            <el-button size="small" :disabled="invPageIndex <= 0" @click="invPagePrev">上一条</el-button>
            <span class="m-inv-pager__pos">{{ invPageIndex + 1 }} / {{ invRowsSorted.length }}</span>
            <el-button
              size="small"
              :disabled="invPageIndex >= invRowsSorted.length - 1"
              @click="invPageNext"
            >
              下一条
            </el-button>
          </div>
        </div>

      <div ref="invDetailCardsRef" v-loading="invLoading" class="m-inv-cards">
        <template v-if="invCurrentRow">
          <div
            :key="invRowKey(invCurrentRow)"
            class="m-inv-card"
            :class="{
              'st-on': stockTakeMode,
              'st-pending': isPendingIntakeRow(invCurrentRow),
              'st-rejected': isRejectedIntakeRow(invCurrentRow)
            }"
          >
            <div v-if="isPendingIntakeRow(invCurrentRow)" class="m-inv-pending-badge">
              待入库申请 #{{ invCurrentRow.intake_request_id }} · 桌面确认后入账
            </div>
            <div
              v-if="isPendingIntakeRow(invCurrentRow) && canCancelPendingIntake(invCurrentRow)"
              class="m-inv-pending-actions"
              @click.stop
            >
              <el-button
                type="danger"
                plain
                size="small"
                :loading="intakeCancellingId === invCurrentRow.intake_request_id"
                @click="cancelPendingIntake(invCurrentRow)"
              >
                撤销申请
              </el-button>
            </div>
            <div v-if="isRejectedIntakeRow(invCurrentRow)" class="m-inv-rejected-badge">
              申请 #{{ invCurrentRow.intake_request_id }} 已驳回 ·
              <button type="button" class="m-inv-reject-link" @click="openRejectedIntakeDialog(invCurrentRow)">
                （驳回）查看原因
              </button>
            </div>
            <div class="m-inv-card__line">
              <span class="m-inv-code"
                >{{
                  isIntakePlaceholderRow(invCurrentRow)
                    ? invCurrentRow.warehouse_manual_code || '未知料号'
                    : invCurrentRow.material_identity?.material_identity_code || '—'
                }}<ColorCardMark
                  v-if="!isIntakePlaceholderRow(invCurrentRow)"
                  :row="invCurrentRow"
              /></span>
              <span
                v-if="overviewNotesHint(invCurrentRow)"
                class="m-inv-list-line__notes"
                :title="overviewNotesHint(invCurrentRow)"
                >备</span
              >
              <span class="m-inv-qty">
                {{ isIntakePlaceholderRow(invCurrentRow) ? '申请' : '账面' }}
                {{ fmtQty(lineBook(invCurrentRow))
                }}<MeterActualMark :row="invCurrentRow" /> 码
                <MobileDispatchPendingHint
                  v-if="!isIntakePlaceholderRow(invCurrentRow)"
                  :row="invCurrentRow"
                  :fmt-qty="fmtQty"
                />
                <template v-if="lineBranchRollsDisplay(invCurrentRow) != null">
                  · {{ lineBranchRollsDisplay(invCurrentRow) }} 支</template
                >
              </span>
            </div>
            <template v-if="!isIntakePlaceholderRow(invCurrentRow)">
            <div class="m-inv-card__muted">
              {{ invCurrentRow.material_identity?.material_category?.internal_name || '' }}
              <template
                v-if="
                  invCurrentRow.material_identity?.color?.internal_name_cn ||
                  invCurrentRow.material_identity?.color?.internal_name_en
                "
              >
                ·
                {{
                  [
                    invCurrentRow.material_identity.color.internal_name_cn,
                    invCurrentRow.material_identity.color.internal_name_en
                  ]
                    .filter(Boolean)
                    .join(' / ')
                }}
              </template>
            </div>
            <div v-if="rowSpecText(invCurrentRow)" class="m-inv-card__spec">
              规格 {{ rowSpecText(invCurrentRow) }}
            </div>
            <div class="m-inv-card__line">
              <span class="muted">批号 {{ invCurrentRow.material_batch?.system_batch_number || '—' }}</span>
            </div>
            </template>
            <div v-else-if="isRejectedIntakeRow(invCurrentRow)" class="m-inv-card__muted">
              该申请已被驳回 · 可查看原因后重新申请或删除
            </div>
            <div v-else class="m-inv-card__muted">
              系统尚无料号 · 等待桌面匹配材料身份后入库
            </div>
            <div class="m-inv-card__line">
              <span class="muted"
                >库位
                {{
                  invCurrentRow.storage_location?.location_name ||
                  invCurrentRow.storage_location?.location_code ||
                  '—'
                }}</span
              >
            </div>
            <div
              v-if="invCurrentRow.warehouse_manual_code && !isIntakePlaceholderRow(invCurrentRow)"
              class="m-inv-card__muted"
            >
              仓库编号 {{ invCurrentRow.warehouse_manual_code }}
            </div>
            <div
              v-if="
                !isIntakePlaceholderRow(invCurrentRow) &&
                !canEditInvNotes &&
                (invCurrentRow.quality_notes || invCurrentRow.storage_notes)
              "
              class="m-inv-card__notes-ro"
            >
              <p v-if="invCurrentRow.quality_notes">品质 {{ invCurrentRow.quality_notes }}</p>
              <p v-if="invCurrentRow.storage_notes">存储 {{ invCurrentRow.storage_notes }}</p>
            </div>
            <div
              v-if="isIntakePlaceholderRow(invCurrentRow) && invCurrentRow.applicant_notes"
              class="m-inv-card__muted"
            >
              备注 {{ invCurrentRow.applicant_notes }}
            </div>
            <div
              v-if="isRejectedIntakeRow(invCurrentRow)"
              class="m-inv-rejected-actions"
              @click.stop
            >
              <el-button type="warning" size="small" @click="openRejectedIntakeDialog(invCurrentRow)">
                查看驳回原因
              </el-button>
              <el-button type="primary" size="small" @click="reapplyRejectedIntake(invCurrentRow)">
                重新申请
              </el-button>
              <el-button size="small" @click="deleteRejectedIntake(invCurrentRow)">删除申请</el-button>
            </div>

            <div
              v-if="invCurrentRow.inventory_id && invCurrentRow.material_batch?.batch_id"
              class="m-inv-card__branch"
              @click.stop
            >
              <div v-if="canEditMaterialBatch" class="m-st-row m-branch-enable-row">
                <span class="m-st-label">分支</span>
                <el-switch
                  :model-value="!!invCurrentRow.material_batch.enable_branch_management"
                  :loading="branchToggleLoading === invCurrentRow.inventory_id"
                  inline-prompt
                  active-text="开"
                  inactive-text="关"
                  @change="(v) => onToggleBranchMgmt(invCurrentRow, v)"
                />
              </div>
              <div class="m-inv-branch-actions">
                <el-button
                  link
                  type="primary"
                  size="small"
                  :loading="branchQuickLoading === invCurrentRow.inventory_id"
                  @click="openBranchView(invCurrentRow)"
                >
                  查看分支
                </el-button>
                <el-button
                  v-if="canEditMaterialBatch"
                  link
                  type="success"
                  size="small"
                  :loading="branchQuickLoading === invCurrentRow.inventory_id"
                  @click="openBranchEdit(invCurrentRow)"
                >
                  录入分支
                </el-button>
              </div>
              <p v-if="!canEditMaterialBatch" class="m-inv-card__muted m-branch-hint">
                启用分支需「料号编辑」；录入本库位格子还需「库存作业」或料号编辑权限。
              </p>
              <el-alert
                v-if="invCurrentRow.material_batch?.enable_branch_management && branchMismatchRow(invCurrentRow)"
                type="warning"
                :closable="false"
                show-icon
                class="m-inv-slot-branch-alert"
              >
                本库位分支合计与账面不符：请点「录入分支」修正格子，或使用上方报溢/报损调整账面。
              </el-alert>
            </div>

            <template v-if="stockTakeMode && invScopeInOpenRound && !isIntakePlaceholderRow(invCurrentRow)">
              <div class="m-st-qty-block">
                <div class="m-st-qty-book">
                  账面数量 <strong>{{ fmtQty(lineBook(invCurrentRow)) }}</strong
                  ><MeterActualMark :row="invCurrentRow" /> 码
                  <template v-if="lineBranchRollsDisplay(invCurrentRow) != null">
                    · {{ lineBranchRollsDisplay(invCurrentRow) }} 支
                  </template>
                </div>
                <div class="m-st-row m-st-row--counted">
                  <span class="m-st-label">实盘数量</span>
                  <el-input
                    v-model="stateFor(invCurrentRow).countedStr"
                    inputmode="decimal"
                    placeholder="填写实际盘点数量"
                    size="small"
                    class="m-st-input"
                    @input="debounceCountedPersist(invCurrentRow)"
                    @blur="normalizeCounted(invCurrentRow)"
                  />
                  <span class="m-st-unit">码</span>
                </div>
                <div
                  v-if="lineVariance(invCurrentRow) != null"
                  class="m-st-var"
                  :class="varClass(lineVariance(invCurrentRow))"
                >
                  差异 {{ lineVariance(invCurrentRow) > 0 ? '+' : '' }}{{ lineVariance(invCurrentRow) }} 码
                </div>
                <div class="m-st-row">
                  <el-checkbox
                    :model-value="!!stateFor(invCurrentRow).checked"
                    @change="(v) => onStockTakeCheckboxChange(invCurrentRow, v)"
                  >
                    已盘
                  </el-checkbox>
                  <el-checkbox
                    :model-value="!!invCurrentRow.physical_run_verified_at"
                    :disabled="meterActualLoadingId === invCurrentRow.inventory_id"
                    @change="(v) => onMeterActualChange(invCurrentRow, v)"
                  >
                    跑码实数<span class="m-meter-actual-hint">（实）</span>
                  </el-checkbox>
                </div>
                <div class="m-st-btns">
                  <el-button
                    v-if="canViewStockTakeTx"
                    size="small"
                    plain
                    @click="openStockTakeTxDrawer(invCurrentRow)"
                  >
                    交易记录
                  </el-button>
                  <el-button
                    v-if="canAdjustStock"
                    size="small"
                    type="success"
                    plain
                    @click="openQtyDialog(invCurrentRow, 'overflow')"
                  >
                    报溢
                  </el-button>
                  <el-button
                    v-if="canAdjustStock"
                    size="small"
                    type="warning"
                    plain
                    @click="openQtyDialog(invCurrentRow, 'loss')"
                  >
                    报损
                  </el-button>
                  <el-button
                    v-if="canAdjustStock"
                    size="small"
                    type="primary"
                    plain
                    :disabled="!canTransferInvRow(invCurrentRow)"
                    @click="openTransferDialog(invCurrentRow)"
                  >
                    移库
                  </el-button>
                </div>
                <p v-if="canViewStockTakeTx && !canViewStockTakeTxFull" class="m-inv-card__muted m-st-tx-hint">
                  交易记录为脱敏版（不含客户/供应商名称）
                </p>
              </div>
            </template>
            <div
              v-if="
                canEditInvNotes &&
                invCurrentRow.inventory_id &&
                !isIntakePlaceholderRow(invCurrentRow)
              "
              class="m-st-notes-block"
              :class="{ 'm-st-notes-block--in-st': stockTakeMode }"
              @click.stop
            >
              <p class="m-st-notes-title">
                {{ stockTakeMode ? '材料备注（盘查时可顺手记录）' : '材料备注' }}
              </p>
              <el-form label-position="top" class="m-st-notes-form" size="small">
                <el-form-item label="品质备注">
                  <el-input
                    v-model="notesForm.quality_notes"
                    type="textarea"
                    :rows="2"
                    placeholder="瑕疵、偏黄等"
                    @input="notesDirty = true"
                  />
                </el-form-item>
                <el-form-item label="存储备注">
                  <el-input
                    v-model="notesForm.storage_notes"
                    type="textarea"
                    :rows="2"
                    placeholder="与什么放一起等"
                    @input="notesDirty = true"
                  />
                  <div class="m-color-card-edit">
                    <el-checkbox
                      :model-value="!!invCurrentRow.has_color_card"
                      :disabled="colorCardLoadingId === invCurrentRow.inventory_id"
                      @change="(v) => onColorCardChange(invCurrentRow, v)"
                    >
                      有色卡<span class="m-color-card-hint">（色卡）</span>
                    </el-checkbox>
                  </div>
                </el-form-item>
              </el-form>
              <el-button
                type="primary"
                size="small"
                :loading="notesSaving"
                :disabled="!notesDirty"
                @click="saveInvNotes(invCurrentRow)"
              >
                保存备注
              </el-button>
            </div>
          </div>
        </template>
        <el-empty v-if="!invLoading && invRowsSorted.length === 0" description="该范围内暂无库存记录" />
      </div>
        </div>
      </div>
      </div>
    </el-drawer>

    <!-- 报溢/报损：须盖在盘点抽屉之上（iOS 上默认 z-index 会低于已打开的 drawer） -->
    <el-dialog
      v-model="qtyDialogVisible"
      :title="qtyDialogTitle"
      width="90%"
      append-to-body
      align-center
      destroy-on-close
      :z-index="5000"
      class="m-stock-take-front-dialog"
      modal-class="m-stock-take-front-overlay"
      @closed="resetQtyForm"
    >
      <p v-if="qtyTargetRow" class="m-qty-cur">当前账面 <strong>{{ fmtQty(lineBook(qtyTargetRow)) }}</strong></p>
      <el-form label-position="top" class="m-qty-form">
        <el-form-item :label="qtyMode === 'overflow' ? '增加数量' : '减少数量'">
          <el-input v-model="qtyForm.deltaInput" inputmode="decimal" placeholder="必填其一" />
        </el-form-item>
        <el-form-item :label="qtyMode === 'overflow' ? '实盘数量（报溢后）' : '实盘数量（报损后）'">
          <el-input v-model="qtyForm.actualInput" inputmode="decimal" placeholder="或填此项" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="qtyForm.notes" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="qtyDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="qtySubmitting" @click="submitQtyTransaction">提交入账</el-button>
      </template>
    </el-dialog>

    <!-- 盘点明细移库：全量启用库位，可按编码/名称搜索 -->
    <el-dialog
      v-model="transferDialogVisible"
      title="移库"
      width="92%"
      append-to-body
      align-center
      destroy-on-close
      :z-index="5000"
      class="m-stock-take-front-dialog"
      modal-class="m-stock-take-front-overlay"
      @closed="resetTransferForm"
    >
      <el-form label-position="top">
        <el-form-item label="当前库位">
          <el-input :model-value="transferFromLocationLabel" readonly />
        </el-form-item>
        <el-form-item label="账面数量">
          <el-input :model-value="fmtQty(transferMaxQty)" readonly />
        </el-form-item>
        <el-form-item label="移库数量" required>
          <el-input-number
            v-model="transferForm.quantity"
            :min="0.0001"
            :max="transferMaxQty > 0 ? transferMaxQty : undefined"
            :precision="4"
            :step="1"
            controls-position="right"
            style="width: 100%"
          />
          <p class="m-muted" style="margin-top: 6px">
            默认移动本库位全部可移库数量；若只移一部分，请改小此处。
          </p>
          <p
            v-if="
              transferTargetRow?.transfer_limited_by_reservation &&
              transferMaxQty + 1e-6 < Number(transferTargetRow?.physical_quantity || 0)
            "
            class="m-muted"
          >
            派单分码后存在占用，可移库量 {{ fmtQty(transferMaxQty) }}，小于账面。
          </p>
        </el-form-item>
        <el-form-item label="新库位" required>
          <el-select
            v-model="transferForm.to_location_id"
            filterable
            clearable
            placeholder="输入编码或名称搜索，如 R24"
            style="width: 100%"
            :loading="transferLocationsLoading"
            :teleported="false"
            popper-class="m-stock-take-select-popper"
          >
            <el-option
              v-for="loc in transferLocations"
              :key="loc.location_id"
              :label="transferLocationOptionLabel(loc)"
              :value="loc.location_id"
            />
          </el-select>
          <p v-if="!transferLocationsLoading && !transferLocations.length" class="m-muted">
            未加载到库位，请重试。
            <el-button link type="primary" @click="ensureTransferLocationsLoaded(true)">刷新库位</el-button>
          </p>
          <p v-else class="m-muted" style="margin-top: 6px">
            已加载 {{ transferLocations.length }} 个启用库位（编码+名称可搜）
            <el-button link type="primary" @click="ensureTransferLocationsLoaded(true)">刷新</el-button>
          </p>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="transferForm.notes" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="transferDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :disabled="!canTransferInvRow(transferTargetRow)"
          :loading="transferSubmitting"
          @click="submitTransferDialog"
        >
          确认移库
        </el-button>
      </template>
    </el-dialog>

    <!-- 新增库存：现有料号落入指定库位 -->
    <el-dialog
      v-model="addDialogOpen"
      title="新增本范围库存"
      width="92%"
      append-to-body
      align-center
      destroy-on-close
      :z-index="5000"
      class="m-stock-take-front-dialog"
      modal-class="m-stock-take-front-overlay"
      @closed="resetAddForm"
    >
      <p class="m-muted">
        <strong>① 先查询料号</strong>核对材料信息与在库位置；
        <strong>② 查到后再确认</strong>如何处理（移库 / 新建）并填写数量。查无请走「未知料号·申请入库」。
      </p>
      <el-form label-position="top">
        <el-form-item label="放入库位（目标）" required>
          <el-select
            v-model="addForm.storage_location_id"
            placeholder="可改选本仓库其它库位"
            filterable
            :teleported="false"
            popper-class="m-stock-take-select-popper"
            style="width: 100%"
          >
            <el-option
              v-for="loc in addLocationOptions"
              :key="Number(loc.location_id)"
              :label="locationOptionLabelForAdd(loc)"
              :value="Number(loc.location_id)"
            />
          </el-select>
          <p class="m-muted m-add-loc-hint">
            默认当前盘点库位；可改为该料号已有库存的库位，或本仓库其它库位。
          </p>
        </el-form-item>

        <!-- ① 查询 -->
        <el-form-item label="① 查询料号" required>
          <div class="m-add-query-row">
            <el-input
              v-model="addBatchSearchKeyword"
              clearable
              placeholder="输入仓库料号或系统批号"
              maxlength="64"
              @input="onAddBatchKeywordInput"
              @keyup.enter="runAddBatchQuery"
            />
            <el-button type="primary" :loading="batchSearchLoading" @click="runAddBatchQuery">
              查询
            </el-button>
          </div>
        </el-form-item>

        <div v-if="addQueryStatus === 'not_found'" class="m-add-query-result">
          <p class="m-add-not-found">
            系统暂无此料号「{{ addQueriedKeyword }}」
          </p>
          <p class="m-muted m-add-loc-hint">请核对输入是否有误；确认现场为新料号后，可申请入库（桌面端再匹配材料身份）。</p>
        </div>

        <div v-else-if="addQueryStatus === 'candidates'" class="m-add-query-result">
          <p class="m-add-preview-title">找到多条相近料号，请点选一条核对</p>
          <div
            v-for="b in addQueryCandidates"
            :key="b.batch_id"
            class="m-add-candidate"
            :class="{ 'is-active': Number(addForm.material_batch_id) === Number(b.batch_id) }"
            @click="selectAddBatchCandidate(b)"
          >
            <div class="m-add-candidate-codes">
              {{ (b.manual_batch_number || '').trim() || '—' }}
              <span class="m-muted"> / {{ (b.system_batch_number || '').trim() || '—' }}</span>
            </div>
            <div class="m-add-candidate-meta m-muted">
              {{ formatIdentityLinesFromBatch(b).name }} ·
              {{ formatIdentityLinesFromBatch(b).colorCn }} ·
              {{ formatIdentityLinesFromBatch(b).spec }}
            </div>
          </div>
          <p class="m-muted m-add-loc-hint" style="margin-top: 8px">
            点选的都不是现场料号「{{ addQueriedKeyword }}」时，请走未知料号入库申请。
          </p>
          <el-button type="warning" plain size="small" @click="goIntakeFromAddNotFound">
            都不是，去申请入库
          </el-button>
        </div>

        <div
          v-if="addForm.material_batch_id && (addQueryStatus === 'found' || addQueryStatus === 'candidates')"
          v-loading="addInventoryPreviewLoading"
          class="m-add-preview"
        >
          <div v-if="addSelectedBatchIdentitySummary" class="m-add-identity">
            <p class="m-add-preview-title">料号材料信息</p>
            <p class="m-add-identity-line">
              仓库料号：{{ (addSelectedBatchCache?.manual_batch_number || '').trim() || '—' }}
            </p>
            <p class="m-add-identity-line">
              系统批号：{{ (addSelectedBatchCache?.system_batch_number || '').trim() || '—' }}
            </p>
            <p class="m-add-identity-line">材料：{{ addSelectedBatchIdentitySummary.name }}</p>
            <p class="m-add-identity-line">颜色：{{ addSelectedBatchIdentitySummary.colorCn }} / {{ addSelectedBatchIdentitySummary.colorEn }}</p>
            <p class="m-add-identity-line">规格：{{ addSelectedBatchIdentitySummary.spec }}</p>
          </div>
          <p class="m-add-preview-title">系统中该料号的在库位置（核对实物是否在此）</p>
          <el-alert
            v-if="addAtTargetStockRows.length"
            type="warning"
            show-icon
            :closable="false"
            class="m-add-alert"
            title="目标库位已有该料号在库记录，请勿重复新增；请核对实盘或使用报溢/报损。"
          />
          <div v-else-if="addPreviewDisplayRows.length" class="m-add-preview-list">
            <div
              v-for="pr in addPreviewDisplayRows"
              :key="pr.inventory_id"
              class="m-add-preview-row"
            >
              <span class="m-add-preview-loc">{{ pr.locationLabel }}</span>
              <span class="m-add-preview-qty">{{ fmtQty(pr.physical_quantity) }} 码</span>
              <el-tag v-if="pr.isTargetBin" size="small" type="success">所选目标库位</el-tag>
            </div>
          </div>
          <el-empty v-else description="该料号暂无在库数量（可报溢新建，须审批）" :image-size="56" />
        </div>

        <!-- ② 确认处理：仅查询到料号后显示 -->
        <template v-if="addForm.material_batch_id && !addAtTargetStockRows.length">
          <p class="m-add-step2-title">② 确认如何处理</p>
          <el-alert
            v-if="!addElsewhereStockRows.length"
            type="warning"
            show-icon
            :closable="false"
            class="m-add-alert"
            title="本处新建将按「报溢入库」提交，须管理员审批通过后入账（与报损报溢相同）。"
          />
          <el-form-item
            v-if="addElsewhereStockRows.length > 0"
            label="他处已有该料号时如何处理"
            required
            class="m-add-src"
          >
            <el-radio-group v-model="addPlacementMode" class="m-add-radio m-add-mode">
              <el-radio label="transfer">移库到目标（放错 / 合并；从他处扣减）</el-radio>
              <el-radio label="create">在目标报溢新建（须审批；他处不动）</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-if="addPlacementMode === 'transfer' && addElsewhereStockRows.length > 1"
            label="从哪个库位移入（来源）"
            required
            class="m-add-src"
          >
            <el-radio-group v-model="addSelectedSourceInventoryId" class="m-add-radio">
              <el-radio v-for="row in addElsewhereStockRows" :key="row.inventory_id" :label="row.inventory_id">
                {{ resolveLocationLabelForAddRow(row) }} · 账面 {{ fmtQty(row.physical_quantity) }} 码
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-alert
            v-if="addPlacementMode === 'transfer' && addElsewhereStockRows.length > 0"
            type="info"
            show-icon
            :closable="false"
            class="m-add-alert"
            title="将执行移库：默认移动来源库位全部可移库数量；若只移一部分，请在下方「移库数量」修改。格子明细写入移入侧目标库位。"
          />
          <el-alert
            v-else-if="addPlacementMode === 'create' && addElsewhereStockRows.length > 0"
            type="warning"
            show-icon
            :closable="false"
            class="m-add-alert"
            title="将按「报溢入库」提交：与报损报溢相同，须管理员审批通过后才入账；他处账面不变。请填写本处实际数量。"
          />

          <div class="m-st-branch-block">
            <div class="m-st-branch-head">
              <p class="m-st-branch-title">分支格子（可选）</p>
              <el-button link type="primary" size="small" @click="clearAddBranchGrid">清空格子</el-button>
            </div>
            <p class="m-st-branch-hint m-muted">
              现场可逐格录入本<strong>目标库位</strong>每支数量，<strong>合计自动填入下方「数量」</strong>。也可只填总数不录分支。
            </p>
            <p v-if="addBranchSumQty > 0" class="m-st-branch-sum">
              分支合计 <b>{{ fmtQty(addBranchSumQty) }}</b> 码
              <span class="m-st-branch-sum-note">（已同步到「数量」）</span>
            </p>
            <div
              v-show="addBranchActiveCell.r != null && addBranchActiveCell.c != null"
              class="m-st-br-active-preview"
              aria-live="polite"
            >
              <span class="m-st-br-active-label">{{ addBranchActivePreviewLabel }}</span>
              <span class="m-st-br-active-value">{{ addBranchActivePreviewValue }}</span>
            </div>
            <div class="m-st-br-grid-wrap" role="grid" :aria-colcount="BRANCH_GRID_COLS + 1" :aria-rowcount="BRANCH_GRID_ROWS + 1">
              <span class="m-st-br-corner" aria-hidden="true" />
              <span v-for="c in BRANCH_GRID_COLS" :key="'abr-h' + c" class="m-st-br-h">{{ colLetterAdd(c - 1) }}</span>
              <template v-for="r in BRANCH_GRID_ROWS" :key="'abr-row' + r">
                <span class="m-st-br-row-lab">{{ r }}</span>
                <div v-for="cc in BRANCH_GRID_COLS" :key="r + '-' + cc" class="m-st-br-cell-wrap">
                  <input
                    v-model="addBranchGrid[r - 1][cc - 1]"
                    type="text"
                    inputmode="decimal"
                    autocomplete="off"
                    autocorrect="off"
                    spellcheck="false"
                    class="m-st-br-input"
                    @focus="e => onAddBranchFocus(e, r - 1, cc - 1)"
                    @blur="onAddBranchBlur(r - 1, cc - 1)"
                  />
                </div>
              </template>
            </div>
          </div>

          <el-form-item :label="addQtyFieldLabel" required>
            <el-input v-model="addForm.qtyStr" inputmode="decimal" :placeholder="addQtyFieldPlaceholder" />
          </el-form-item>
          <el-form-item v-if="addPlacementMode === 'create' || !addElsewhereStockRows.length" label="跑码实数">
            <el-checkbox v-model="addForm.mark_as_meter_actual">
              此数量为跑码表实数（显示红字「实」）
            </el-checkbox>
          </el-form-item>
        </template>
      </el-form>
      <template #footer>
        <el-button @click="addDialogOpen = false">取消</el-button>
        <el-button
          v-if="addQueryStatus === 'not_found'"
          type="warning"
          @click="goIntakeFromAddNotFound"
        >
          去申请入库
        </el-button>
        <el-button
          v-else-if="addForm.material_batch_id && !addAtTargetStockRows.length"
          type="primary"
          :loading="addSubmitting"
          @click="submitAddInventory"
        >
          确定
        </el-button>
        <template v-else-if="addQueryStatus === 'candidates'">
          <el-button type="warning" @click="goIntakeFromAddNotFound">都不是，去申请入库</el-button>
          <el-button type="primary" :loading="batchSearchLoading" @click="runAddBatchQuery">
            重新查询
          </el-button>
        </template>
        <el-button
          v-else-if="addQueryStatus === 'idle'"
          type="primary"
          :loading="batchSearchLoading"
          @click="runAddBatchQuery"
        >
          查询
        </el-button>
      </template>
    </el-dialog>

    <!-- 盘点：系统尚无料号时，仅提交入库申请（桌面端匹配材料身份后入账） -->
    <el-dialog
      v-model="intakeDialogOpen"
      title="未知料号·入库申请"
      width="92%"
      append-to-body
      align-center
      destroy-on-close
      :z-index="5000"
      class="m-stock-take-front-dialog"
      modal-class="m-stock-take-front-overlay"
      @closed="resetIntakeForm"
    >
      <p class="m-muted">
        填写现场<strong>仓库料号</strong>、<strong>数量（码）</strong>与<strong>支数</strong>（可选分支格子）；桌面端匹配材料身份并确认后才会生成库存，此处<strong>不会直接入库</strong>。提交后本范围列表会出现灰色占位行，计入合计，处理完前不可盖章。
      </p>
      <el-form label-position="top">
        <el-form-item label="目标库位" required>
          <el-select
            v-model="intakeForm.storage_location_id"
            placeholder="选择库位"
            filterable
            :teleported="false"
            popper-class="m-stock-take-select-popper"
            style="width: 100%"
          >
            <el-option
              v-for="loc in addLocationOptions"
              :key="Number(loc.location_id)"
              :label="locationOptionLabelForAdd(loc)"
              :value="Number(loc.location_id)"
            />
          </el-select>
          <p class="m-muted m-add-loc-hint">默认当前盘点库位；可改选本仓库其它可用库位。</p>
        </el-form-item>
        <el-form-item label="仓库料号（现场标识）" required>
          <el-input v-model="intakeForm.warehouse_manual_code" placeholder="尚未录入系统的现场编号" maxlength="64" />
        </el-form-item>
        <div class="m-st-branch-block">
          <div class="m-st-branch-head">
            <p class="m-st-branch-title">分支格子（可选）</p>
            <el-button link type="primary" size="small" @click="clearIntakeBranchGrid">清空格子</el-button>
          </div>
          <p class="m-st-branch-hint m-muted">
            逐格录入每支数量时，<strong>合计自动填入数量</strong>，支数取有数格子数。也可只填总数与支数、不录分支。
          </p>
          <p v-if="intakeBranchSumQty > 0" class="m-st-branch-sum">
            分支合计 <b>{{ fmtQty(intakeBranchSumQty) }}</b> 码
            <span class="m-st-branch-sum-note">（已同步到「数量」）</span>
          </p>
          <div
            v-show="intakeBranchActiveCell.r != null && intakeBranchActiveCell.c != null"
            class="m-st-br-active-preview"
            aria-live="polite"
          >
            <span class="m-st-br-active-label">{{ intakeBranchActivePreviewLabel }}</span>
            <span class="m-st-br-active-value">{{ intakeBranchActivePreviewValue }}</span>
          </div>
          <div class="m-st-br-grid-wrap" role="grid" :aria-colcount="BRANCH_GRID_COLS + 1" :aria-rowcount="BRANCH_GRID_ROWS + 1">
            <span class="m-st-br-corner" aria-hidden="true" />
            <span v-for="c in BRANCH_GRID_COLS" :key="'ibr-h' + c" class="m-st-br-h">{{ colLetterAdd(c - 1) }}</span>
            <template v-for="r in BRANCH_GRID_ROWS" :key="'ibr-row' + r">
              <span class="m-st-br-row-lab">{{ r }}</span>
              <div v-for="cc in BRANCH_GRID_COLS" :key="'i' + r + '-' + cc" class="m-st-br-cell-wrap">
                <input
                  v-model="intakeBranchGrid[r - 1][cc - 1]"
                  type="text"
                  inputmode="decimal"
                  autocomplete="off"
                  autocorrect="off"
                  spellcheck="false"
                  class="m-st-br-input"
                  @focus="e => onIntakeBranchFocus(e, r - 1, cc - 1)"
                  @blur="onIntakeBranchBlur(r - 1, cc - 1)"
                />
              </div>
            </template>
          </div>
        </div>
        <el-form-item label="数量（码）" required>
          <el-input v-model="intakeForm.quantityStr" inputmode="decimal" placeholder="实盘数量" />
        </el-form-item>
        <el-form-item label="跑码实数">
          <el-checkbox v-model="intakeForm.mark_as_meter_actual">
            此数量为跑码表实数（入库后显示红字「实」）
          </el-checkbox>
        </el-form-item>
        <el-form-item label="支数">
          <el-input
            v-model="intakeForm.rollCountStr"
            inputmode="numeric"
            placeholder="选填；填分支后自动取有数格子数"
            :disabled="intakeBranchOccupiedCount > 0"
          />
        </el-form-item>
        <el-form-item label="现场备注">
          <el-input v-model="intakeForm.applicant_notes" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="intakeDialogOpen = false">取消</el-button>
        <el-button type="primary" :loading="intakeSubmitting" @click="submitIntakeRequest">提交申请</el-button>
      </template>
    </el-dialog>

    <!-- 驳回申请：原因 + 重新申请 / 删除 -->
    <el-dialog
      v-model="rejectedDialogOpen"
      title="申请已驳回"
      width="90%"
      append-to-body
      align-center
      destroy-on-close
      :z-index="5200"
      class="m-stock-take-front-dialog"
      modal-class="m-stock-take-front-overlay"
    >
      <template v-if="rejectedDialogRow">
        <p class="m-rej-meta">
          <strong>仓库料号</strong> {{ rejectedDialogRow.warehouse_manual_code || '—' }}
          · <strong>数量</strong> {{ fmtQty(rejectedDialogRow.physical_quantity) }} 码
        </p>
        <p class="m-rej-reason-label">驳回原因</p>
        <div class="m-rej-reason-box">
          {{ (rejectedDialogRow.review_notes || '').trim() || '（未填写原因）' }}
        </div>
      </template>
      <template #footer>
        <el-button :loading="rejectedDeleting" @click="deleteRejectedIntake(rejectedDialogRow)">
          删除申请
        </el-button>
        <el-button type="primary" @click="reapplyRejectedIntake(rejectedDialogRow)">重新申请</el-button>
      </template>
    </el-dialog>

    <!-- 盘点模式内查看料号交易（盖在库存抽屉之上） -->
    <el-drawer
      v-model="stTxDrawerVisible"
      title="料号交易记录"
      direction="btt"
      size="72%"
      append-to-body
      destroy-on-close
      :z-index="5100"
      class="m-stock-take-front-dialog"
      @closed="resetStockTakeTxDrawer"
    >
      <div v-loading="stTxLoading" class="m-st-tx-drawer">
        <p v-if="stTxTargetLabel" class="m-muted m-st-tx-target">{{ stTxTargetLabel }}</p>
        <p v-if="canViewStockTakeTx && !canViewStockTakeTxFull" class="m-muted">
          当前为脱敏查看：不显示客户 / 供应商名称。
        </p>
        <div v-for="tx in stTxRows" :key="tx.transaction_id" class="m-st-tx-card">
          <div class="m-st-tx-card__head">
            <span>{{ tx.transaction_number || '#' + tx.transaction_id }}</span>
            <span class="muted">{{ fmtStTxDate(tx.transaction_date) }}</span>
          </div>
          <div class="m-st-tx-line">
            类型 {{ tx.transaction_type?.type_name || tx.transaction_type_id }}
            · 变动数量 {{ formatInventoryTxChangeQty(tx) }}
          </div>
          <div v-if="formatInventoryTxLocationChange(tx)" class="m-st-tx-line">
            {{ formatInventoryTxLocationChange(tx) }}
          </div>
          <div v-if="formatInventoryTxLocationBalances(tx).length" class="m-st-tx-balances">
            <div class="m-st-tx-line">当下库存</div>
            <div
              v-for="(line, bi) in formatInventoryTxLocationBalances(tx)"
              :key="bi"
              class="m-st-tx-line m-st-tx-balance-line"
            >
              {{ line }}
            </div>
          </div>
          <div v-if="stTxOutboundLabel(tx)" class="m-st-tx-line">出库对象 {{ stTxOutboundLabel(tx) }}</div>
          <div v-if="stTxOrderContext(tx)" class="m-st-tx-line muted">{{ stTxOrderContext(tx) }}</div>
          <div v-else-if="stTxSupplier(tx)" class="m-st-tx-line muted">{{ stTxSupplier(tx) }}</div>
          <div
            v-if="
              canViewStockTakeTxFull &&
              shouldShowInventoryTxNotes(tx) &&
              tx.notes
            "
            class="m-st-tx-line muted"
          >
            {{ tx.notes }}
          </div>
        </div>
        <el-empty v-if="!stTxLoading && stTxRows.length === 0" description="暂无交易记录" />
        <div v-if="stTxPages > 1" class="m-st-tx-pager">
          <el-button size="small" :disabled="stTxPage <= 1" @click="stTxPrev">上一页</el-button>
          <span>{{ stTxPage }} / {{ stTxPages }}</span>
          <el-button size="small" :disabled="stTxPage >= stTxPages" @click="stTxNext">下一页</el-button>
        </div>
      </div>
    </el-drawer>

    <MobileBatchBranchViewDrawer
      v-model="branchViewOpen"
      :branch-details="branchViewMergedDetails"
      :physical-qty="branchPreviewDetail?.physical_quantity"
      :enable-branch="!!branchPreviewDetail?.material_batch?.enable_branch_management"
    />
    <MobileBatchBranchEditDrawer
      v-model="branchEditOpen"
      :inventory-id="branchPreviewInventoryId"
      :batch-id="branchPreviewDetail?.material_batch?.batch_id"
      :initial-branch-details="branchEditInitialBranchDetails"
      :physical-qty="branchPreviewDetail?.physical_quantity"
      @saved="onBranchSavedFromDrawer"
    />
  </div>
</template>

<script setup>
import { useMobileWorkbench } from '@/composables/useMobileWorkbench'

const { mPath } = useMobileWorkbench()

import { ref, reactive, computed, onMounted, onActivated, onDeactivated, watch, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@/store/user'
import { hasPermission } from '@/utils/permission'
import { canAccessMobileInventoryOperationApproval } from '@/utils/mobileAccess'
import { getStorageLocations } from '@/api/storageLocations'
import { fetchAllActiveStorageLocations } from '@/utils/fetchAllStorageLocations'
import {
  getWarehouseLayout,
  findWarehouseLocation,
  getWarehouseLocationCards
} from '@/api/warehouseLayout'
import {
  getMaterialInventory,
  getMaterialInventoryRecord,
  getMaterialInventorySlotTransactions,
  transferMaterialInventoryLocation,
  updateMaterialInventory,
  patchMaterialInventoryMeterActualMark,
  patchMaterialInventoryColorCardMark
} from '@/api/materialInventory'
import MeterActualMark from '@/components/MeterActualMark.vue'
import ColorCardMark from '@/components/ColorCardMark.vue'
import { collectSameRowLocations, parseLocationRowParts } from '@/utils/locationRowGroup'
import {
  formatInventoryTxOutboundObject,
  formatInventoryTxOrderContext,
  formatInventoryTxSupplier,
  formatInventoryTxChangeQty,
  formatInventoryTxLocationChange,
  formatInventoryTxLocationBalances,
  shouldShowInventoryTxNotes,
  isDispatchCompleteTransitRow
} from '@/utils/inventoryTransactionDisplay'
import { createInventoryTransaction } from '@/api/inventoryTransactions'
import {
  interpretInventoryTransactionResponse,
  notifyInventoryTransactionResult
} from '@/utils/inventoryTransactionResponse'
import { getInventoryTransactionTypes } from '@/api/inventoryTransactionTypes'
import { getUnits } from '@/api/units'
import { getMaterialBatches, getMaterialBatch, updateMaterialBatch } from '@/api/materialBatches'
import {
  BRANCH_GRID_ROWS,
  BRANCH_GRID_COLS,
  gridStringsToBranchDetailsPayload,
  sumBranchDetails,
  normalizeCellInputString,
  mergedBranchDetailsForDisplay,
  occupiedBranchCells,
  BRANCH_QTY_EPS
} from '@/utils/materialBatchBranchGrid'
import {
  patchMobileStockTakeLineMark,
  voidStockTakeSessionsBefore,
  reCountStockTakeScope
} from '@/api/inventoryStockTake'
import {
  createInventoryManualCodeIntakeRequest,
  listInventoryManualCodeIntakeRequests,
  deleteInventoryManualCodeIntakeRequest,
  cancelInventoryManualCodeIntakeRequest
} from '@/api/inventoryManualCodeIntakeRequests'
import { useStockTakeRounds } from '@/composables/useStockTakeRounds'
import StockTakeRoundBar from '@/components/inventory/StockTakeRoundBar.vue'
import MobileBatchBranchViewDrawer from '@/views/mobile/components/MobileBatchBranchViewDrawer.vue'
import MobileBatchBranchEditDrawer from '@/views/mobile/components/MobileBatchBranchEditDrawer.vue'
import { resetBodyElLoadingArtifacts } from '@/utils/elementLoadingCleanup'
import MobileDispatchPendingHint from '@/components/mobile/MobileDispatchPendingHint.vue'

defineOptions({ name: 'MobileWarehouseBrowse' })

const userStore = useUserStore()

const canViewStockTakeTx = computed(() => !!userStore.canViewInventorySlotTransactions)
const canViewStockTakeTxFull = computed(() => !!userStore.canViewInventorySlotTransactionsFull)
const stTxMaskOpts = computed(() =>
  canViewStockTakeTxFull.value ? {} : { maskCounterparty: true }
)

const stTxDrawerVisible = ref(false)
const stTxLoading = ref(false)
const stTxRows = ref([])
const stTxPage = ref(1)
const stTxPages = ref(1)
const stTxInventoryId = ref(null)
const stTxTargetLabel = ref('')

const loading = ref(false)
const warehouseList = ref([])
const selectedWarehouseId = ref(null)
const layoutData = ref([])
const browsePath = ref([])

const {
  openRound,
  openRounds,
  hasOpenRound,
  canManageRound,
  roundActionLoading,
  stampedLocationIds,
  loadOpenRound,
  startRound,
  endRound,
  stampLocation,
  stampLocationsBatch,
  voidLatestStamp,
  listRounds,
  refreshRoundProgress,
  applyOpenRoundsList,
  scopeSummary
} = useStockTakeRounds(() => selectedWarehouseId.value)

const mobileRoundProgressText = computed(() => {
  if (!hasOpenRound.value) return ''
  return `进行中 ${openRounds.value.length} 轮 · 已盖章 ${stampedLocationIds.value.length}`
})

const mobileStartVisible = ref(false)
const mobileStartForm = reactive({
  name: '',
  scope_mode: 'warehouse',
  scope_location_ids: []
})

const isStockTakeAdmin = computed(
  () => !!(userStore.user?.is_admin || userStore.user?.is_superuser)
)
const voidBeforeDialogVisible = ref(false)
const voidBeforeLoading = ref(false)
const voidBeforeForm = reactive({
  before: '',
  scope: 'warehouse'
})
const reCountLoading = ref(false)

/** 盘点相关确认框：盖过 z-index 5000/5200 的业务弹窗 */
const ST_MSGBOX_OPTS = {
  modalClass: 'm-stock-take-msgbox-overlay',
  customClass: 'm-stock-take-msgbox'
}

function openVoidBeforeDialog() {
  if (!isStockTakeAdmin.value) {
    ElMessage.warning('仅管理员可批量作废盘点印记')
    return
  }
  voidBeforeForm.before = ''
  voidBeforeForm.scope = 'warehouse'
  voidBeforeDialogVisible.value = true
}

async function confirmVoidBefore() {
  if (!isStockTakeAdmin.value) {
    ElMessage.warning('仅管理员可批量作废盘点印记')
    return
  }
  if (!voidBeforeForm.before) {
    ElMessage.warning('请选择印记时间截止点')
    return
  }
  const whName =
    warehouseList.value.find((w) => Number(w.location_id) === Number(selectedWarehouseId.value))
      ?.location_name || selectedWarehouseId.value
  const scopeLabel = voidBeforeForm.scope === 'warehouse' ? `当前仓库「${whName}」` : '全部仓库'
  try {
    await ElMessageBox.confirm(
      `将作废 ${scopeLabel} 中，印记时间早于「${voidBeforeForm.before}」的全部盘查盖章。\n材料「已盘」勾选不会清除。\n\n确认执行？`,
      '确认批量作废印记',
      {
        type: 'warning',
        confirmButtonText: '确认作废',
        cancelButtonText: '取消',
        ...ST_MSGBOX_OPTS
      }
    )
  } catch {
    return
  }
  voidBeforeLoading.value = true
  try {
    const payload = { before: voidBeforeForm.before }
    if (voidBeforeForm.scope === 'warehouse') {
      if (!selectedWarehouseId.value) {
        ElMessage.warning('请先选择仓库')
        return
      }
      payload.warehouse_location_id = selectedWarehouseId.value
    }
    const res = await voidStockTakeSessionsBefore(payload)
    if (res?.success) {
      ElMessage.success(res.message || '已作废')
      voidBeforeDialogVisible.value = false
      await loadLayout({ preserveBrowsePath: true, silent: true })
      if (invDrawerVisible.value) await loadInvRowsForCurrentScope()
    } else {
      ElMessage.error(res?.message || '作废失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '作废失败')
  } finally {
    voidBeforeLoading.value = false
  }
}

async function reCountCurrentScope() {
  if (!canSaveStockTake.value) {
    ElMessage.warning('无权限重盘')
    return
  }
  const ids = (invSubtreeIds.value || []).map(Number).filter(Boolean)
  if (!ids.length && invScopeNode.value?.location_id != null) {
    ids.push(Number(invScopeNode.value.location_id))
  }
  if (!ids.length) {
    ElMessage.warning('当前清单无库位范围')
    return
  }
  const scopeName =
    invScopeNode.value?.location_name ||
    invScopeNode.value?.location_code ||
    `共 ${ids.length} 个库位`
  try {
    await ElMessageBox.confirm(
      `将对「${scopeName}」清单范围执行重盘：\n· 作废本范围内盘查印记\n· 清除本范围内全部「已盘」勾选\n\n可重新勾选并盖章。此操作不可撤销，确认继续？`,
      '重盘确认',
      {
        type: 'warning',
        confirmButtonText: '确认重盘',
        cancelButtonText: '取消',
        distinguishCancelAndClose: true,
        ...ST_MSGBOX_OPTS
      }
    )
  } catch {
    return
  }
  reCountLoading.value = true
  try {
    // 同时传当前清单上的库存行 ID，避免仅按库位+round 漏清一行
    const inventoryIds = (invRowsSorted.value || [])
      .map((r) => Number(r?.inventory_id))
      .filter((n) => Number.isFinite(n) && n > 0)
    const res = await reCountStockTakeScope({
      storage_location_ids: ids,
      inventory_ids: inventoryIds,
      warehouse_location_id: selectedWarehouseId.value || undefined
    })
    if (res?.success) {
      ElMessage.success(res.message || '重盘完成')
      for (const lid of ids) {
        patchLayoutLastStockTake(lid, null)
      }
      // 清空本地勾选缓存并强制按服务端重建，避免漏掉一行仍显示 ✓
      Object.keys(lineState).forEach((k) => delete lineState[k])
      lineStoreRevision.value++
      await loadInvRowsForCurrentScope()
      // 再清一次：防止加载过程中 watch/ensureLineState 用旧行写回 checked
      Object.keys(lineState).forEach((k) => delete lineState[k])
      ;(invRows.value || []).forEach((row) => {
        if (!row?.inventory_id) return
        const k = invRowKey(row)
        lineState[k] = {
          checked: serverRowMarkedChecked(row),
          countedStr: serverRowCountedStr(row),
          book: lineBook(row)
        }
      })
      lineStoreRevision.value++
      await refreshRoundProgress()
      if (selectedWarehouseId.value) {
        // 强制刷新版面印记/轮次，避免 silent 短路导致本地仍像「已盖章」或勾选不同步
        layoutData.value = layoutData.value.map((loc) => ({
          ...loc,
          inventory_preview_loaded: false
        }))
        await loadLayout({ preserveBrowsePath: true, silent: true, force: true })
      }
    } else {
      ElMessage.error(res?.message || '重盘失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '重盘失败')
  } finally {
    reCountLoading.value = false
  }
}

function openMobileStartRound() {
  if (!canManageRound.value) {
    ElMessage.warning('仅管理员可开启盘点轮次')
    return
  }
  mobileStartForm.name = ''
  mobileStartForm.scope_mode = 'warehouse'
  mobileStartForm.scope_location_ids = []
  mobileStartVisible.value = true
}

async function confirmMobileStartRound() {
  const r = await startRound({
    name: mobileStartForm.name || undefined,
    scope_mode: mobileStartForm.scope_mode,
    scope_location_ids: mobileStartForm.scope_location_ids
  })
  if (r) {
    mobileStartVisible.value = false
    await loadLayout({ preserveBrowsePath: true, silent: true })
  }
}

async function onMobileEndRound(roundId) {
  const ok = await endRound(roundId)
  if (ok) await loadLayout({ preserveBrowsePath: true, silent: false })
}

async function openMobileHistory() {
  try {
    const rounds = await listRounds()
    const lines = rounds.slice(0, 12).map(r => {
      const st = r.status === 'open' ? '进行中' : '已结束'
      return `${r.name || '#' + r.round_id}（${st}）`
    })
    await ElMessageBox.alert(
      lines.length ? lines.join('\n') : '暂无盘点轮次',
      '盘点历史',
      { confirmButtonText: '知道了' }
    )
  } catch (_) {
    /* cancel */
  }
}

async function voidCurrentScopeStamp() {
  const sid = invScopeLastStockTake.value?.session_id
  if (!sid) return
  const ok = await voidLatestStamp(sid)
  if (ok && invScopeNode.value?.location_id != null) {
    patchLayoutLastStockTake(invScopeNode.value.location_id, null)
    await refreshRoundProgress()
  }
}

const invDrawerVisible = ref(false)
/** list=对照清单；detail=盘点明细（可左右滑切换） */
const invDrawerPane = ref('list')
const invPaneSwipeRef = ref(null)
const invDetailCardsRef = ref(null)
/** pane 切换手势 */
const paneTouchStartX = ref(null)
const paneTouchStartY = ref(null)
const paneTouchLockedHoriz = ref(false)
const invLoading = ref(false)
const invRows = ref([])
const invScopeNode = ref(null)
const invSubtreeIds = ref([])
/** single | subtree | row */
const invScopeMode = ref('single')
/** 整排盘点时的排号展示（如 A1），勿与下方 invRowKey(row) 库存行键函数重名 */
const invScopeRowKey = ref(null)
/** 打开本排时的种子库位（用于「仅看当前库位」） */
const invScopeSeedLoc = ref(null)
/** 明细横向翻页：当前页（与 invRowsSorted 同序） */
const invPageIndex = ref(0)
const invTouchStartX = ref(null)
const invTouchStartY = ref(null)
const invTouchLockedHoriz = ref(false)
const invDrawerBodyRef = ref(null)
const cardStampLoadingId = ref(null)

const stockTakeMode = ref(false)
const lineState = reactive({})
const savingSession = ref(false)
/** 服务端已盘字段变更或版面刷新后，库位预览上的绿勾随 computed 刷新 */
const lineStoreRevision = ref(0)

/** 与后端 `INVENTORY_PHYSICAL_DUST_MAX_YARDS` 一致：不足半码视同无库存 */
const PHYS_DUST_CEILING_YDS = 0.5
function hasMeaningfulPhysicalYds(pq) {
  const n = Number(pq)
  if (!Number.isFinite(n) || n <= 1e-9) return false
  if (n > 0 && n < PHYS_DUST_CEILING_YDS) return false
  return true
}

/** 清单与移动端库存列表对齐：账面不足半码时接口可能将 quantity 显示为 0，但本库位 slot 分支仍存在 */
function inventoryRowVisibleInBrowseDrawer(row) {
  const pq = Number(row.physical_quantity ?? row.current_quantity ?? 0)
  if (hasMeaningfulPhysicalYds(pq)) return true
  const merged = mergedBranchDetailsForDisplay(row)
  return occupiedBranchCells(merged || []).length > 0
}

function parseQtyInput(str) {
  if (str == null || String(str).trim() === '') return null
  const n = parseFloat(String(str).replace(/,/g, '').trim())
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 10000) / 10000
}

const overflowTypeId = ref(null)
const lossTypeId = ref(null)
const yardUnitId = ref(null)

const qtyDialogVisible = ref(false)
const qtyMode = ref('overflow')
const qtyTargetRow = ref(null)
const qtySubmitting = ref(false)
const qtyForm = reactive({ deltaInput: '', actualInput: '', notes: '' })

const transferDialogVisible = ref(false)
const transferSubmitting = ref(false)
const transferLocationsLoading = ref(false)
const transferLocations = ref([])
const transferTargetRow = ref(null)
const transferForm = reactive({
  to_location_id: null,
  quantity: null,
  notes: ''
})

const isInventoryAdmin = computed(
  () => !!(userStore.user?.is_admin || userStore.user?.is_superuser || userStore.isAdmin || userStore.isSuperuser)
)

function canTransferInvRow(row) {
  if (!row?.inventory_id || !canAdjustStock.value) return false
  if (isIntakePlaceholderRow(row)) return false
  if (!isDispatchCompleteTransitRow(row)) return true
  return isInventoryAdmin.value
}

function transferMaxQtyForRow(row) {
  if (!row) return 0
  const xfer = Number(row.location_transferable_quantity)
  if (Number.isFinite(xfer) && xfer > 0) return xfer
  const phys = Number(row.physical_quantity ?? row.location_inventory_quantity ?? lineBook(row))
  return Number.isFinite(phys) && phys > 0 ? phys : 0
}

const transferMaxQty = computed(() => transferMaxQtyForRow(transferTargetRow.value))

const transferFromLocationLabel = computed(() => {
  const row = transferTargetRow.value
  if (!row) return '—'
  const loc = row.storage_location
  const code = (loc?.location_code || '').trim()
  const name = (loc?.location_name || '').trim()
  if (code && name && code !== name) return `${code} - ${name}`
  return code || name || (row.storage_location_id != null ? `库位#${row.storage_location_id}` : '—')
})

function transferLocationOptionLabel(loc) {
  if (!loc) return '—'
  const code = (loc.location_code || '').trim()
  const name = (loc.location_name || '').trim()
  if (code && name && code !== name) return `${code} - ${name}`
  return code || name || String(loc.location_id)
}

async function ensureTransferLocationsLoaded(force = false) {
  if (!force && transferLocations.value.length) return
  transferLocationsLoading.value = true
  try {
    transferLocations.value = await fetchAllActiveStorageLocations()
    if (!transferLocations.value.length) {
      ElMessage.warning('未加载到可用库位，请检查权限或稍后重试')
    }
  } catch (e) {
    transferLocations.value = []
    ElMessage.error(e?.response?.data?.message || e?.message || '加载库位失败')
  } finally {
    transferLocationsLoading.value = false
  }
}

function resetTransferForm() {
  transferTargetRow.value = null
  transferForm.to_location_id = null
  transferForm.quantity = null
  transferForm.notes = ''
}

async function openTransferDialog(row) {
  if (!canAdjustStock.value) {
    ElMessage.warning('无库存作业权限，无法移库')
    return
  }
  if (!row?.inventory_id) {
    ElMessage.warning('该行无库存记录，无法移库')
    return
  }
  if (!canTransferInvRow(row)) {
    ElMessage.warning('派工完成虚库仅管理员可移库；开送货单出库不受影响')
    return
  }
  transferTargetRow.value = row
  transferForm.to_location_id = null
  transferForm.notes = ''
  transferForm.quantity = transferMaxQtyForRow(row) || null
  transferDialogVisible.value = true
  await ensureTransferLocationsLoaded(false)
  // 拉详情补齐可移库量（列表行可能缺 location_transferable_quantity）
  try {
    const res = await getMaterialInventoryRecord(row.inventory_id)
    if (res?.success && res.data) {
      transferTargetRow.value = { ...row, ...res.data }
      const max = transferMaxQtyForRow(transferTargetRow.value)
      if (max > 0) transferForm.quantity = max
    }
  } catch (e) {
    console.warn('加载移库详情失败，使用列表行数量', e)
  }
}

async function submitTransferDialog() {
  const row = transferTargetRow.value
  if (!canTransferInvRow(row)) {
    ElMessage.warning('当前行不可移库')
    return
  }
  const invId = Number(row.inventory_id)
  const toId = Number(transferForm.to_location_id)
  if (!toId) {
    ElMessage.warning('请选择新库位')
    return
  }
  const fromId = Number(row.storage_location_id ?? row.storage_location?.location_id)
  if (fromId && toId === fromId) {
    ElMessage.warning('新库位与当前相同')
    return
  }
  const qty = Number(transferForm.quantity)
  const max = transferMaxQty.value
  if (!Number.isFinite(qty) || qty <= 0) {
    ElMessage.warning('请填写有效的移库数量')
    return
  }
  if (max > 0 && qty > max + 1e-6) {
    ElMessage.warning(`移库数量不能超过可移库量 ${fmtQty(max)}`)
    return
  }
  const toLabel =
    transferLocations.value.find((l) => Number(l.location_id) === toId) || null
  const toName = toLabel ? transferLocationOptionLabel(toLabel) : `库位#${toId}`
  const partial = max > 0 && qty < max - 1e-6
  try {
    await ElMessageBox.confirm(
      `从来源「${transferFromLocationLabel.value}」移出 ${fmtQty(qty)} 码\n目标：${toName}\n${
        partial ? '部分移库，其余仍留在原库位。\n' : ''
      }\n确认执行移库？`,
      '移库确认',
      {
        type: 'warning',
        confirmButtonText: '确认移库',
        cancelButtonText: '取消',
        ...ST_MSGBOX_OPTS
      }
    )
  } catch {
    return
  }
  transferSubmitting.value = true
  try {
    const res = await transferMaterialInventoryLocation(invId, {
      storage_location_id: toId,
      quantity: qty,
      notes: (transferForm.notes || '').trim() || '移动端盘点·移库'
    })
    if (res?.success) {
      const moved = res.data?.moved_quantity
      ElMessage.success(
        moved != null
          ? res.data?.partial
            ? `已移库 ${fmtQty(moved)}（部分移库，其余仍在原库位）`
            : `已移库 ${fmtQty(moved)}`
          : res.message || '移库完成'
      )
      transferDialogVisible.value = false
      await loadInvRowsForCurrentScope()
      if (selectedWarehouseId.value) {
        await loadLayout({ preserveBrowsePath: true, silent: true })
      }
    } else {
      ElMessage.error(res?.message || '移库失败')
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '移库失败')
  } finally {
    transferSubmitting.value = false
  }
}

const addDialogOpen = ref(false)
const addSubmitting = ref(false)
const batchSearchLoading = ref(false)
const batchOptions = ref([])
/** ① 查询输入框中的料号（始终保留用户输入，不与选中 id 绑在同一控件） */
const addBatchSearchKeyword = ref('')
/** 最近一次点「查询」时用的关键词 */
const addQueriedKeyword = ref('')
/** idle | not_found | candidates | found */
const addQueryStatus = ref('idle')
const addQueryCandidates = ref([])
const addInventoryPreviewLoading = ref(false)
const addInventoryPreviewRows = ref([])
const addSelectedSourceInventoryId = ref(null)
/** 他处有库存时：transfer=移库合并；create=目标新建（分支分开放置） */
const addPlacementMode = ref('')
const addForm = reactive({
  storage_location_id: null,
  material_batch_id: null,
  qtyStr: '',
  mark_as_meter_actual: false
})

const intakeDialogOpen = ref(false)
const intakeSubmitting = ref(false)
const intakeCancellingId = ref(null)
const meterActualLoadingId = ref(null)
const colorCardLoadingId = ref(null)
/** 重新申请成功后要删除的原驳回申请 ID */
const intakeReapplyFromRejectedId = ref(null)
const rejectedDialogOpen = ref(false)
const rejectedDialogRow = ref(null)
const rejectedDeleting = ref(false)
const intakeForm = reactive({
  storage_location_id: null,
  warehouse_manual_code: '',
  quantityStr: '',
  rollCountStr: '',
  applicant_notes: '',
  mark_as_meter_actual: false
})

/** 未知料号申请：分支格子（与新增库存一致） */
const intakeBranchGrid = reactive(
  Array.from({ length: BRANCH_GRID_ROWS }, () => Array(BRANCH_GRID_COLS).fill(''))
)
const intakeBranchActiveCell = reactive({ r: null, c: null })

/** 盘点新增/移库对话框：分支格子（与桌面 9×10 一致），合计写入 qtyStr */
const addBranchGrid = reactive(
  Array.from({ length: BRANCH_GRID_ROWS }, () => Array(BRANCH_GRID_COLS).fill(''))
)

/** 盘点新增对话框：当前焦点格子，用于顶部大号预览（与分支录入抽屉一致） */
const addBranchActiveCell = reactive({ r: null, c: null })

const canAccess = computed(() => {
  const u = userStore.user
  if (!u) return false
  if (u.is_admin || u.is_superuser) return true
  return hasPermission(u, 'inventory.locations.view')
})

/** 报溢/报损、新增库存 */
const canAdjustStock = computed(() => hasPermission(userStore.user, 'inventory.operations.edit'))
/** 与后端保存盘点单一致：作业编辑或库存记录编辑 */
const canSaveStockTake = computed(
  () =>
    hasPermission(userStore.user, 'inventory.operations.edit') ||
    hasPermission(userStore.user, 'inventory.records.edit')
)
const canUseStockTakePanel = computed(() => canAdjustStock.value || canSaveStockTake.value)
/** 品质/存储备注：与保存盘点/库存作业权限一致 */
const canEditInvNotes = computed(() => canSaveStockTake.value)
const canReviewApprovals = computed(() => canAccessMobileInventoryOperationApproval(userStore.user))

const canEditMaterialBatch = computed(() => hasPermission(userStore.user, 'inventory.material_batches.edit'))
/** 写入本库位库存行分支（或库存作业） */
const canWriteSlotBranch = computed(() => canAdjustStock.value || canEditMaterialBatch.value)

const notesForm = reactive({ quality_notes: '', storage_notes: '' })
const notesDirty = ref(false)
const notesSaving = ref(false)
const notesBoundInventoryId = ref(null)

const branchViewOpen = ref(false)
const branchEditOpen = ref(false)
const branchPreviewDetail = ref(null)
const branchPreviewInventoryId = ref(null)
const branchQuickLoading = ref(null)
const branchToggleLoading = ref(null)

/** 查看抽屉与卡片一致：优先本库位合并后的 branch_details，勿只用料号全局 */
const branchViewMergedDetails = computed(() => mergedBranchDetailsForDisplay(branchPreviewDetail.value))

/** 录入抽屉预填：本库位已保存的格子优先（slot_branch_details），否则用料号/API 合并后的 branch_details，避免仅从料号带出时误判为空 */
const branchEditInitialBranchDetails = computed(() => {
  const d = branchPreviewDetail.value
  if (!d) return null
  if (d.slot_branch_details != null) return d.slot_branch_details
  return d.branch_details ?? d.material_batch?.branch_details ?? null
})

const selectedWarehouse = computed(() =>
  warehouseList.value.find(w => w.location_id === selectedWarehouseId.value)
)

const browseParentId = computed(() => {
  const path = browsePath.value
  if (!path.length) return selectedWarehouseId.value
  return path[path.length - 1].id
})

function sortLocationsNatural(list) {
  return [...list].sort((a, b) => {
    const ca = String(a.location_code || a.location_name || '').trim()
    const cb = String(b.location_code || b.location_name || '').trim()
    return ca.localeCompare(cb, 'zh-CN', { numeric: true, sensitivity: 'base' })
  })
}

function clearLocationJump() {
  locationJumpFilterId.value = null
  locationJumpInput.value = ''
}

/** 在当前所选仓库内按编码/名称定位；优先轻量查找接口，失败再回退本地版面 */
async function jumpToLocation() {
  const qRaw = locationJumpInput.value.trim()
  const q = qRaw.toLowerCase()
  if (!q) {
    ElMessage.warning('请输入库位编码或名称')
    return
  }
  const wid = Number(selectedWarehouseId.value)
  if (!wid) {
    ElMessage.warning('请先选择仓库')
    return
  }

  locationJumpLoading.value = true
  try {
    // 1) 轻量服务端定位（不依赖重 layout）
    try {
      const res = await findWarehouseLocation({ warehouse_id: wid, q: qRaw })
      if (res?.success && res.data?.target) {
        if (!layoutData.value.length) {
          await loadLayout({ preserveBrowsePath: true, silent: true })
        }
        const path = Array.isArray(res.data.browse_path) ? res.data.browse_path : []
        if (path.length) browsePath.value = path.map(p => ({ id: p.id, name: p.name }))
        locationJumpFilterId.value = Number(res.data.target.location_id)
        const t = res.data.target
        const label = `${t.location_name || ''} ${t.location_code || ''}`.trim()
        ElMessage.success(label ? `已定位：${label}` : '已定位库位')
        return
      }
      if (res && res.success === false && res.message) {
        ElMessage.warning(res.message)
        return
      }
    } catch (e) {
      // 回退本地
      console.warn('findWarehouseLocation failed, fallback local', e)
    }

    // 2) 回退：本地已缓存的 lite 树
    if (!layoutData.value.length) {
      await loadLayout()
    }
    const w = selectedWarehouse.value
    if (!w) return

    const byId = new Map(layoutData.value.map(l => [Number(l.location_id), l]))

    const underWarehouse = loc => {
      let id = Number(loc.location_id)
      for (let i = 0; i < 600; i++) {
        if (!id) return false
        if (id === wid) return true
        const row = byId.get(id)
        if (!row) return false
        id = Number(row.parent_location_id)
      }
      return false
    }

    const candidates = layoutData.value.filter(loc => {
      if (!underWarehouse(loc)) return false
      const code = String(loc.location_code || '').toLowerCase()
      const name = String(loc.location_name || '').toLowerCase()
      return code.includes(q) || name.includes(q)
    })

    if (!candidates.length) {
      ElMessage.warning('在当前仓库版面内未找到匹配的库位')
      return
    }

    let target =
      candidates.find(l => String(l.location_code || '').toLowerCase() === q) ||
      candidates.find(l => String(l.location_name || '').toLowerCase() === q) ||
      candidates[0]

    const nodesToParent = []
    let x = target
    while (x) {
      const pid = Number(x.parent_location_id)
      nodesToParent.unshift({ id: x.location_id, name: x.location_name })
      if (pid === wid) break
      x = byId.get(pid)
      if (!x) break
    }
    browsePath.value = [
      { id: w.location_id, name: w.location_name },
      ...nodesToParent.slice(0, -1)
    ]
    locationJumpFilterId.value = Number(target.location_id)
    const label = `${target.location_name || ''} ${target.location_code || ''}`.trim()
    ElMessage.success(label ? `已定位：${label}` : '已定位库位')
  } finally {
    locationJumpLoading.value = false
  }
}

const folderChildren = computed(() => {
  const pid = browseParentId.value
  if (!pid) return []
  const raw = layoutData.value.filter(l => Number(l.parent_location_id) === Number(pid))
  const seen = new Map()
  for (const l of raw) {
    const id = Number(l.location_id)
    if (!seen.has(id)) seen.set(id, l)
  }
  return sortLocationsNatural(Array.from(seen.values()))
})

function locationHasChildren(loc) {
  if (!loc?.location_id) return false
  return layoutData.value.some(l => Number(l.parent_location_id) === Number(loc.location_id))
}

const locationJumpInput = ref('')
const locationJumpLoading = ref(false)
/** 跳转后仅在当前层级列表中保留该库位一行（通常为叶子 bin） */
const locationJumpFilterId = ref(null)
/** 合并并发 loadLayout，避免互相取消导致「加载仓库结构失败」 */
let loadLayoutInflight = null
let enrichCardsTimer = null
let enrichCardsSeq = 0

const folderChildrenWithMeta = computed(() => {
  void lineStoreRevision.value
  let base = folderChildren.value
  if (locationJumpFilterId.value != null) {
    const fid = Number(locationJumpFilterId.value)
    base = base.filter(l => Number(l.location_id) === fid)
  }
  return base.map(loc => ({
    ...loc,
    hasChildren: locationHasChildren(loc)
  }))
})

watch(
  () =>
    (folderChildrenWithMeta.value || [])
      .map(r => `${r.location_id}:${r.inventory_preview_loaded ? 1 : 0}`)
      .join('|'),
  () => scheduleEnrichVisibleLocationCards()
)

/** 当前抽屉汇总节点在平面数据中的最近盘点记录（与列表节点一致） */
const invScopeLastStockTake = computed(() => {
  const n = invScopeNode.value
  if (!n?.location_id) return null
  const row = layoutData.value.find(l => Number(l.location_id) === Number(n.location_id))
  return row?.last_stock_take || null
})

const invDrawerTitle = computed(() =>
  invScopeNode.value ? `库存汇总 · ${invScopeNode.value.location_name}` : '库存汇总'
)

function locationOptionLabelForAdd(loc) {
  if (!loc) return ''
  const name = (loc.location_name || '').trim()
  const code = (loc.location_code || '').trim()
  if (name && code) return `${name} (${code})`
  return name || code || `库位#${loc.location_id}`
}

/**
 * 新增库存可选目标库位：
 * 1) 当前盘点范围 bin（优先）
 * 2) 本仓库全部 bin（可改选 R20 等）
 * 3) 该料号预览中已有库存的库位（即使 layout 未标为 bin）
 */
const addLocationOptions = computed(() => {
  const seen = new Map()
  const put = loc => {
    if (!loc || loc.location_id == null) return
    const id = Number(loc.location_id)
    if (!Number.isFinite(id) || seen.has(id)) return
    seen.set(id, loc)
  }

  const root = invScopeNode.value
  const scopeIds = new Set((invSubtreeIds.value || []).map(Number))
  for (const l of layoutData.value) {
    if (scopeIds.has(Number(l.location_id)) && l.location_type === 'bin') put(l)
  }
  if (root?.location_type === 'bin') put(root)

  for (const l of layoutData.value) {
    if (l.location_type === 'bin') put(l)
  }

  for (const r of addInventoryPreviewRows.value || []) {
    const loc = r?.storage_location
    if (loc?.location_id != null) {
      put({
        location_id: loc.location_id,
        location_name: loc.location_name,
        location_code: loc.location_code,
        location_type: loc.location_type || 'bin'
      })
    } else if (r?.storage_location_id != null) {
      const fromLayout = layoutData.value.find(
        x => Number(x.location_id) === Number(r.storage_location_id)
      )
      if (fromLayout) put(fromLayout)
      else {
        put({
          location_id: r.storage_location_id,
          location_name: `库位#${r.storage_location_id}`,
          location_code: String(r.storage_location_id),
          location_type: 'bin'
        })
      }
    }
  }

  const list = Array.from(seen.values())
  if (!list.length && root) return [root]
  return sortLocationsNatural(list)
})

function locIdOfRow(r) {
  return Number(r?.storage_location?.location_id ?? r?.storage_location_id)
}

function resolveLocationLabelForAdd(fallbackLocId) {
  const row = layoutData.value.find(l => Number(l.location_id) === Number(fallbackLocId))
  if (!row) return `库位 #${fallbackLocId}`
  const name = (row.location_name || '').trim()
  const code = (row.location_code || '').trim()
  if (name && code) return `${name} (${code})`
  return name || code || `库位 #${fallbackLocId}`
}

function resolveLocationLabelForAddRow(r) {
  const loc = r?.storage_location
  if (loc?.location_name || loc?.location_code) {
    const name = (loc.location_name || '').trim()
    const code = (loc.location_code || '').trim()
    if (name && code) return `${name} (${code})`
    return name || code || `库位 #${locIdOfRow(r)}`
  }
  return resolveLocationLabelForAdd(locIdOfRow(r))
}

const addSelectedBatchCache = ref(null)

const addSelectedBatchIdentitySummary = computed(() => {
  const batchId = Number(addForm.material_batch_id)
  if (!Number.isFinite(batchId) || batchId <= 0) return null
  const batch =
    (addSelectedBatchCache.value && Number(addSelectedBatchCache.value.batch_id) === batchId
      ? addSelectedBatchCache.value
      : null) ||
    batchOptions.value.find(b => Number(b.batch_id) === batchId) ||
    null
  if (!batch?.material_identity_info) return null
  return formatIdentityLinesFromBatch(batch)
})

const addPreviewDisplayRows = computed(() =>
  addInventoryPreviewRows.value.map(r => ({
    ...r,
    locationLabel: resolveLocationLabelForAddRow(r),
    isTargetBin: Number(addForm.storage_location_id) === locIdOfRow(r)
  }))
)

const addAtTargetStockRows = computed(() => {
  const tid = Number(addForm.storage_location_id)
  if (!tid) return []
  return addInventoryPreviewRows.value.filter(
    r => r.inventory_id && hasMeaningfulPhysicalYds(r.physical_quantity) && locIdOfRow(r) === tid
  )
})

const addElsewhereStockRows = computed(() => {
  const tid = Number(addForm.storage_location_id)
  if (!tid) return []
  return addInventoryPreviewRows.value.filter(
    r => r.inventory_id && hasMeaningfulPhysicalYds(r.physical_quantity) && locIdOfRow(r) !== tid
  )
})

const addQtyFieldLabel = computed(() => {
  if (addPlacementMode.value === 'transfer' && addElsewhereStockRows.value.length > 0) {
    return '移库数量（默认全部可移库）'
  }
  return '数量（可与分支合计一致，或以分支合计为准）'
})

const addQtyFieldPlaceholder = computed(() => {
  if (addPlacementMode.value === 'transfer' && addElsewhereStockRows.value.length > 0) {
    return '默认来源全部可移库量；改小则为部分移库'
  }
  return '可手填；填分支格子后会自动更新此处'
})

const addBranchSumQty = computed(() => {
  const p = gridStringsToBranchDetailsPayload(addBranchGrid)
  return sumBranchDetails(p == null ? [] : p)
})

const addBranchActivePreviewLabel = computed(() => {
  const r = addBranchActiveCell.r
  const c = addBranchActiveCell.c
  if (r == null || c == null) return ''
  return `格 ${r + 1}${String.fromCharCode(65 + c)}`
})

const addBranchActivePreviewValue = computed(() => {
  const r = addBranchActiveCell.r
  const c = addBranchActiveCell.c
  if (r == null || c == null) return ''
  const s = (addBranchGrid[r][c] || '').trim()
  return s ? s : '（空）'
})

function colLetterAdd(idx) {
  return String.fromCharCode(65 + idx)
}

function resetAddBranchGrid() {
  addBranchActiveCell.r = null
  addBranchActiveCell.c = null
  for (let r = 0; r < BRANCH_GRID_ROWS; r++) {
    for (let c = 0; c < BRANCH_GRID_COLS; c++) {
      addBranchGrid[r][c] = ''
    }
  }
}

function clearAddBranchGrid() {
  resetAddBranchGrid()
}

function formatAddQtyFromBranchSum(s) {
  if (!Number.isFinite(s) || s <= 0) return ''
  const r = Math.round(s * 10000) / 10000
  if (Number.isInteger(r)) return String(r)
  const t = r.toFixed(4).replace(/\.?0+$/, '')
  return t
}

function onAddBranchFocus(e, r, c) {
  addBranchActiveCell.r = r
  addBranchActiveCell.c = c
  const t = e.target
  if (t && typeof t.select === 'function') t.select()
}

function onAddBranchBlur(r, c) {
  addBranchGrid[r][c] = normalizeCellInputString(addBranchGrid[r][c])
}

function normalizeAddBranchCellsForSubmit() {
  for (let r = 0; r < BRANCH_GRID_ROWS; r++) {
    for (let c = 0; c < BRANCH_GRID_COLS; c++) {
      addBranchGrid[r][c] = normalizeCellInputString(addBranchGrid[r][c])
    }
  }
}

watch(addBranchSumQty, s => {
  if (addPlacementMode.value === 'transfer' && addElsewhereStockRows.value.length > 0) return
  if (s > 0.0005) {
    addForm.qtyStr = formatAddQtyFromBranchSum(s)
  }
})

function resolveRowTransferableQty(row) {
  if (!row) return 0
  const phys = Number(row.physical_quantity)
  return Number.isFinite(phys) && phys > 0 ? phys : 0
}

function syncAddTransferDefaultQty() {
  if (addPlacementMode.value !== 'transfer') return
  const rows = addElsewhereStockRows.value
  if (!rows.length) return
  let src = null
  if (rows.length === 1) {
    src = rows[0]
  } else {
    const sid = Number(addSelectedSourceInventoryId.value)
    src = rows.find(r => Number(r.inventory_id) === sid) || null
  }
  if (!src) return
  const xfer = resolveRowTransferableQty(src)
  if (xfer > 0) {
    addForm.qtyStr = formatAddQtyFromBranchSum(xfer)
  }
}

async function refreshAddInventoryPreview(batchId) {
  if (!batchId) {
    addInventoryPreviewRows.value = []
    addSelectedBatchCache.value = null
    return
  }
  addInventoryPreviewLoading.value = true
  try {
    const detail = await fetchBatchDetailForAdd(batchId)
    addSelectedBatchCache.value = detail
    const invRes = await getMaterialInventory({
      material_batch_id: batchId,
      only_inventory_row: true,
      has_stock: true,
      per_page: 500,
      page: 1
    })
    if (!invRes.success) {
      addInventoryPreviewRows.value = []
      return
    }
    const rows = (invRes.data || []).filter(r => r.inventory_id && hasMeaningfulPhysicalYds(r.physical_quantity))
    addInventoryPreviewRows.value = rows
    await nextTick()
    applyDefaultSourceSelection(rows)
    syncAddTransferDefaultQty()
  } catch {
    addInventoryPreviewRows.value = []
  } finally {
    addInventoryPreviewLoading.value = false
  }
}

function applyDefaultSourceSelection(rows) {
  const tid = Number(addForm.storage_location_id)
  const elsewhere = rows.filter(r => locIdOfRow(r) !== tid && hasMeaningfulPhysicalYds(r.physical_quantity))
  if (elsewhere.length === 1) {
    addSelectedSourceInventoryId.value = elsewhere[0].inventory_id
  } else if (elsewhere.length > 1) {
    const cur = addSelectedSourceInventoryId.value
    if (!elsewhere.some(r => r.inventory_id === cur)) {
      addSelectedSourceInventoryId.value = null
    }
  } else {
    addSelectedSourceInventoryId.value = null
  }
}

function fmtStockTakeTime(iso) {
  if (!iso) return '—'
  return String(iso).replace('T', ' ').slice(0, 19)
}

function typeText(type) {
  const m = { warehouse: '仓库', area: '区域', shelf: '货架', pallet: '卡板', bin: '库位' }
  return m[type] || type || '—'
}

function tagType(type) {
  const m = {
    warehouse: 'primary',
    area: 'success',
    shelf: 'warning',
    pallet: 'warning',
    bin: 'info'
  }
  return m[type] || 'info'
}

function fmtQty(n) {
  if (n == null || Number.isNaN(Number(n))) return '0'
  const x = Number(n)
  return Number.isInteger(x) ? String(x) : x.toFixed(2)
}

/** 支数：优先用后端 total_branch_rolls；为 0 但有库存时按品种数近似；旧接口未返回字段时亦按品种数 */
function resolveBranchRollsForHint(s) {
  if (s == null) return 0
  const items = Math.round(Number(s.total_items ?? 0))
  const qty = Number(s.total_quantity ?? 0)
  const tr = s.total_branch_rolls
  if (tr !== undefined && tr !== null && String(tr).trim() !== '') {
    const n = Math.round(Number(tr))
    if (Number.isFinite(n) && n > 0) return n
    if (qty > 0 && items > 0) return items
    return Number.isFinite(n) ? Math.max(0, n) : 0
  }
  return items
}

function cardInventoryLines(item) {
  const rows = item?.inventory_lines_preview
  return Array.isArray(rows) ? rows : []
}

function isPreviewPendingIntake(ln) {
  if (!ln || ln.inventory_id) return false
  if (ln.rejected_intake || ln.intake_status === 'rejected') return false
  return !!(ln.pending_intake || ln.intake_request_id)
}

function isPreviewRejectedIntake(ln) {
  return !!(ln && !ln.inventory_id && (ln.rejected_intake || ln.intake_status === 'rejected'))
}

/** 有库存行、非待确认、且尚未勾「已盘」→ 整行红色，便于扫一眼看出未处理 */
function isPreviewUncheckedStock(ln) {
  if (!ln || isPreviewPendingIntake(ln) || isPreviewRejectedIntake(ln)) return false
  if (!ln.inventory_id) return false
  return !isPreviewLineMarkedStockTaken(ln)
}

function previewLineKey(ln, idx) {
  if (ln?.intake_request_id) return `intake-${ln.intake_request_id}`
  if (ln?.inventory_id) return `inv-${ln.inventory_id}`
  return `ln-${ln?.warehouse_manual_code || ''}-${idx}`
}

/** 库位卡片顶部：本节点合计码数与总支数（与 inventory_summary 一致） */
function invTotalsSummary(item) {
  const s = item?.inventory_summary
  if (!s || Number(s.total_quantity) <= 0) return ''
  const rolls = resolveBranchRollsForHint(s)
  return `合计 ${fmtQty(s.total_quantity)}码 · ${rolls}支（${s.total_items}种）`
}

function invHint(item) {
  const s = item.inventory_summary
  if (s && Number(s.total_quantity) > 0) {
    const rolls = resolveBranchRollsForHint(s)
    return `本节点登记 ${s.total_items} 种 · ${fmtQty(s.total_quantity)}码 · ${rolls} 支`
  }
  if (item.hasChildren) {
    return '本节点无直接库存（可能在下级）'
  }
  return '本节点无在库实物'
}

function showMobileEmptyChar(item) {
  if (item?.is_active === false) return false
  if (item?.hasChildren) return false
  if (!['bin', 'pallet'].includes(item?.location_type)) return false
  // lite 树尚未补明细时不标「空」，避免误导
  if (item?.inventory_preview_loaded !== true) return false
  const s = item?.inventory_summary
  const qty = s ? Number(s.total_quantity ?? 0) : 0
  return qty <= 0
}

function collectSubtreeLocationIds(rootId) {
  const ids = []
  const dfs = id => {
    ids.push(id)
    layoutData.value
      .filter(l => Number(l.parent_location_id) === Number(id))
      .forEach(ch => dfs(ch.location_id))
  }
  dfs(rootId)
  return ids
}

function serverRowMarkedChecked(row) {
  const v = row?.mobile_stock_take_checked
  if (!(v === true || v === 1 || v === '1')) return false
  const openId =
    row?.open_stock_take_round_id ||
    coveringRoundIdForInvRow(row) ||
    openRound.value?.round_id
  if (openId == null) return false
  const rid = row?.mobile_stock_take_round_id
  if (rid == null) return false
  return Number(rid) === Number(openId)
}

function coveringRoundIdForInvRow(row) {
  const lid = Number(row?.storage_location_id || row?.storage_location?.location_id)
  if (!lid) return null
  const ancestors = new Set([lid])
  let cur = layoutData.value.find(l => Number(l.location_id) === lid)
  const guard = new Set()
  while (cur?.parent_location_id != null && !guard.has(Number(cur.location_id))) {
    guard.add(Number(cur.location_id))
    const pid = Number(cur.parent_location_id)
    ancestors.add(pid)
    cur = layoutData.value.find(l => Number(l.location_id) === pid)
  }
  let best = null
  for (const r of openRounds.value) {
    if ((r.scope_mode || 'warehouse') !== 'partial') {
      best = r.round_id
      continue
    }
    const roots = (r.scope_location_ids || []).map(Number)
    if (roots.some(root => ancestors.has(root))) {
      if (best == null || Number(r.round_id) > Number(best)) best = r.round_id
    }
  }
  return best
}

const invScopeInOpenRound = computed(() => {
  const loc = invScopeNode.value
  if (!loc) return false
  if (loc.open_stock_take_round_id) return true
  return !!coveringRoundIdForInvRow({ storage_location_id: loc.location_id })
})

function isSingleSkuLeaf(item) {
  if (!item || item.hasChildren) return false
  const n = Number(item?.inventory_summary?.total_items ?? 0)
  if (n === 1) return true
  const lines = cardInventoryLines(item)
  return lines.length === 1 && !item.inventory_preview_truncated
}

function locationInOpenRound(item) {
  if (!item) return false
  if (item.open_stock_take_round_id) return true
  return !!coveringRoundIdForInvRow({ storage_location_id: item.location_id })
}

function canStampOnCard(item) {
  return !!(canSaveStockTake.value && isSingleSkuLeaf(item) && locationInOpenRound(item))
}

function isLocationStamped(item) {
  return !!(item?.last_stock_take?.operator_name || item?.last_stock_take?.session_id)
}

async function stampFromCard(item) {
  if (!canStampOnCard(item) || isLocationStamped(item)) return
  const allLines = cardInventoryLines(item)
  const pendingLines = allLines.filter(isPreviewPendingIntake)
  if (pendingLines.length > 0) {
    ElMessage.warning(
      `尚有 ${pendingLines.length} 条未知料号入库申请待桌面确认，处理完成前不可盖章。`
    )
    return
  }
  const lines = allLines.filter(ln => ln?.inventory_id)
  const unchecked = lines.filter(ln => !isPreviewLineMarkedStockTaken(ln))
  if (unchecked.length > 0) {
    ElMessage.warning(
      '请先打开库存清单，勾选全部「已盘」后再盖章；实物不在本库位的请先移库或报损。'
    )
    return
  }
  cardStampLoadingId.value = item.location_id
  try {
    const data = await stampLocation({
      storageLocationId: item.location_id,
      warehouseLocationId: selectedWarehouseId.value,
      locationName: item.location_name
    })
    if (data && item.location_id != null) {
      patchLayoutLastStockTake(item.location_id, data)
    }
  } finally {
    cardStampLoadingId.value = null
  }
}

/** 仓库料号按数字比较：10327.2 视为小数，从小到大 */
function warehouseCodeNumericValue(row) {
  const raw = String(
    row?.warehouse_manual_code || row?.material_batch?.manual_batch_number || ''
  ).trim()
  if (!raw) return Number.POSITIVE_INFINITY
  const m = raw.match(/^(\d+(?:\.\d+)?)/)
  if (m) {
    const n = Number(m[1])
    if (Number.isFinite(n)) return n
  }
  const n2 = Number.parseFloat(raw)
  return Number.isFinite(n2) ? n2 : Number.POSITIVE_INFINITY
}

function overviewLocationCode(row) {
  return (
    (row?.storage_location?.location_code || '').trim() ||
    (row?.location_code || '').trim() ||
    ''
  )
}

function locationSortKey(row) {
  const code = overviewLocationCode(row)
  const p = parseLocationRowParts(code)
  const suffix = p?.suffix == null ? Number.POSITIVE_INFINITY : p.suffix
  return [String(p?.rowKey || '').toLowerCase(), suffix, code.toLowerCase()]
}

function invRowSortKey(row) {
  // 驳回优先展示，其次待确认，再是正式库存；多库位时按库位后缀再按料号
  const intakeRank = isRejectedIntakeRow(row) ? 0 : isPendingIntakeRow(row) ? 1 : 2
  const [rk, suf, loc] = locationSortKey(row)
  const num = warehouseCodeNumericValue(row)
  const wh = String(
    row?.warehouse_manual_code || row?.material_batch?.manual_batch_number || ''
  ).toLowerCase()
  const code = String(row?.material_identity?.material_identity_code || '').toLowerCase()
  const batch = String(
    row?.material_batch?.system_batch_number || row?.system_batch_code || ''
  ).toLowerCase()
  const id = Number(row?.inventory_id) || Number(row?.intake_request_id) || 0
  return [intakeRank, rk, suf, loc, num, wh, code, batch, id]
}

const invRowsSorted = computed(() => {
  const list = [...(invRows.value || [])]
  list.sort((a, b) => {
    const ka = invRowSortKey(a)
    const kb = invRowSortKey(b)
    for (let i = 0; i < ka.length; i++) {
      if (ka[i] < kb[i]) return -1
      if (ka[i] > kb[i]) return 1
    }
    return 0
  })
  return list
})

function isIntakePlaceholderRow(row) {
  return !!(row && (row._intake_placeholder || row._pending_intake || row.intake_request_id) && !row.inventory_id)
}

function isPendingIntakeRow(row) {
  if (!isIntakePlaceholderRow(row)) return false
  const st = (row.intake_status || 'pending').toString()
  return st === 'pending' && !row._rejected_intake
}

function isRejectedIntakeRow(row) {
  if (!isIntakePlaceholderRow(row)) return false
  return row._rejected_intake === true || (row.intake_status || '').toString() === 'rejected'
}

function pendingIntakeRollsOf(row) {
  if (row?.roll_count != null && Number.isFinite(Number(row.roll_count))) {
    const n = Math.round(Number(row.roll_count))
    if (n > 0) return n
  }
  const bd = row?.branch_details
  if (bd) {
    const n = occupiedBranchCells(bd || []).length
    if (n > 0) return n
  }
  return hasMeaningfulPhysicalYds(row?.physical_quantity) ? 1 : 0
}

function invRowDisplayCode(row) {
  return (
    (row?.warehouse_manual_code || '').trim() ||
    (row?.material_batch?.manual_batch_number || '').trim() ||
    (row?.material_batch?.system_batch_number || '').trim() ||
    (row?.material_identity?.material_identity_code || '').trim() ||
    (row?.inventory_id != null ? `#${row.inventory_id}` : '—')
  )
}

function isInvRowCheckedForStamp(row) {
  if (!row?.inventory_id) return false
  const k = invRowKey(row)
  if (lineState[k] && typeof lineState[k].checked === 'boolean') {
    return !!lineState[k].checked
  }
  return serverRowMarkedChecked(row)
}

const uncheckedInvRows = computed(() => {
  void lineStoreRevision.value
  return invRowsSorted.value.filter(r => r?.inventory_id && !isInvRowCheckedForStamp(r))
})

const checkableInvCount = computed(() =>
  invRowsSorted.value.filter(r => r?.inventory_id).length
)

const stampedInvCount = computed(() => {
  void lineStoreRevision.value
  return invRowsSorted.value.filter(r => r?.inventory_id && isInvRowCheckedForStamp(r)).length
})

const pendingIntakeRows = computed(() => invRowsSorted.value.filter(isPendingIntakeRow))

const pendingIntakeCount = computed(() => pendingIntakeRows.value.length)

const pendingIntakeQty = computed(() =>
  pendingIntakeRows.value.reduce((s, r) => s + (Number(r.physical_quantity) || 0), 0)
)

const pendingIntakeRolls = computed(() =>
  pendingIntakeRows.value.reduce((s, r) => s + pendingIntakeRollsOf(r), 0)
)

const scopeTotalQty = computed(() =>
  invRowsSorted.value.reduce((s, r) => {
    if (isRejectedIntakeRow(r)) return s
    return s + (Number(lineBook(r)) || 0)
  }, 0)
)

const scopeTotalRolls = computed(() =>
  invRowsSorted.value.reduce((s, r) => {
    if (isRejectedIntakeRow(r)) return s
    const n = lineBranchRollsDisplay(r)
    return s + (n != null ? Number(n) : 0)
  }, 0)
)

const isMultiLocationScope = computed(() => (invSubtreeIds.value || []).length > 1)

const stampActionLabel = computed(() => {
  if (invScopeMode.value === 'row' && invScopeRowKey.value) {
    return `确认本排 ${invScopeRowKey.value} 已盘查`
  }
  if (isMultiLocationScope.value) {
    return `确认本范围已盘查（${invSubtreeIds.value.length}库位）`
  }
  return '确认本库位已盘查'
})

const canStampCurrentScope = computed(() => {
  if (!invScopeInOpenRound.value || !canSaveStockTake.value) return false
  if (pendingIntakeCount.value > 0) return false
  return uncheckedInvRows.value.length === 0
})

function rowKeyOfLoc(loc) {
  return parseLocationRowParts(loc?.location_code)?.rowKey || null
}

function rowSiblingCountOf(loc) {
  if (!loc) return 0
  return collectSameRowLocations(layoutData.value, loc).locationIds.length
}

function canOpenRowInventory(item) {
  if (!item || item.hasChildren) return false
  return rowSiblingCountOf(item) > 1
}

const canExpandCurrentToRow = computed(() => {
  if (!invScopeNode.value || invScopeMode.value === 'row') return false
  if ((invSubtreeIds.value || []).length > 1) return false
  return rowSiblingCountOf(invScopeNode.value) > 1
})

const uncheckedInvPreviewText = computed(() => {
  const rows = uncheckedInvRows.value
  const labels = rows.slice(0, 5).map(invRowDisplayCode)
  if (rows.length > 5) labels.push(`…等${rows.length}条`)
  return labels.join('、') || '—'
})

const invCurrentRow = computed(() => invRowsSorted.value[invPageIndex.value] || null)

const invJumpKey = computed({
  get() {
    const row = invCurrentRow.value
    return row ? invRowKey(row) : null
  },
  set(key) {
    const idx = invRowsSorted.value.findIndex(r => invRowKey(r) === key)
    if (idx >= 0) invPageIndex.value = idx
  }
})

function invJumpOptionLabel(row, idx) {
  const code =
    row?.warehouse_manual_code ||
    row?.material_identity?.material_identity_code ||
    row?.material_batch?.system_batch_number ||
    '—'
  const tag = isRejectedIntakeRow(row)
    ? '〔驳回〕'
    : isPendingIntakeRow(row)
      ? '〔待申请〕'
      : ''
  const color = overviewDisplayColor(row)
  const colorPart = color && color !== '—' && color !== '待确认' && color !== '已驳回' ? ` · ${color}` : ''
  return `${idx + 1}. ${tag}${code}${colorPart}`
}

function invPagePrev() {
  if (invPageIndex.value > 0) invPageIndex.value -= 1
}

function invPageNext() {
  if (invPageIndex.value < invRowsSorted.value.length - 1) invPageIndex.value += 1
}

function isOverviewChecked(row) {
  void lineStoreRevision.value
  if (isIntakePlaceholderRow(row) || !row?.inventory_id) return false
  return isInvRowCheckedForStamp(row)
}

function isOverviewUnchecked(row) {
  void lineStoreRevision.value
  if (isIntakePlaceholderRow(row) || !row?.inventory_id) return false
  return !isInvRowCheckedForStamp(row)
}

function overviewDisplayCode(row) {
  return (
    (row?.warehouse_manual_code || '').trim() ||
    (row?.material_identity?.material_identity_code || '').trim() ||
    (row?.material_batch?.system_batch_number || '').trim() ||
    '—'
  )
}

/** 现场优先中文色名；无中文时再回退英文 */
function colorTextPreferCn(cn, en) {
  const c = (cn || '').trim()
  const e = (en || '').trim()
  return c || e || '—'
}

function previewLineColorText(ln) {
  return colorTextPreferCn(ln?.color_internal_name_cn, ln?.color_internal_name_en)
}

function overviewDisplayColor(row) {
  if (isRejectedIntakeRow(row)) return '已驳回'
  if (isPendingIntakeRow(row)) return '待确认'
  return colorTextPreferCn(
    row?.material_identity?.color?.internal_name_cn || row?.color_internal_name_cn,
    row?.material_identity?.color?.internal_name_en || row?.color_internal_name_en
  )
}

function overviewDisplayRolls(row) {
  const n = lineBranchRollsDisplay(row)
  return n != null ? n : 0
}

function overviewNotesHint(row) {
  if (!row || isIntakePlaceholderRow(row)) return ''
  const q = (row.quality_notes || '').trim()
  const s = (row.storage_notes || '').trim()
  if (!q && !s) return ''
  const parts = []
  if (q) parts.push(`品质：${q}`)
  if (s) parts.push(`存储：${s}`)
  return parts.join('；')
}

function syncNotesFormFromRow(row) {
  const id = row?.inventory_id != null ? Number(row.inventory_id) : null
  notesBoundInventoryId.value = id
  notesForm.quality_notes = (row?.quality_notes || '').trim() ? String(row.quality_notes) : ''
  notesForm.storage_notes = (row?.storage_notes || '').trim() ? String(row.storage_notes) : ''
  notesDirty.value = false
}

async function saveInvNotes(row) {
  if (!canEditInvNotes.value) {
    ElMessage.warning('无权限编辑材料备注')
    return
  }
  const id = Number(row?.inventory_id)
  if (!id) {
    ElMessage.warning('该行无库存记录')
    return
  }
  notesSaving.value = true
  try {
    const payload = {
      quality_notes: (notesForm.quality_notes || '').trim() || null,
      storage_notes: (notesForm.storage_notes || '').trim() || null
    }
    const res = await updateMaterialInventory(id, payload)
    if (!res?.success) {
      ElMessage.error(res?.message || '保存备注失败')
      return
    }
    const target = invRows.value.find(r => Number(r.inventory_id) === id)
    if (target) {
      target.quality_notes = payload.quality_notes
      target.storage_notes = payload.storage_notes
    }
    if (row && Number(row.inventory_id) === id) {
      row.quality_notes = payload.quality_notes
      row.storage_notes = payload.storage_notes
    }
    touchLayoutPreviewNotes({
      inventory_id: id,
      quality_notes: payload.quality_notes,
      storage_notes: payload.storage_notes
    })
    notesDirty.value = false
    ElMessage.success('备注已保存')
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || e?.message || '保存备注失败')
  } finally {
    notesSaving.value = false
  }
}

function openDetailForRow(row) {
  const idx = invRowsSorted.value.findIndex(r => invRowKey(r) === invRowKey(row))
  if (idx >= 0) invPageIndex.value = idx
  invDrawerPane.value = 'detail'
}

/**
 * 轻量横滑识别：全程 passive，不 preventDefault，避免锁住上下滚动。
 * 仅在松手时且横滑明显（|dx| 远大于 |dy|）才切页/翻料号。
 */
function clearPaneTouch() {
  paneTouchStartX.value = null
  paneTouchStartY.value = null
  paneTouchLockedHoriz.value = false
}

function clearInvTouch() {
  invTouchStartX.value = null
  invTouchStartY.value = null
  invTouchLockedHoriz.value = false
}

function onPaneTouchStart(e) {
  const t = e.touches?.[0] || e.changedTouches?.[0]
  paneTouchStartX.value = t?.clientX ?? null
  paneTouchStartY.value = t?.clientY ?? null
}

function onPaneTouchEnd(e) {
  const startX = paneTouchStartX.value
  const startY = paneTouchStartY.value
  clearPaneTouch()
  if (startX == null) return
  const end = e.changedTouches?.[0]?.clientX
  const endY = e.changedTouches?.[0]?.clientY
  if (end == null) return
  const dx = end - startX
  const dy = endY != null && startY != null ? endY - startY : 0
  // 必须明显横滑，否则当作上下翻页
  if (Math.abs(dx) < 80 || Math.abs(dx) < Math.abs(dy) * 2) return
  if (dx < 0) invDrawerPane.value = 'detail'
}

function onInvTouchStart(e) {
  const t = e.touches?.[0] || e.changedTouches?.[0]
  invTouchStartX.value = t?.clientX ?? null
  invTouchStartY.value = t?.clientY ?? null
}

function onInvTouchEnd(e) {
  const startX = invTouchStartX.value
  const startY = invTouchStartY.value
  clearInvTouch()
  if (startX == null) return
  const end = e.changedTouches?.[0]?.clientX
  const endY = e.changedTouches?.[0]?.clientY
  if (end == null) return
  const dx = end - startX
  const dy = endY != null && startY != null ? endY - startY : 0
  if (Math.abs(dx) < 80 || Math.abs(dx) < Math.abs(dy) * 2) return
  if (dx < 0) {
    invPageNext()
    return
  }
  if (invPageIndex.value <= 0) invDrawerPane.value = 'list'
  else invPagePrev()
}

function bindPassiveSwipe(el, start, end, flag) {
  if (!el || el[flag]) return
  el.addEventListener('touchstart', start, { passive: true })
  el.addEventListener('touchend', end, { passive: true })
  el.addEventListener('touchcancel', start === onPaneTouchStart ? clearPaneTouch : clearInvTouch, {
    passive: true
  })
  el[flag] = true
}

function unbindPassiveSwipe(el, start, end, flag) {
  if (!el || !el[flag]) return
  el.removeEventListener('touchstart', start)
  el.removeEventListener('touchend', end)
  el.removeEventListener('touchcancel', start === onPaneTouchStart ? clearPaneTouch : clearInvTouch)
  el[flag] = false
}

async function rebindInvDrawerGestures() {
  await nextTick()
  const listEl = invPaneSwipeRef.value
  const detailEl = invDetailCardsRef.value
  unbindPassiveSwipe(listEl, onPaneTouchStart, onPaneTouchEnd, '__invPaneTouchBound')
  unbindPassiveSwipe(detailEl, onInvTouchStart, onInvTouchEnd, '__invTouchBound')
  if (invDrawerPane.value === 'list') {
    bindPassiveSwipe(listEl, onPaneTouchStart, onPaneTouchEnd, '__invPaneTouchBound')
  } else {
    bindPassiveSwipe(detailEl, onInvTouchStart, onInvTouchEnd, '__invTouchBound')
  }
}

watch(invDrawerVisible, async open => {
  window.dispatchEvent(
    new CustomEvent('erp-mobile-dock-suppress', { detail: { suppress: !!open } })
  )
  if (open) {
    invDrawerPane.value = 'list'
    await rebindInvDrawerGestures()
  } else {
    unbindPassiveSwipe(invPaneSwipeRef.value, onPaneTouchStart, onPaneTouchEnd, '__invPaneTouchBound')
    unbindPassiveSwipe(invDetailCardsRef.value, onInvTouchStart, onInvTouchEnd, '__invTouchBound')
  }
})

watch(invDrawerPane, () => {
  rebindInvDrawerGestures()
})

watch(invRowsSorted, list => {
  if (!list.length) {
    invPageIndex.value = 0
    return
  }
  if (invPageIndex.value >= list.length) invPageIndex.value = list.length - 1
})

watch(
  () => invCurrentRow.value?.inventory_id,
  (id) => {
    if (notesDirty.value && notesBoundInventoryId.value != null && Number(id) !== notesBoundInventoryId.value) {
      // 翻到另一条时丢弃未保存草稿，以当前行已存备注为准
    }
    syncNotesFormFromRow(invCurrentRow.value)
  },
  { immediate: true }
)

/** 服务端 global 实盘 */
function serverRowCountedStr(row) {
  const q = row?.mobile_stock_take_counted_qty
  if (q === null || q === undefined || q === '') return ''
  const n = Number(q)
  if (!Number.isFinite(n)) return String(q).trim()
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10000) / 10000)
}

/** 预览行「已盘」：与 material_inventory.mobile_stock_take_checked 同步（全库多端一致）*/
function isPreviewLineMarkedStockTaken(ln) {
  return !!(ln?.inventory_id && serverRowMarkedChecked(ln))
}

function applyMobileTakePayloadToRow(row, data) {
  if (!row || !data || data.inventory_id == null) return
  if (Number(data.inventory_id) !== Number(row.inventory_id)) return
  row.mobile_stock_take_checked = !!data.mobile_stock_take_checked
  row.mobile_stock_take_checked_at = data.mobile_stock_take_checked_at ?? null
  row.mobile_stock_take_checked_by = data.mobile_stock_take_checked_by ?? null
  const cq = data.mobile_stock_take_counted_qty
  row.mobile_stock_take_counted_qty = cq !== undefined && cq !== null ? Number(cq) : null
  if (data.mobile_stock_take_round_id !== undefined) {
    row.mobile_stock_take_round_id = data.mobile_stock_take_round_id
  }
}

/** 已盘状态改在服务端后，同步更新当前仓库 layout 里预览行，绿勾不必等整包刷新 */
function touchLayoutPreviewForInventory(data) {
  const invId = data?.inventory_id
  if (invId == null) return
  const idn = Number(invId)
  layoutData.value = layoutData.value.map(loc => {
    const lines = loc.inventory_lines_preview
    if (!Array.isArray(lines) || !lines.length) return loc
    let changed = false
    const nl = lines.map((ln) => {
      if (Number(ln?.inventory_id) !== idn) return ln
      changed = true
      return {
        ...ln,
        mobile_stock_take_checked: !!data.mobile_stock_take_checked,
        mobile_stock_take_round_id:
          data.mobile_stock_take_round_id !== undefined
            ? data.mobile_stock_take_round_id
            : ln.mobile_stock_take_round_id
      }
    })
    return changed ? { ...loc, inventory_lines_preview: nl } : loc
  })
}

async function onMeterActualChange(row, marked) {
  const invId = row?.inventory_id
  if (!invId) {
    ElMessage.warning('该记录无库存明细 ID，无法标记跑码实数')
    return
  }
  meterActualLoadingId.value = Number(invId)
  try {
    const res = await patchMaterialInventoryMeterActualMark(Number(invId), { marked: !!marked })
    if (res?.success) {
      row.physical_run_verified_at = marked
        ? res.data?.physical_run_verified_at || new Date().toISOString()
        : null
      touchLayoutPreviewMeterActual(row)
      lineStoreRevision.value++
      ElMessage.success(marked ? '已标记跑码实数' : '已取消跑码实数')
    } else {
      ElMessage.error(res?.message || '标记失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '标记失败')
  } finally {
    meterActualLoadingId.value = null
  }
}

async function onColorCardChange(row, marked) {
  const invId = row?.inventory_id
  if (!invId) {
    ElMessage.warning('该记录无库存明细 ID，无法标记色卡')
    return
  }
  const next = !!marked
  try {
    await ElMessageBox.confirm(
      next
        ? '确认标记为「已裁料做色卡」？料号旁将显示红字「色卡」。'
        : '确认取消色卡标记？料号旁将不再显示「色卡」。',
      next ? '标记色卡' : '取消色卡',
      { type: 'warning', confirmButtonText: '确认', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  colorCardLoadingId.value = Number(invId)
  try {
    const res = await patchMaterialInventoryColorCardMark(Number(invId), { marked: next })
    if (res?.success) {
      const flag = res.data?.has_color_card != null ? !!res.data.has_color_card : next
      row.has_color_card = flag
      touchLayoutPreviewColorCard(row)
      lineStoreRevision.value++
      ElMessage.success(flag ? '已标记色卡' : '已取消色卡')
    } else {
      ElMessage.error(res?.message || '标记失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '标记失败')
  } finally {
    colorCardLoadingId.value = null
  }
}

function touchLayoutPreviewColorCard(row) {
  const invId = row?.inventory_id
  if (invId == null) return
  const idn = Number(invId)
  const flag = !!row.has_color_card
  layoutData.value = layoutData.value.map((loc) => {
    const lines = loc.inventory_lines_preview
    if (!Array.isArray(lines) || !lines.length) return loc
    let changed = false
    const nl = lines.map((ln) => {
      if (Number(ln?.inventory_id) !== idn) return ln
      changed = true
      return { ...ln, has_color_card: flag }
    })
    return changed ? { ...loc, inventory_lines_preview: nl } : loc
  })
}

function touchLayoutPreviewMeterActual(row) {
  const invId = row?.inventory_id
  if (invId == null) return
  const idn = Number(invId)
  const at = row.physical_run_verified_at || null
  layoutData.value = layoutData.value.map(loc => {
    const lines = loc.inventory_lines_preview
    if (!Array.isArray(lines) || !lines.length) return loc
    let changed = false
    const nl = lines.map(ln => {
      if (Number(ln?.inventory_id) !== idn) return ln
      changed = true
      return { ...ln, physical_run_verified_at: at }
    })
    return changed ? { ...loc, inventory_lines_preview: nl } : loc
  })
}

/** 备注保存后同步库位卡片预览行上的「备」标记 */
function touchLayoutPreviewNotes(data) {
  const invId = data?.inventory_id
  if (invId == null) return
  const idn = Number(invId)
  layoutData.value = layoutData.value.map(loc => {
    const lines = loc.inventory_lines_preview
    if (!Array.isArray(lines) || !lines.length) return loc
    let changed = false
    const nl = lines.map(ln => {
      if (Number(ln?.inventory_id) !== idn) return ln
      changed = true
      return {
        ...ln,
        quality_notes: data.quality_notes ?? null,
        storage_notes: data.storage_notes ?? null
      }
    })
    return changed ? { ...loc, inventory_lines_preview: nl } : loc
  })
}

async function persistMobileStockTakeMark(row, patch) {
  const invId = row?.inventory_id
  if (!invId) {
    ElMessage.warning('该记录无库存明细 ID，无法保存已盘标记')
    return false
  }
  try {
    const res = await patchMobileStockTakeLineMark({
      inventory_id: Number(invId),
      warehouse_location_id: selectedWarehouseId.value,
      ...patch
    })
    if (res?.success && res.data) {
      applyMobileTakePayloadToRow(row, res.data)
      touchLayoutPreviewForInventory(res.data)
      lineStoreRevision.value++
      return true
    }
    ElMessage.error(res?.message || '保存已盘标记失败')
    return false
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存已盘标记失败')
    return false
  }
}

function invRowKey(row) {
  if (row?.intake_request_id && !row?.inventory_id) return `intake-${row.intake_request_id}`
  return row.inventory_id ? `inv-${row.inventory_id}` : `b-${row.material_batch_id}-${row.storage_location_id}`
}

function lineBook(row) {
  const n = Number(row.physical_quantity)
  if (!Number.isFinite(n) || n <= 0) return 0
  if (n > 0 && n < PHYS_DUST_CEILING_YDS) return 0
  return n
}

/** 材料身份规格文案 */
function rowSpecText(row) {
  const s =
    row?.material_identity?.specification ||
    row?.material_batch?.material_identity?.specification ||
    row?.specification ||
    ''
  const t = String(s).trim()
  return t || ''
}

/** 与后端库位支数口径一致：未开分支计 1 支；已开分支按有数格子数（本库位合并后的分支明细） */
function lineBranchRollsDisplay(row) {
  if (isIntakePlaceholderRow(row)) {
    const n = pendingIntakeRollsOf(row)
    return n > 0 ? n : null
  }
  const pq = Number(row.physical_quantity ?? 0)
  const mb = row.material_batch
  const bd = mergedBranchDetailsForDisplay(row)
  if (mb?.enable_branch_management) {
    const n = occupiedBranchCells(bd || []).length
    if (n > 0) return n
    return hasMeaningfulPhysicalYds(pq) ? 1 : null
  }
  return hasMeaningfulPhysicalYds(pq) ? 1 : null
}

/** 库位卡片：分支合计 vs 库位库存（分支仅参考） */
function branchMismatchRow(row) {
  if (row?.branch_details_mismatch === true) return true
  if (!row?.material_batch?.enable_branch_management) return false
  const loc = Number(row.location_inventory_quantity ?? lineBook(row))
  const br = sumBranchDetails(mergedBranchDetailsForDisplay(row))
  return Math.abs(loc - br) > BRANCH_QTY_EPS
}

function ensureLineState(row, { syncCheckedFromServer = false } = {}) {
  if (isIntakePlaceholderRow(row) || !row?.inventory_id) return
  const k = invRowKey(row)
  if (!lineState[k]) {
    lineState[k] = {
      checked: serverRowMarkedChecked(row),
      countedStr: serverRowCountedStr(row),
      book: lineBook(row)
    }
  } else {
    lineState[k].book = lineBook(row)
    // 重盘/刷新后：以服务端勾选为准，避免本地残留 true 挡住更新
    if (syncCheckedFromServer) {
      lineState[k].checked = serverRowMarkedChecked(row)
      lineState[k].countedStr = serverRowCountedStr(row)
    }
  }
}

/** 正在写入「已盘」的行，避免受控勾选弹回时二次触发取消确认 */
const stockTakeMarkInflight = new Set()

async function onStockTakeCheckboxChange(row, nextChecked) {
  if (!stockTakeMode.value) return
  if (!canUseStockTakePanel.value) {
    ElMessage.warning('无权限保存「已盘」勾选（需库存作业或库存资料编辑类权限）')
    return
  }
  if (!invScopeInOpenRound.value) {
    ElMessage.warning('当前库位不在进行中的盘点范围内，请管理员先开启盘点轮次')
    return
  }
  const k = invRowKey(row)
  if (stockTakeMarkInflight.has(k)) return
  ensureLineState(row)
  const prev = !!lineState[k].checked
  const want = !!nextChecked
  if (prev === want) return
  if (!row.inventory_id) {
    ElMessage.warning('该记录无库存明细 ID，无法保存「已盘」')
    return
  }
  if (prev && !want) {
    try {
      await ElMessageBox.confirm(
        '确定取消该行的「已盘」勾选吗？多端将同步撤销该标记。',
        '取消「已盘」确认',
        {
          type: 'warning',
          confirmButtonText: '取消已盘',
          cancelButtonText: '保留已盘',
          ...ST_MSGBOX_OPTS
        }
      )
      stockTakeMarkInflight.add(k)
      try {
        const ok = await persistMobileStockTakeMark(row, { is_checked: false, counted_quantity: null })
        if (ok) {
          lineState[k].checked = false
          lineState[k].countedStr = ''
          lineStoreRevision.value++
        }
      } finally {
        stockTakeMarkInflight.delete(k)
      }
    } catch {
      /* keep checked — 受控回写 */
      lineState[k].checked = true
      lineStoreRevision.value++
    }
  } else if (want) {
    // 先乐观勾上，避免受控 checkbox 弹回导致「勾不上」
    lineState[k].checked = true
    lineStoreRevision.value++
    stockTakeMarkInflight.add(k)
    try {
      const cq = parseQtyInput(lineState[k].countedStr)
      const patch = cq != null ? { is_checked: true, counted_quantity: cq } : { is_checked: true }
      const ok = await persistMobileStockTakeMark(row, patch)
      if (ok) {
        lineState[k].checked = true
      } else {
        lineState[k].checked = prev
      }
      lineStoreRevision.value++
    } finally {
      stockTakeMarkInflight.delete(k)
    }
  }
}

/** 保证盘点行状态已初始化（模板里用，避免 undefined） */
function stateFor(row) {
  ensureLineState(row)
  return lineState[invRowKey(row)]
}

watch(
  invRows,
  rows => {
    const keep = new Set(rows.map(invRowKey))
    Object.keys(lineState).forEach(key => {
      if (!keep.has(key)) delete lineState[key]
    })
    rows.forEach(ensureLineState)
  },
  { deep: true }
)

async function flushCountedQtyToServer(row) {
  if (!row?.inventory_id || !stockTakeMode.value || !canUseStockTakePanel.value) return
  const k = invRowKey(row)
  ensureLineState(row)
  const s = lineState[k]
  if (!s) return
  await persistMobileStockTakeMark(row, {
    counted_quantity: parseQtyInput(s.countedStr)
  })
}

const _countedPersistDebouncers = new Map()
function debounceCountedPersist(row) {
  const k = invRowKey(row)
  if (_countedPersistDebouncers.has(k)) {
    clearTimeout(_countedPersistDebouncers.get(k))
  }
  _countedPersistDebouncers.set(
    k,
    setTimeout(() => {
      flushCountedQtyToServer(row)
      _countedPersistDebouncers.delete(k)
    }, 420)
  )
}

function normalizeCounted(row) {
  const k = invRowKey(row)
  const s = lineState[k]
  if (!s) return
  const n = parseFloat(String(s.countedStr || '').replace(/,/g, ''))
  if (!Number.isFinite(n)) {
    s.countedStr = ''
    flushCountedQtyToServer(row)
    return
  }
  const r = Math.round(n * 10000) / 10000
  s.countedStr = String(r)
  flushCountedQtyToServer(row)
}

function lineVariance(row) {
  const k = invRowKey(row)
  const s = lineState[k]
  if (!s) return null
  const c = parseFloat(String(s.countedStr || '').replace(/,/g, ''))
  if (!Number.isFinite(c)) return null
  return Math.round((c - s.book) * 10000) / 10000
}

function varClass(v) {
  if (v > 0) return 'var-plus'
  if (v < 0) return 'var-minus'
  return ''
}

const qtyDialogTitle = computed(() => (qtyMode.value === 'overflow' ? '报溢入库' : '报损出库'))

function pickTransactionTypeId(types, code, nameHints) {
  if (!Array.isArray(types) || !types.length) return null
  let t = types.find(x => x.type_code === code)
  if (t?.type_id) return t.type_id
  const want = (code || '').toLowerCase()
  t = types.find(x => String(x.type_code || '').toLowerCase() === want)
  if (t?.type_id) return t.type_id
  for (const h of nameHints) {
    t = types.find(x => (x.type_name || '').includes(h))
    if (t?.type_id) return t.type_id
  }
  return null
}

async function loadTxnMeta() {
  const extractList = (res) => {
    if (Array.isArray(res)) return res
    if (Array.isArray(res?.data)) return res.data
    if (Array.isArray(res?.items)) return res.items
    if (Array.isArray(res?.data?.items)) return res.data.items
    return []
  }
  try {
    const [typesRes, unitsRes] = await Promise.all([
      getInventoryTransactionTypes({ page: 1, per_page: 200, is_active: true }),
      getUnits({ page: 1, per_page: 500, is_active: true })
    ])
    if (typesRes && typesRes.success === false) {
      return { ok: false, error: typesRes.message || '加载交易类型失败' }
    }
    if (unitsRes && unitsRes.success === false) {
      return { ok: false, error: unitsRes.message || '加载单位失败（需库存作业或单位查看权限）' }
    }
    const types = extractList(typesRes)
    overflowTypeId.value = pickTransactionTypeId(types, 'OVERFLOW_IN', ['报溢', '盘盈'])
    lossTypeId.value = pickTransactionTypeId(types, 'LOSS_OUT', ['报损', '盘亏'])
    const units = extractList(unitsRes)
    const yard =
      units.find(u => {
        const c = String(u.unit_code || '').toLowerCase()
        return c === 'yd' || c === 'yard'
      }) || units.find(u => u.unit_name === '码' || String(u.unit_name || '').includes('码'))
    yardUnitId.value = yard?.unit_id ?? null
    const ok = !!(overflowTypeId.value && lossTypeId.value && yardUnitId.value)
    const miss = []
    if (!overflowTypeId.value) miss.push('报溢类型 OVERFLOW_IN')
    if (!lossTypeId.value) miss.push('报损类型 LOSS_OUT')
    if (!yardUnitId.value) miss.push('单位「码」')
    return { ok, error: miss.length ? `缺少 ${miss.join('、')}` : null }
  } catch (e) {
    console.error(e)
    const msg = e.response?.data?.message || e.message || '加载报溢报损配置失败'
    return { ok: false, error: msg }
  }
}

function matchExactBatchInList(list, kw) {
  const key = String(kw || '').trim()
  if (!key || !Array.isArray(list) || !list.length) return null
  return (
    list.find(b => String(b.manual_batch_number || '').trim() === key) ||
    list.find(b => String(b.system_batch_number || '').trim() === key) ||
    null
  )
}

function onAddBatchKeywordInput() {
  // 改了输入但尚未重新查询：作废上次查询结果，避免「像已确认」
  const cur = String(addBatchSearchKeyword.value || '').trim()
  if (addQueryStatus.value === 'idle') return
  if (cur === addQueriedKeyword.value) return
  addQueryStatus.value = 'idle'
  addQueriedKeyword.value = ''
  addQueryCandidates.value = []
  addForm.material_batch_id = null
  addSelectedBatchCache.value = null
  addInventoryPreviewRows.value = []
  addSelectedSourceInventoryId.value = null
  addPlacementMode.value = ''
  batchOptions.value = []
}

function selectAddBatchCandidate(batch) {
  if (!batch?.batch_id) return
  addForm.material_batch_id = batch.batch_id
  addSelectedBatchCache.value = batch
  if (!batchOptions.value.some(b => Number(b.batch_id) === Number(batch.batch_id))) {
    batchOptions.value = [batch, ...batchOptions.value]
  }
  addQueryStatus.value = 'found'
  addQueryCandidates.value = []
}

async function goIntakeFromAddNotFound() {
  const code = (addQueriedKeyword.value || addBatchSearchKeyword.value || '').trim()
  await openIntakeDialogFromAdd(code, addForm.qtyStr, addForm.storage_location_id)
}

async function runAddBatchQuery() {
  const kw = String(addBatchSearchKeyword.value || '').trim()
  if (!kw) {
    ElMessage.warning('请输入仓库料号或系统批号')
    return
  }
  addForm.material_batch_id = null
  addSelectedBatchCache.value = null
  addInventoryPreviewRows.value = []
  addSelectedSourceInventoryId.value = null
  addPlacementMode.value = ''
  addQueryCandidates.value = []
  addForm.qtyStr = ''
  resetAddBranchGrid()

  batchSearchLoading.value = true
  addQueriedKeyword.value = kw
  try {
    // 盘点新增：只按仓库料号/系统批号等业务字段匹配，不按内部 batch_id
    // （否则输入 2887 会误命中 batch_id=2887、仓库号实为 1704）
    const batchSearchParams = {
      search: kw,
      search_mode: 'exact',
      match_batch_id: 0,
      page: 1,
      per_page: 30,
      is_active: true
    }
    let list = []
    try {
      const exactRes = await getMaterialBatches(batchSearchParams)
      list = exactRes?.data || []
    } catch {
      list = []
    }
    let exact = matchExactBatchInList(list, kw)
    if (!exact && list.length === 0) {
      const fuzzyRes = await getMaterialBatches({
        ...batchSearchParams,
        search_mode: 'fuzzy'
      })
      list = fuzzyRes?.data || []
      exact = matchExactBatchInList(list, kw)
    }
    batchOptions.value = list
    if (exact) {
      selectAddBatchCandidate(exact)
      return
    }
    if (list.length > 0) {
      addQueryStatus.value = 'candidates'
      addQueryCandidates.value = list
      return
    }
    addQueryStatus.value = 'not_found'
  } catch (e) {
    addQueryStatus.value = 'not_found'
    ElMessage.error(e.response?.data?.message || e.message || '查询失败')
  } finally {
    batchSearchLoading.value = false
  }
}

function formatIdentityLinesFromBatch(batch) {
  const info = batch?.material_identity_info || {}
  const name =
    (info.material_category_name || '').trim() ||
    (info.material_identity_code || '').trim() ||
    '—'
  const colorCn = String(info.color_name_cn || '')
    .replace(/\s+/g, ' ')
    .trim() || '—'
  const colorEn = (info.color_name || '').trim() || '—'
  const spec = (info.specification || '').trim() || '—'
  return { name, colorCn, colorEn, spec }
}

async function fetchBatchDetailForAdd(batchId) {
  const id = Number(batchId)
  if (!Number.isFinite(id) || id <= 0) return null
  const cached = batchOptions.value.find(b => Number(b.batch_id) === id)
  if (cached?.material_identity_info) return cached
  try {
    const res = await getMaterialBatch(id)
    if (res?.success && res.data) return res.data
    if (res?.batch_id) return res
    if (res?.data?.batch_id) return res.data
  } catch {
    /* ignore */
  }
  return cached || null
}

async function resolveBatchForAddSubmit() {
  const batchId =
    addForm.material_batch_id != null && addForm.material_batch_id !== ''
      ? Number(addForm.material_batch_id)
      : null
  if (Number.isFinite(batchId) && batchId > 0) {
    const detail = await fetchBatchDetailForAdd(batchId)
    if (detail) return detail
  }

  const kw = (addBatchSearchKeyword.value || '').trim()
  if (!kw) return null

  try {
    const res = await getMaterialBatches({
      search: kw,
      search_mode: 'exact',
      match_batch_id: 0,
      page: 1,
      per_page: 30,
      is_active: true
    })
    const list = res?.data || []
    const exact =
      list.find(b => String(b.manual_batch_number || '').trim() === kw) ||
      list.find(b => String(b.system_batch_number || '').trim() === kw) ||
      null
    if (exact) {
      addForm.material_batch_id = exact.batch_id
      if (!batchOptions.value.some(b => Number(b.batch_id) === Number(exact.batch_id))) {
        batchOptions.value = [exact, ...batchOptions.value]
      }
      return exact
    }
  } catch {
    /* ignore */
  }
  return null
}

async function openIntakeDialogFromAdd(code, qtyStr, locationId) {
  const prefill = {
    storage_location_id: locationId || null,
    warehouse_manual_code: (code || '').trim(),
    quantityStr: qtyStr != null ? String(qtyStr) : ''
  }
  addDialogOpen.value = false
  resetAddForm()
  openIntakeDialog(prefill)
}

function clearQtyInputs() {
  qtyForm.deltaInput = ''
  qtyForm.actualInput = ''
  qtyForm.notes = ''
}

function resetQtyForm() {
  clearQtyInputs()
  qtyTargetRow.value = null
}

function fmtStTxDate(s) {
  if (!s) return '—'
  return String(s).replace('T', ' ').slice(0, 19)
}

function stTxOutboundLabel(tx) {
  return formatInventoryTxOutboundObject(tx, stTxMaskOpts.value)
}

function stTxOrderContext(tx) {
  return formatInventoryTxOrderContext(tx)
}

function stTxSupplier(tx) {
  return formatInventoryTxSupplier(tx, stTxMaskOpts.value)
}

function resetStockTakeTxDrawer() {
  stTxRows.value = []
  stTxPage.value = 1
  stTxPages.value = 1
  stTxInventoryId.value = null
  stTxTargetLabel.value = ''
}

async function loadStockTakeTxPage(page) {
  const id = Number(stTxInventoryId.value)
  if (!id) return
  stTxLoading.value = true
  stTxPage.value = page
  try {
    const res = await getMaterialInventorySlotTransactions(id, {
      page,
      per_page: 20,
      scope: 'batch'
    })
    if (res?.success) {
      stTxRows.value = res.data || []
      const p = res.pagination || {}
      stTxPages.value = p.pages || 1
      stTxPage.value = p.page || page
    } else {
      ElMessage.error(res?.message || '加载交易记录失败')
      stTxRows.value = []
    }
  } catch (e) {
    if (e.response?.status === 403) {
      ElMessage.warning(e.response?.data?.message || '无权限查看交易记录')
    } else {
      ElMessage.error(e.response?.data?.message || e.message || '加载交易记录失败')
    }
    stTxRows.value = []
  } finally {
    stTxLoading.value = false
  }
}

function openStockTakeTxDrawer(row) {
  if (!canViewStockTakeTx.value) {
    ElMessage.warning('无权限查看交易记录')
    return
  }
  const invId = Number(row?.inventory_id)
  if (!invId) {
    ElMessage.warning('该行无库存明细，无法加载交易记录')
    return
  }
  const code =
    (row.warehouse_manual_code || '').trim() ||
    (row.material_batch?.manual_batch_number || '').trim() ||
    (row.material_batch?.system_batch_number || '').trim() ||
    (row.material_identity?.material_identity_code || '').trim() ||
    `#${invId}`
  const loc =
    row.storage_location?.location_name ||
    row.storage_location?.location_code ||
    ''
  stTxInventoryId.value = invId
  stTxTargetLabel.value = loc ? `${code} · ${loc}` : code
  stTxDrawerVisible.value = true
  loadStockTakeTxPage(1)
}

function stTxPrev() {
  if (stTxPage.value <= 1) return
  loadStockTakeTxPage(stTxPage.value - 1)
}

function stTxNext() {
  if (stTxPage.value >= stTxPages.value) return
  loadStockTakeTxPage(stTxPage.value + 1)
}

async function openQtyDialog(row, mode) {
  if (!canAdjustStock.value) {
    ElMessage.warning('无库存作业权限')
    return
  }
  // 每次打开都重新拉取，避免首次未加载或权限缓存导致误报「未配置」
  const meta = await loadTxnMeta()
  const needTypeId = mode === 'overflow' ? overflowTypeId.value : lossTypeId.value
  if (!needTypeId || !yardUnitId.value) {
    ElMessage.warning(
      meta?.error
        ? `无法${mode === 'overflow' ? '报溢' : '报损'}：${meta.error}`
        : `无法${mode === 'overflow' ? '报溢' : '报损'}：缺少交易类型或单位「码」`
    )
    return
  }
  clearQtyInputs()
  qtyMode.value = mode
  qtyTargetRow.value = row
  const book = lineBook(row)
  let counted = parseQtyInput(stateFor(row)?.countedStr)
  if (counted == null && row?.mobile_stock_take_counted_qty != null && row.mobile_stock_take_counted_qty !== '') {
    const n = Number(row.mobile_stock_take_counted_qty)
    if (Number.isFinite(n)) counted = n
  }
  if (counted != null) {
    qtyForm.actualInput = String(counted)
    const diff = Math.round((counted - book) * 10000) / 10000
    if (mode === 'overflow') {
      if (diff <= 0) {
        ElMessage.warning('实盘未大于账面，不能报溢；请核对实盘数量或改用报损')
        return
      }
      qtyForm.deltaInput = String(diff)
    } else {
      if (diff >= 0) {
        ElMessage.warning('实盘未小于账面，不能报损；请核对实盘数量或改用报溢')
        return
      }
      qtyForm.deltaInput = String(Math.abs(diff))
    }
  }
  qtyDialogVisible.value = true
}

function resolveOverflowQty(row) {
  const cur = lineBook(row)
  const d = parseQtyInput(qtyForm.deltaInput)
  const a = parseQtyInput(qtyForm.actualInput)
  if (d != null && d > 0) return d
  if (a != null) {
    const inc = Math.round((a - cur) * 10000) / 10000
    if (inc <= 0) return { error: '实际数量须大于账面，差额为报溢量' }
    return inc
  }
  return { error: '请填写增加数量或实际数量' }
}

function resolveLossQty(row) {
  const cur = lineBook(row)
  const d = parseQtyInput(qtyForm.deltaInput)
  const a = parseQtyInput(qtyForm.actualInput)
  if (d != null && d > 0) {
    if (d > cur) return { error: '减少数量不能大于账面' }
    return d
  }
  if (a != null) {
    if (a >= cur) return { error: '报损后实际数量须小于账面' }
    const loss = Math.round((cur - a) * 10000) / 10000
    if (loss <= 0) return { error: '差额须大于 0' }
    return loss
  }
  return { error: '请填写减少数量或实际数量' }
}

async function submitQtyTransaction() {
  const row = qtyTargetRow.value
  if (!row) {
    ElMessage.warning('未选中明细行，请关闭对话框后重新点报溢/报损')
    return
  }
  if (!yardUnitId.value) {
    await loadTxnMeta()
  }
  if (!yardUnitId.value) {
    ElMessage.warning('未解析到「码」单位，请稍后再试或联系管理员配置单位与权限')
    return
  }
  let tid = qtyMode.value === 'overflow' ? overflowTypeId.value : lossTypeId.value
  if (!tid) {
    await loadTxnMeta()
    tid = qtyMode.value === 'overflow' ? overflowTypeId.value : lossTypeId.value
  }
  if (!tid) {
    ElMessage.warning('未找到报溢/报损交易类型，请检查库存交易类型是否配置（如 OVERFLOW_IN / LOSS_OUT）')
    return
  }
  const resolved = qtyMode.value === 'overflow' ? resolveOverflowQty(row) : resolveLossQty(row)
  if (typeof resolved === 'object' && resolved?.error) {
    ElMessage.warning(resolved.error)
    return
  }
  const q = resolved
  if (!Number.isFinite(q) || q <= 0) {
    ElMessage.warning('数量无效')
    return
  }
  const batchId = Number(row.material_batch_id || row.material_batch?.batch_id)
  if (!Number.isFinite(batchId) || batchId <= 0) {
    ElMessage.warning('该行缺少料号，无法报溢/报损（库存以料号为准）')
    return
  }
  const locId = Number(row.storage_location_id || row.storage_location?.location_id)
  if (!Number.isFinite(locId) || locId <= 0) {
    ElMessage.warning('库位信息缺失，无法入账')
    return
  }
  const identityId = Number(
    row.material_identity?.identity_id ||
      row.material_identity?.material_identity_id ||
      row.material_batch?.material_identity_id
  )
  qtySubmitting.value = true
  try {
    const operator = userStore.user?.username || userStore.user?.name || '用户'
    const timeStr = new Date().toISOString().slice(0, 19).replace('T', ' ')
    const noteParts = [`[盘点移动端 ${operator} ${timeStr}]`, qtyForm.notes].filter(Boolean)
    const res = await createInventoryTransaction({
      transaction_type_id: tid,
      quantity: q,
      unit_id: yardUnitId.value,
      notes: noteParts.join(' '),
      inventory_id: row.inventory_id,
      material_batch_id: batchId,
      material_identity_id: Number.isFinite(identityId) && identityId > 0 ? identityId : undefined,
      storage_location_id: locId,
      location_id: locId,
      is_adjustment: true,
      related_order_type: 'adjustment'
    })
    const result = interpretInventoryTransactionResponse(res, '已提交审批')
    if (notifyInventoryTransactionResult(result)) {
      qtyDialogVisible.value = false
      if (!result.pendingApproval) {
        await loadInvRowsForCurrentScope()
        if (selectedWarehouseId.value) {
          loadLayout({ preserveBrowsePath: true, silent: true }).catch(() => {})
        }
      }
    } else {
      ElMessage.error(res?.message || '失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '失败')
  } finally {
    qtySubmitting.value = false
  }
}

async function saveStockTakeSession() {
  if (!canSaveStockTake.value) {
    ElMessage.warning('无权限保存盘点记录')
    return
  }
  if (!invScopeInOpenRound.value) {
    ElMessage.warning('当前库位不在进行中的盘点范围内')
    return
  }
  if (!invScopeNode.value) {
    ElMessage.warning('请先打开要确认的库位')
    return
  }
  if (pendingIntakeCount.value > 0) {
    try {
      await ElMessageBox.alert(
        `尚有 ${pendingIntakeCount.value} 条未知料号入库申请待桌面处理（合计 ${fmtQty(pendingIntakeQty.value)} 码 · ${pendingIntakeRolls.value} 支）。\n\n请先完成或撤销这些申请后再盖章。`,
        '无法确认已盘查',
        { type: 'warning', confirmButtonText: '知道了' }
      )
    } catch {
      /* ignore */
    }
    return
  }
  if (uncheckedInvRows.value.length > 0) {
    try {
      await ElMessageBox.alert(
        `尚有 ${uncheckedInvRows.value.length} 条未勾「已盘」：${uncheckedInvPreviewText.value}。\n\n请全部勾选后再盖章。若实物已不在本库位，请先移库或报损处理账面。`,
        '无法确认已盘查',
        { type: 'warning', confirmButtonText: '知道了' }
      )
    } catch {
      /* ignore */
    }
    return
  }
  savingSession.value = true
  try {
    const ids = (invSubtreeIds.value || []).map(Number).filter(Boolean)
    if (ids.length > 1) {
      const rowLabel = invScopeRowKey.value ? `排 ${invScopeRowKey.value}` : `共 ${ids.length} 库位`
      try {
        await ElMessageBox.confirm(
          `将为${rowLabel}内每个库位分别盖上「已盘查」印记（已盖过的会跳过）。确认？`,
          stampActionLabel.value,
          {
            type: 'info',
            confirmButtonText: '整排盖章',
            cancelButtonText: '取消',
            ...ST_MSGBOX_OPTS
          }
        )
      } catch {
        return
      }
      const data = await stampLocationsBatch({
        storageLocationIds: ids,
        warehouseLocationId: selectedWarehouseId.value,
        notes: invScopeRowKey.value
          ? `盘点·整排已盘查 · ${invScopeRowKey.value}`
          : `盘点·范围已盘查 · ${ids.length}库位`
      })
      if (data?.stamped?.length) {
        for (const s of data.stamped) {
          patchLayoutLastStockTake(s.storage_location_id, {
            session_id: s.session_id,
            operator_name: s.operator_name,
            checked_at: s.checked_at
          })
        }
      }
    } else {
      const data = await stampLocation({
        storageLocationId: invScopeNode.value.location_id,
        warehouseLocationId: selectedWarehouseId.value,
        locationName: invScopeNode.value.location_name
      })
      if (data && invScopeNode.value?.location_id != null) {
        patchLayoutLastStockTake(invScopeNode.value.location_id, data)
      }
    }
  } finally {
    savingSession.value = false
  }
}

const intakeBranchSumQty = computed(() => {
  const p = gridStringsToBranchDetailsPayload(intakeBranchGrid)
  return sumBranchDetails(p == null ? [] : p)
})

const intakeBranchOccupiedCount = computed(() => {
  const p = gridStringsToBranchDetailsPayload(intakeBranchGrid)
  return occupiedBranchCells(p == null ? [] : p).length
})

const intakeBranchActivePreviewLabel = computed(() => {
  const r = intakeBranchActiveCell.r
  const c = intakeBranchActiveCell.c
  if (r == null || c == null) return ''
  return `格 ${r + 1}${String.fromCharCode(65 + c)}`
})

const intakeBranchActivePreviewValue = computed(() => {
  const r = intakeBranchActiveCell.r
  const c = intakeBranchActiveCell.c
  if (r == null || c == null) return ''
  const s = (intakeBranchGrid[r][c] || '').trim()
  return s ? s : '（空）'
})

function resetIntakeBranchGrid() {
  intakeBranchActiveCell.r = null
  intakeBranchActiveCell.c = null
  for (let r = 0; r < BRANCH_GRID_ROWS; r++) {
    for (let c = 0; c < BRANCH_GRID_COLS; c++) {
      intakeBranchGrid[r][c] = ''
    }
  }
}

function clearIntakeBranchGrid() {
  resetIntakeBranchGrid()
}

function onIntakeBranchFocus(e, r, c) {
  intakeBranchActiveCell.r = r
  intakeBranchActiveCell.c = c
  const t = e.target
  if (t && typeof t.select === 'function') t.select()
}

function onIntakeBranchBlur(r, c) {
  intakeBranchGrid[r][c] = normalizeCellInputString(intakeBranchGrid[r][c])
}

watch(intakeBranchSumQty, s => {
  if (s > 0.0005) {
    intakeForm.quantityStr = formatAddQtyFromBranchSum(s)
  }
})

watch(intakeBranchOccupiedCount, n => {
  if (n > 0) intakeForm.rollCountStr = String(n)
})

function resolveDefaultIntakeLocationId(prefillId = null) {
  const pref = Number(prefillId)
  if (Number.isFinite(pref) && pref > 0) {
    const inOpts = addLocationOptions.value.some(l => Number(l.location_id) === pref)
    if (inOpts) return pref
  }
  const root = invScopeNode.value
  if (root?.location_type === 'bin' && root.location_id != null) {
    const rid = Number(root.location_id)
    if (addLocationOptions.value.some(l => Number(l.location_id) === rid)) return rid
    // 当前末级即使尚未进 options（类型标记异常），仍优先用当前库位
    if (Number.isFinite(rid) && rid > 0) return rid
  }
  if (addLocationOptions.value.length === 1) {
    return Number(addLocationOptions.value[0].location_id)
  }
  return null
}

function openIntakeDialog(prefill = null) {
  if (!canSaveStockTake.value) {
    ElMessage.warning('无权限提交申请')
    return
  }
  if (!addLocationOptions.value.length && !(invScopeNode.value?.location_type === 'bin')) {
    ElMessage.warning('当前汇总范围无可用库位，请进入末级库位或缩小汇总范围后再试')
    return
  }
  resetIntakeBranchGrid()
  intakeForm.storage_location_id = resolveDefaultIntakeLocationId(prefill?.storage_location_id)
  intakeForm.warehouse_manual_code = (prefill?.warehouse_manual_code || '').trim()
  intakeForm.quantityStr =
    prefill?.quantityStr != null && prefill.quantityStr !== '' ? String(prefill.quantityStr) : ''
  intakeForm.rollCountStr =
    prefill?.rollCountStr != null && prefill.rollCountStr !== '' ? String(prefill.rollCountStr) : ''
  intakeForm.applicant_notes = ''
  intakeDialogOpen.value = true
}

function resetIntakeForm() {
  intakeForm.storage_location_id = null
  intakeForm.warehouse_manual_code = ''
  intakeForm.quantityStr = ''
  intakeForm.rollCountStr = ''
  intakeForm.applicant_notes = ''
  intakeForm.mark_as_meter_actual = false
  intakeReapplyFromRejectedId.value = null
  resetIntakeBranchGrid()
}

async function submitIntakeRequest() {
  if (!canSaveStockTake.value) {
    ElMessage.warning('无权限提交申请')
    return
  }
  const code = (intakeForm.warehouse_manual_code || '').trim()
  if (!code) {
    ElMessage.warning('请填写仓库料号')
    return
  }
  if (!intakeForm.storage_location_id) {
    ElMessage.warning('请选择目标库位')
    return
  }
  const q = parseQtyInput(intakeForm.quantityStr)
  if (q == null || q <= 0) {
    ElMessage.warning('请填写大于 0 的数量')
    return
  }
  const branchPayload = gridStringsToBranchDetailsPayload(intakeBranchGrid)
  const branchSum = sumBranchDetails(branchPayload || [])
  let rollCount = null
  if (intakeBranchOccupiedCount.value > 0) {
    rollCount = intakeBranchOccupiedCount.value
  } else if ((intakeForm.rollCountStr || '').trim() !== '') {
    const rc = parseInt(String(intakeForm.rollCountStr).trim(), 10)
    if (!Number.isFinite(rc) || rc < 0) {
      ElMessage.warning('支数须为非负整数')
      return
    }
    rollCount = rc
  }
  intakeSubmitting.value = true
  try {
    const payload = {
      warehouse_location_id: selectedWarehouseId.value || undefined,
      storage_location_id: Number(intakeForm.storage_location_id),
      warehouse_manual_code: code,
      quantity: q,
      applicant_notes: (intakeForm.applicant_notes || '').trim() || undefined,
      mark_as_meter_actual: !!intakeForm.mark_as_meter_actual
    }
    if (rollCount != null) payload.roll_count = rollCount
    if (branchPayload && branchSum > 0.0005) payload.branch_details = branchPayload
    const res = await createInventoryManualCodeIntakeRequest(payload)
    if (res?.success) {
      const oldId = intakeReapplyFromRejectedId.value
      if (oldId) {
        try {
          await deleteInventoryManualCodeIntakeRequest(oldId)
        } catch (delErr) {
          console.warn('新申请已提交，但清除原驳回记录失败', delErr)
        }
        intakeReapplyFromRejectedId.value = null
      }
      ElMessage.success(res.message || '已提交申请')
      intakeDialogOpen.value = false
      rejectedDialogOpen.value = false
      await loadInvRowsForCurrentScope()
      if (selectedWarehouseId.value) {
        await loadLayout({ preserveBrowsePath: true, silent: true })
      }
    } else {
      ElMessage.error(res?.message || '提交失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '提交失败')
  } finally {
    intakeSubmitting.value = false
  }
}

function openRejectedIntakeDialog(row) {
  if (!isRejectedIntakeRow(row)) return
  rejectedDialogRow.value = row
  rejectedDialogOpen.value = true
}

function reapplyRejectedIntake(row) {
  const r = row || rejectedDialogRow.value
  if (!r || !isRejectedIntakeRow(r)) return
  rejectedDialogOpen.value = false
  intakeReapplyFromRejectedId.value = r.intake_request_id || null
  // 预填原申请内容
  intakeForm.storage_location_id = r.storage_location_id || null
  intakeForm.warehouse_manual_code = (r.warehouse_manual_code || '').trim()
  intakeForm.quantityStr =
    r.physical_quantity != null && Number(r.physical_quantity) > 0
      ? String(r.physical_quantity)
      : ''
  intakeForm.rollCountStr =
    r.roll_count != null && Number(r.roll_count) > 0 ? String(r.roll_count) : ''
  intakeForm.applicant_notes = (r.applicant_notes || '').trim()
  intakeForm.mark_as_meter_actual = !!r.mark_as_meter_actual
  // 分支格子
  resetIntakeBranchGrid()
  const bd = r.branch_details
  if (Array.isArray(bd) && bd.length) {
    for (const cell of bd) {
      const rr = Number(cell.row ?? cell.r)
      const cc = Number(cell.col ?? cell.c)
      const q = cell.quantity ?? cell.qty
      if (!Number.isFinite(rr) || !Number.isFinite(cc)) continue
      if (rr < 0 || rr >= BRANCH_GRID_ROWS || cc < 0 || cc >= BRANCH_GRID_COLS) continue
      if (q == null || String(q).trim() === '') continue
      intakeBranchGrid[rr][cc] = String(q)
    }
  }
  intakeDialogOpen.value = true
}

async function deleteRejectedIntake(row) {
  const r = row || rejectedDialogRow.value
  const id = r?.intake_request_id
  if (!id) {
    ElMessage.warning('无申请编号，无法删除')
    return
  }
  try {
    await ElMessageBox.confirm(
      '确定删除这条已驳回的申请？删除后清单中不再显示。',
      '删除驳回申请',
      {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
        ...ST_MSGBOX_OPTS
      }
    )
  } catch {
    return
  }
  rejectedDeleting.value = true
  try {
    const res = await deleteInventoryManualCodeIntakeRequest(id)
    if (res?.success) {
      ElMessage.success(res.message || '已删除')
      rejectedDialogOpen.value = false
      rejectedDialogRow.value = null
      await loadInvRowsForCurrentScope()
      if (selectedWarehouseId.value) {
        await loadLayout({ preserveBrowsePath: true, silent: true })
      }
    } else {
      ElMessage.error(res?.message || '删除失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '删除失败')
  } finally {
    rejectedDeleting.value = false
  }
}

function mapPendingIntakeToPlaceholder(req) {
  const locLabel = (req.storage_location_label || '').trim()
  let location_name = locLabel
  let location_code = ''
  const m = locLabel.match(/^(.*)\s*\(([^)]*)\)\s*$/)
  if (m) {
    location_name = m[1].trim()
    location_code = m[2].trim()
  }
  const status = (req.status || 'pending').toString()
  const rejected = status === 'rejected'
  return {
    _intake_placeholder: true,
    _pending_intake: !rejected,
    _rejected_intake: rejected,
    intake_status: status,
    intake_request_id: req.request_id,
    applicant_user_id: req.applicant_user_id ?? null,
    warehouse_manual_code: req.warehouse_manual_code,
    physical_quantity: Number(req.quantity) || 0,
    roll_count: req.roll_count,
    branch_details: req.branch_details || null,
    mark_as_meter_actual: !!req.mark_as_meter_actual,
    physical_run_verified_at: !rejected && req.mark_as_meter_actual ? 'pending' : null,
    applicant_notes: req.applicant_notes || '',
    review_notes: req.review_notes || '',
    storage_location_id: req.storage_location_id,
    storage_location: {
      location_id: req.storage_location_id,
      location_name: location_name || '—',
      location_code
    },
    material_identity: null,
    material_batch: null,
    inventory_id: null
  }
}

function canCancelPendingIntake(row) {
  if (!isPendingIntakeRow(row) || !row?.intake_request_id) return false
  const uid = Number(userStore.user?.user_id)
  const aid = Number(row.applicant_user_id)
  if (!Number.isFinite(uid) || uid <= 0) return false
  // 无申请人字段时仍展示按钮，由接口校验「仅本人」
  if (!Number.isFinite(aid) || aid <= 0) return true
  return aid === uid
}

async function cancelPendingIntake(row) {
  const id = row?.intake_request_id
  if (!id) {
    ElMessage.warning('无申请编号，无法撤销')
    return
  }
  const code = (row.warehouse_manual_code || '').trim() || `#${id}`
  try {
    await ElMessageBox.confirm(
      `确定撤销未知料号「${code}」的入库申请？撤销后清单中不再占用，可重新申请。`,
      '撤销申请',
      {
        confirmButtonText: '撤销',
        cancelButtonText: '取消',
        type: 'warning',
        ...ST_MSGBOX_OPTS
      }
    )
  } catch {
    return
  }
  intakeCancellingId.value = id
  try {
    const res = await cancelInventoryManualCodeIntakeRequest(id)
    if (res?.success) {
      ElMessage.success(res.message || '已撤销')
      await loadInvRowsForCurrentScope()
      if (selectedWarehouseId.value) {
        await loadLayout({ preserveBrowsePath: true, silent: true })
      }
    } else {
      ElMessage.error(res?.message || '撤销失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '撤销失败')
  } finally {
    intakeCancellingId.value = null
  }
}

function resetAddForm() {
  addForm.storage_location_id = null
  addForm.material_batch_id = null
  addForm.qtyStr = ''
  addForm.mark_as_meter_actual = false
  addBatchSearchKeyword.value = ''
  addQueriedKeyword.value = ''
  addQueryStatus.value = 'idle'
  addQueryCandidates.value = []
  addPlacementMode.value = ''
  batchOptions.value = []
  addSelectedBatchCache.value = null
  addInventoryPreviewRows.value = []
  addInventoryPreviewLoading.value = false
  addSelectedSourceInventoryId.value = null
  resetAddBranchGrid()
}

watch(addDialogOpen, v => {
  if (!v) return
  if (addForm.storage_location_id) return
  const root = invScopeNode.value
  if (root?.location_type === 'bin' && root.location_id != null) {
    addForm.storage_location_id = root.location_id
    return
  }
  if (addLocationOptions.value.length === 1) {
    addForm.storage_location_id = addLocationOptions.value[0].location_id
  }
})

watch(intakeDialogOpen, v => {
  if (!v) return
  if (intakeForm.storage_location_id) return
  const def = resolveDefaultIntakeLocationId(null)
  if (def != null) intakeForm.storage_location_id = def
})

watch(
  () => addForm.material_batch_id,
  id => {
    refreshAddInventoryPreview(id)
  }
)

watch(
  () => addForm.storage_location_id,
  () => {
    if (!addForm.material_batch_id) return
    applyDefaultSourceSelection(addInventoryPreviewRows.value)
    syncAddTransferDefaultQty()
  }
)

watch([addElsewhereStockRows, addSelectedSourceInventoryId, addPlacementMode], () => {
  if (!addElsewhereStockRows.value.length || addAtTargetStockRows.value.length) {
    if (addPlacementMode.value) addPlacementMode.value = ''
    return
  }
  syncAddTransferDefaultQty()
})

watch(addPlacementMode, mode => {
  if (mode === 'create') {
    const s = addBranchSumQty.value
    if (s > 0.0005) addForm.qtyStr = formatAddQtyFromBranchSum(s)
    else if (!String(addForm.qtyStr || '').trim()) addForm.qtyStr = ''
  } else if (mode === 'transfer') {
    syncAddTransferDefaultQty()
  }
})

async function submitAddInventory() {
  if (!canAdjustStock.value) {
    ElMessage.warning('无权限')
    return
  }
  if (!addForm.storage_location_id) {
    ElMessage.warning('请选择放入库位')
    return
  }
  if (!addForm.material_batch_id) {
    if (addQueryStatus.value === 'not_found') {
      ElMessage.warning('系统暂无此料号，请点「去申请入库」')
    } else {
      ElMessage.warning('请先查询并确认料号')
    }
    return
  }

  addSubmitting.value = true
  try {
    const batch = await resolveBatchForAddSubmit()
    if (!batch) {
      ElMessage.warning('料号信息失效，请重新查询')
      return
    }

    const targetId = Number(addForm.storage_location_id)
    const batchId = Number(batch.batch_id)
    addForm.material_batch_id = batchId

    // 优先用①查询阶段已加载的在库预览，避免重复请求
    let rows = (addInventoryPreviewRows.value || []).filter(
      r => r.inventory_id && hasMeaningfulPhysicalYds(r.physical_quantity)
    )
    if (!rows.length) {
      const invRes = await getMaterialInventory({
        material_batch_id: batchId,
        only_inventory_row: true,
        has_stock: true,
        per_page: 500,
        page: 1
      })
      if (!invRes.success) {
        ElMessage.error(invRes.message || '查询现有库存失败')
        return
      }
      rows = (invRes.data || []).filter(
        r => r.inventory_id && hasMeaningfulPhysicalYds(r.physical_quantity)
      )
    }

    normalizeAddBranchCellsForSubmit()
    const branchPayload = gridStringsToBranchDetailsPayload(addBranchGrid)
    const branchSum = sumBranchDetails(branchPayload || [])
    const qtyParsed = parseQtyInput(addForm.qtyStr)

    const atTarget = rows.filter(r => locIdOfRow(r) === targetId)
    if (atTarget.length > 0) {
      ElMessage.warning('该料号在本库位已有在库记录，无需新增；请核对实盘或使用报溢/报损调整数量')
      return
    }

    const elsewhere = rows.filter(r => locIdOfRow(r) !== targetId)
    if (elsewhere.length > 0) {
      if (addPlacementMode.value !== 'transfer' && addPlacementMode.value !== 'create') {
        ElMessage.warning('他处已有该料号，请选择：移库到目标，或在目标新建库存（分开放置）')
        return
      }
    }

    let src = null
    if (addPlacementMode.value === 'transfer') {
      if (elsewhere.length === 1) {
        src = elsewhere[0]
      } else if (elsewhere.length > 1) {
        const sid = Number(addSelectedSourceInventoryId.value)
        src = elsewhere.find(r => Number(r.inventory_id) === sid) || null
        if (!src) {
          ElMessage.warning('该料号在多个库位有库存，请先选择「从哪个库位移入」')
          return
        }
      } else {
        ElMessage.warning('当前目标外没有可移入的库存，请改选「在目标新建库存」或更换目标库位')
        return
      }
    }

    if (addPlacementMode.value === 'transfer' && src) {
      const srcPhys = Number(src.physical_quantity)
      const srcXfer = resolveRowTransferableQty(src)
      let transferQty = srcXfer
      if (qtyParsed != null && qtyParsed > 0) {
        transferQty = Math.min(qtyParsed, srcXfer)
      }
      if (transferQty <= 0) {
        ElMessage.warning('移库数量无效；默认应移动来源库位全部账面数量')
        return
      }
      const srcName = resolveLocationLabelForAddRow(src)
      const tgtRow = layoutData.value.find(l => Number(l.location_id) === targetId)
      const tgtName = tgtRow?.location_name || tgtRow?.location_code || '目标库位'
      if (transferQty > srcXfer + 1e-6) {
        ElMessage.warning(`移库数量不能大于来源库位账面 ${fmtQty(srcXfer)} 码`)
        return
      }
      const partial = transferQty < srcPhys - 1e-6
      const qtyHint = partial
        ? `将从来源扣减 ${fmtQty(transferQty)} 码，剩余约 ${fmtQty(srcPhys - transferQty)} 码仍在「${srcName}」。`
        : `将移动全部账面 ${fmtQty(transferQty)} 码至「${tgtName}」。`
      try {
        await ElMessageBox.confirm(
          `来源：${srcName}\n账面 ${fmtQty(srcPhys)} 码，本次移库 ${fmtQty(transferQty)} 码\n目标：${tgtName}\n\n${qtyHint}\n\n确认执行移库？`,
          '移库确认',
          {
            type: 'warning',
            confirmButtonText: '确认移库',
            cancelButtonText: '取消',
            ...ST_MSGBOX_OPTS
          }
        )
      } catch {
        return
      }
      const tres = await transferMaterialInventoryLocation(src.inventory_id, {
        storage_location_id: targetId,
        notes: '移动端盘点·新增本范围库存改为移库',
        quantity: transferQty
      })
      if (tres?.success) {
        ElMessage.success(tres.message || '移库完成')
        const targetInvId = tres.data?.inventory_id
        if (targetInvId && branchPayload && branchSum > 0.0005 && canWriteSlotBranch.value) {
          try {
            const ur = await updateMaterialInventory(targetInvId, { branch_details: branchPayload })
            if (ur?.success) {
              ElMessage.info('已按本单格子写入「移入库位」库存行的分支明细。')
            } else {
              ElMessage.warning(ur?.message || '移库成功，但本库位分支明细未保存')
            }
          } catch (e) {
            ElMessage.warning(e.response?.data?.message || '移库成功，但写入本库位分支失败')
          }
        } else if (branchPayload && branchSum > 0.0005 && !canWriteSlotBranch.value) {
          ElMessage.info('移库成功；无权限写入本库位分支，可在有权限后「录入分支」补录。')
        }
        addDialogOpen.value = false
        resetAddForm()
        await loadInvRowsForCurrentScope()
        if (selectedWarehouseId.value) {
          loadLayout({ preserveBrowsePath: true, silent: true }).catch(() => {})
        }
      } else {
        ElMessage.error(tres?.message || '移库失败')
      }
      return
    }

    let finalQty = qtyParsed
    if (branchPayload && branchSum > 0.0005) {
      finalQty = branchSum
      if (qtyParsed != null && Math.abs(qtyParsed - branchSum) > 1e-4) {
        ElMessage.info('数量已按分支格子合计为准')
      }
    }
    if (finalQty == null || finalQty <= 0) {
      ElMessage.warning('请输入有效数量，或在分支格子中至少录入一格')
      return
    }

    // 新建本库库存 = 报溢入库（须审批），禁止无流水直改库存
    if (!yardUnitId.value || !overflowTypeId.value) {
      await loadTxnMeta()
    }
    if (!yardUnitId.value) {
      ElMessage.warning('未解析到「码」单位，请稍后再试或联系管理员配置单位与权限')
      return
    }
    if (!overflowTypeId.value) {
      ElMessage.warning('未找到报溢交易类型 OVERFLOW_IN，无法新建入账')
      return
    }
    const tgtRow = layoutData.value.find(l => Number(l.location_id) === targetId)
    const tgtName = tgtRow?.location_name || tgtRow?.location_code || '目标库位'
    try {
      await ElMessageBox.confirm(
        `将在「${tgtName}」按报溢入库 ${fmtQty(finalQty)} 码新建库存。\n\n与报损报溢相同：非管理员须审批通过后方可入账；管理员可直接入账。\n他处账面不变。确认提交？`,
        '报溢新建确认',
        {
          type: 'warning',
          confirmButtonText: '提交',
          cancelButtonText: '取消',
          ...ST_MSGBOX_OPTS
        }
      )
    } catch {
      return
    }

    const identityId = Number(
      batch.material_identity_id ||
        batch.material_identity?.identity_id ||
        batch.material_identity?.material_identity_id
    )
    const operator = userStore.user?.username || userStore.user?.name || '用户'
    const timeStr = new Date().toISOString().slice(0, 19).replace('T', ' ')
    const noteParts = [
      `[盘点移动端·新增本范围库存 ${operator} ${timeStr}]`,
      '按报溢入库入账（禁止无流水直建）'
    ]
    const res = await createInventoryTransaction({
      transaction_type_id: overflowTypeId.value,
      quantity: finalQty,
      unit_id: yardUnitId.value,
      notes: noteParts.join(' '),
      material_batch_id: batchId,
      material_identity_id: Number.isFinite(identityId) && identityId > 0 ? identityId : undefined,
      storage_location_id: targetId,
      location_id: targetId,
      is_adjustment: true,
      related_order_type: 'adjustment',
      inbound_mode: 'gain_loss',
      mark_as_meter_actual: !!addForm.mark_as_meter_actual,
      branch_details:
        branchPayload && branchSum > 0.0005 && canWriteSlotBranch.value ? branchPayload : undefined
    })
    const result = interpretInventoryTransactionResponse(res, '已提交审批')
    if (notifyInventoryTransactionResult(result)) {
      if (branchPayload && branchSum > 0.0005 && !canWriteSlotBranch.value) {
        ElMessage.info('无权限写入本库位分支格子；审批入账后可在有权限时于「录入分支」补录')
      }
      addDialogOpen.value = false
      resetAddForm()
      if (!result.pendingApproval) {
        await loadInvRowsForCurrentScope()
        if (selectedWarehouseId.value) {
          loadLayout({ preserveBrowsePath: true, silent: true }).catch(() => {})
        }
      }
    } else {
      ElMessage.error(res?.message || '失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '失败')
  } finally {
    addSubmitting.value = false
  }
}

function pickDefaultWarehouseId(list) {
  const rows = Array.isArray(list) ? list : []
  if (!rows.length) return null
  const byName = rows.find(w => {
    const name = String(w?.location_name || '').trim()
    const code = String(w?.location_code || '').trim()
    return name === '主仓库' || code === '主仓库'
  })
  if (byName?.location_id != null) return byName.location_id
  const soft = rows.find(w => String(w?.location_name || '').includes('主仓库'))
  if (soft?.location_id != null) return soft.location_id
  return rows[0]?.location_id ?? null
}

async function loadWarehouses() {
  const res = await getStorageLocations({ location_type: 'warehouse', is_active: true })
  warehouseList.value = res.data || []
  if (selectedWarehouseId.value == null && warehouseList.value.length) {
    const id = pickDefaultWarehouseId(warehouseList.value)
    if (id != null) {
      selectedWarehouseId.value = id
      await handleWarehouseChange()
    }
  }
}

/**
 * 为当前列表可见库位补拉库存预览/合计（lite layout 后用）
 */
async function enrichVisibleLocationCards() {
  const wid = selectedWarehouseId.value
  if (!wid) return
  const rows = folderChildrenWithMeta.value || []
  const need = rows
    .filter(r => r?.location_id != null && r.inventory_preview_loaded !== true)
    .map(r => Number(r.location_id))
    .filter(id => Number.isFinite(id))
  if (!need.length) return
  const seq = ++enrichCardsSeq
  try {
    const res = await getWarehouseLocationCards({
      warehouse_id: wid,
      location_ids: need.join(',')
    })
    if (seq !== enrichCardsSeq) return
    const map = res?.data || {}
    if (!map || typeof map !== 'object') return
    layoutData.value = layoutData.value.map(loc => {
      const patch = map[String(loc.location_id)] || map[loc.location_id]
      if (!patch) return loc
      return {
        ...loc,
        inventory_summary: patch.inventory_summary || loc.inventory_summary,
        inventory_lines_preview: patch.inventory_lines_preview || [],
        inventory_preview_truncated: !!patch.inventory_preview_truncated,
        inventory_preview_loaded: true,
        open_stock_take_round_id:
          patch.open_stock_take_round_id !== undefined
            ? patch.open_stock_take_round_id
            : loc.open_stock_take_round_id
      }
    })
  } catch (e) {
    console.warn('enrichVisibleLocationCards', e)
  }
}

function scheduleEnrichVisibleLocationCards() {
  if (enrichCardsTimer) clearTimeout(enrichCardsTimer)
  enrichCardsTimer = setTimeout(() => {
    enrichCardsTimer = null
    enrichVisibleLocationCards()
  }, 80)
}

/**
 * @param {{ preserveBrowsePath?: boolean, silent?: boolean, force?: boolean }} [opts]
 *   preserveBrowsePath 为 true 时不重置面包屑（保存盘点后后台刷新用）
 *   silent 为 true 时不占全页 loading，避免长时间请求卡住界面
 *   force 为 true 时忽略 silent 短路，必须重新拉版面（重盘后用）
 * 移动端默认 lite=1：只拉库位树，当前层再补库存明细，避免整仓超时。
 */
async function loadLayout(opts = {}) {
  const { preserveBrowsePath = false, silent = false, force = false } = opts
  if (!selectedWarehouseId.value) return

  if (loadLayoutInflight) {
    try {
      await loadLayoutInflight
    } catch {
      /* ignore previous failure; continue with new attempt below if needed */
    }
    // 已有成功缓存且本次 silent：不重复打满（force 时必须重拉）
    if (!force && silent && layoutData.value.length) {
      scheduleEnrichVisibleLocationCards()
      return
    }
  }

  const run = (async () => {
    if (!silent) loading.value = true
    try {
      const res = await getWarehouseLayout({
        warehouse_id: selectedWarehouseId.value,
        lite: 1
      })
      layoutData.value = (res.data || []).map(loc => ({
        ...loc,
        inventory_preview_loaded: loc.inventory_preview_loaded === true
      }))
      if (Array.isArray(res.open_stock_take_rounds)) {
        applyOpenRoundsList(res.open_stock_take_rounds)
      } else if (res.open_stock_take_round) {
        applyOpenRoundsList([res.open_stock_take_round])
      } else {
        await loadOpenRound()
      }
      // 轮次进度后台刷新，不阻塞树展示
      Promise.resolve()
        .then(async () => {
          for (const r of openRounds.value) {
            try {
              await refreshRoundProgress(r.round_id, { mergeStamps: true })
            } catch {
              /* ignore */
            }
          }
        })
        .catch(() => {})
      if (!preserveBrowsePath) resetBrowsePath()
      scheduleEnrichVisibleLocationCards()
    } catch (e) {
      console.error(e)
      if (!silent) {
        ElMessage.error(
          layoutData.value.length
            ? '刷新仓库结构失败，已保留上次数据，可稍后重试'
            : '加载仓库结构失败，请检查网络后点「刷新」'
        )
      }
      // 有旧数据时不清空，避免跳转/浏览全挂
      if (!preserveBrowsePath && !layoutData.value.length) {
        /* keep empty */
      }
    } finally {
      if (!silent) loading.value = false
    }
  })()

  loadLayoutInflight = run
  try {
    await run
  } finally {
    if (loadLayoutInflight === run) loadLayoutInflight = null
  }
}

/** 保存盘点接口返回的 session，用于立即更新列表「已盘查」而不必等全量 layout；传 null 清除 */
function patchLayoutLastStockTake(locationId, sessionPayload) {
  if (locationId == null) return
  const lid = Number(locationId)
  let patch = null
  if (sessionPayload != null) {
    const checkedAt =
      sessionPayload.checked_at ||
      sessionPayload.updated_at ||
      sessionPayload.created_at
    patch = {
      operator_name: sessionPayload.operator_name,
      checked_at: checkedAt,
      session_id: sessionPayload.session_id,
      round_id: sessionPayload.round_id
    }
  }
  layoutData.value = layoutData.value.map(row =>
    Number(row.location_id) === lid ? { ...row, last_stock_take: patch } : row
  )
}

function resetBrowsePath() {
  clearLocationJump()
  const w = selectedWarehouse.value
  if (w) {
    browsePath.value = [{ id: w.location_id, name: w.location_name }]
  } else {
    browsePath.value = []
  }
}

async function handleWarehouseChange() {
  clearLocationJump()
  await loadLayout()
}

async function refreshWarehouseLayout() {
  await loadLayout({ preserveBrowsePath: true, silent: false })
  if (invDrawerVisible.value && invSubtreeIds.value.length) {
    await loadInvRowsForCurrentScope()
  }
}

/** 抽屉内：重新拉取本范围库存行 + 静默刷新仓库版面（与保存分支后一致） */
async function refreshInventoryDrawer() {
  await loadInvRowsForCurrentScope()
  if (selectedWarehouseId.value) {
    await loadLayout({ preserveBrowsePath: true, silent: true })
  }
}

async function openInventory(loc) {
  invScopeNode.value = loc
  invScopeSeedLoc.value = loc
  const sub = collectSubtreeLocationIds(loc.location_id)
  invSubtreeIds.value = sub
  invScopeMode.value = sub.length > 1 ? 'subtree' : 'single'
  invScopeRowKey.value = null
  invPageIndex.value = 0
  invDrawerVisible.value = true
  invRows.value = []
  /** 不按抽屉打开清空勾选：勾选已持久化到本机 localStorage（按 inventory_id）；仅由 invRows watcher 剪掉不在列表中的 key */
  await loadInvRowsForCurrentScope()
  // 版面后台静默刷新，不阻塞打开
  if (selectedWarehouseId.value) {
    setTimeout(() => {
      loadLayout({ preserveBrowsePath: true, silent: true }).catch(() => {})
    }, 0)
  }
}

async function openRowInventory(loc) {
  if (!loc) return
  const { rowKey, locationIds, members } = collectSameRowLocations(layoutData.value, loc)
  if (!locationIds.length) {
    await openInventory(loc)
    return
  }
  invScopeSeedLoc.value = loc
  invScopeNode.value = loc
  invSubtreeIds.value = locationIds
  invScopeMode.value = locationIds.length > 1 ? 'row' : 'single'
  invScopeRowKey.value = rowKey
  invPageIndex.value = 0
  invDrawerVisible.value = true
  invRows.value = []
  if (members.length > 1) {
    ElMessage.success(`已加载本排 ${rowKey}：${members[0].location_code}～${members[members.length - 1].location_code}`)
  }
  await loadInvRowsForCurrentScope()
  if (selectedWarehouseId.value) {
    setTimeout(() => {
      loadLayout({ preserveBrowsePath: true, silent: true }).catch(() => {})
    }, 0)
  }
}

function expandCurrentScopeToRow() {
  if (!invScopeNode.value) return
  openRowInventory(invScopeNode.value)
}

function goBrowseCrumb(index) {
  clearLocationJump()
  browsePath.value = browsePath.value.slice(0, index + 1)
}

function enterFolder(loc) {
  clearLocationJump()
  browsePath.value.push({ id: loc.location_id, name: loc.location_name })
}

function onRowClick(item) {
  if (item.hasChildren) {
    enterFolder(item)
  } else {
    openInventory(item)
  }
}

async function fetchInventoryForLocationId(id) {
  return getMaterialInventory({
    storage_location_id: id,
    per_page: 500,
    page: 1,
    only_inventory_row: true,
    is_active: true,
    has_stock: true
  })
}

async function mapPool(items, concurrency, mapper) {
  const results = new Array(items.length)
  let next = 0
  async function worker() {
    while (next < items.length) {
      const i = next
      next += 1
      results[i] = await mapper(items[i], i)
    }
  }
  const n = Math.min(concurrency, Math.max(1, items.length))
  await Promise.all(Array.from({ length: n }, () => worker()))
  return results
}

async function fetchPendingIntakeForScope(ids) {
  if (!ids?.length) return []
  try {
    const res = await listInventoryManualCodeIntakeRequests({
      status: 'pending,rejected',
      storage_location_ids: ids.join(','),
      page: 1,
      per_page: 500
    })
    if (!res?.success || !Array.isArray(res.data)) return []
    return res.data.map(mapPendingIntakeToPlaceholder)
  } catch (e) {
    console.warn('加载待处理/驳回未知料号申请失败', e)
    return []
  }
}

async function loadInvRowsForCurrentScope() {
  const ids = invSubtreeIds.value
  if (!ids.length) {
    invRows.value = []
    return
  }
  invLoading.value = true
  try {
    if (ids.length > 100) {
      ElMessage.warning('下级位置较多，加载可能需要片刻')
    }
    const responses =
      ids.length === 1
        ? [await fetchInventoryForLocationId(ids[0])]
        : await mapPool(ids, 8, id => fetchInventoryForLocationId(id))
    const merged = []
    const seen = new Set()
    for (const res of responses) {
      if (!res?.success || !res.data) continue
      for (const row of res.data) {
        if (!inventoryRowVisibleInBrowseDrawer(row)) continue
        const iid = row.inventory_id
        if (iid != null) {
          if (seen.has(iid)) continue
          seen.add(iid)
        }
        merged.push(row)
      }
    }
    const pending = await fetchPendingIntakeForScope(ids)
    for (const p of pending) merged.push(p)
    invRows.value = merged
    invPageIndex.value = 0
    merged.forEach((row) => ensureLineState(row, { syncCheckedFromServer: true }))
    lineStoreRevision.value++
  } catch (e) {
    console.error(e)
    ElMessage.error('加载库存失败')
  } finally {
    invLoading.value = false
  }
}

async function loadBranchPreviewForInvRow(row) {
  if (!row.inventory_id) return null
  branchQuickLoading.value = row.inventory_id
  try {
    const res = await getMaterialInventoryRecord(row.inventory_id)
    if (res.success && res.data) {
      branchPreviewDetail.value = res.data
      branchPreviewInventoryId.value = row.inventory_id
      return res.data
    }
    ElMessage.error(res.message || '加载料号信息失败')
    return null
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
    return null
  } finally {
    branchQuickLoading.value = null
  }
}

async function openBranchView(row) {
  if (!row.inventory_id) {
    ElMessage.info('该行无库存记录')
    return
  }
  const d = await loadBranchPreviewForInvRow(row)
  if (d?.material_batch) {
    branchViewOpen.value = true
  } else {
    ElMessage.warning('无料号信息')
  }
}

async function openBranchEdit(row) {
  if (!row.inventory_id) {
    ElMessage.info('该行无库存记录')
    return
  }
  if (!canWriteSlotBranch.value) {
    ElMessage.warning('无权限录入本库位分支（需库存作业或料号编辑）')
    return
  }
  const d = await loadBranchPreviewForInvRow(row)
  if (!d?.material_batch) {
    ElMessage.warning('无料号信息')
    return
  }
  const branchEnabled =
    d.material_batch.enable_branch_management ?? row.material_batch?.enable_branch_management
  if (!branchEnabled) {
    ElMessage.warning('请先在上方打开「分支」开关启用分支管理，再录入格子')
    return
  }
  branchEditOpen.value = true
}

async function onBranchSavedFromDrawer() {
  const id = branchPreviewInventoryId.value
  if (id) {
    try {
      const res = await getMaterialInventoryRecord(id)
      if (res.success && res.data) {
        branchPreviewDetail.value = res.data
      }
    } catch (e) {
      console.error(e)
    }
  }
  await loadInvRowsForCurrentScope()
  if (selectedWarehouseId.value) {
    await loadLayout({ preserveBrowsePath: true, silent: true })
  }
}

async function onToggleBranchMgmt(row, val) {
  const bid = row.material_batch?.batch_id
  if (!bid) return
  branchToggleLoading.value = row.inventory_id
  try {
    const res = await updateMaterialBatch(bid, { enable_branch_management: !!val })
    if (res.success) {
      row.material_batch.enable_branch_management = !!val
      ElMessage.success(val ? '已启用分支管理' : '已关闭分支管理')
      if (selectedWarehouseId.value) {
        loadLayout({ preserveBrowsePath: true, silent: true })
      }
    } else {
      ElMessage.error(res.message || '更新失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '更新失败')
  } finally {
    branchToggleLoading.value = null
  }
}

async function onDrawerClosed() {
  invScopeNode.value = null
  invScopeSeedLoc.value = null
  invRows.value = []
  invSubtreeIds.value = []
  invScopeMode.value = 'single'
  invScopeRowKey.value = null
  invPageIndex.value = 0
  stockTakeMode.value = false
  Object.keys(lineState).forEach(k => delete lineState[k])
  branchViewOpen.value = false
  branchEditOpen.value = false
  branchPreviewDetail.value = null
  branchPreviewInventoryId.value = null
  if (selectedWarehouseId.value) {
    loadLayout({ preserveBrowsePath: true, silent: true }).catch(() => {})
  }
}

/** 切到底栏其它板块时：关掉盘点抽屉/弹层，避免 keep-alive 残留宽内容把全站撑超屏 */
function closeStockTakeOverlays() {
  const el = invDrawerBodyRef.value
  unbindInvDrawerTouchListeners(el)
  invDrawerVisible.value = false
  stockTakeMode.value = false
  qtyDialogVisible.value = false
  addDialogOpen.value = false
  intakeDialogOpen.value = false
  mobileStartVisible.value = false
  branchViewOpen.value = false
  branchEditOpen.value = false
  resetBodyElLoadingArtifacts()
}

onMounted(async () => {
  userStore.initFromStorage()
  if (!canAccess.value) return
  await Promise.all([loadWarehouses(), loadTxnMeta()])
})

onActivated(async () => {
  if (!canAccess.value) return
  if (invDrawerVisible.value && invSubtreeIds.value.length) {
    await refreshInventoryDrawer()
  }
})

onDeactivated(() => {
  closeStockTakeOverlays()
})
</script>

<style scoped>
.m-page {
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  overflow-x: hidden;
  box-sizing: border-box;
}

.m-intro {
  margin: 0 0 12px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
}

.m-toolbar {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  margin-bottom: 12px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.m-toolbar-select,
.m-jump-input,
.m-toolbar-btn {
  width: 100% !important;
  max-width: 100%;
  box-sizing: border-box;
}

.m-toolbar-select :deep(.el-select__wrapper),
.m-jump-input :deep(.el-input__wrapper) {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.m-jump-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.m-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.m-crumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.m-crumbs__label {
  font-size: 12px;
  color: #94a3b8;
}

.m-crumb {
  border: none;
  background: none;
  padding: 0;
  font-size: 14px;
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
}

.m-crumb:not(:last-child)::after {
  content: '/';
  margin-left: 6px;
  color: #cbd5e1;
  text-decoration: none;
}

.m-wh-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 120px;
}

.m-wh-cell {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  background: #fff;
  border-radius: 12px;
  padding: 14px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.m-wh-cell.has-children {
  border-left: 4px solid #3b82f6;
}

.m-wh-cell__main {
  flex: 1;
  min-width: 0;
}

.m-wh-cell__title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.m-wh-cell__name {
  font-weight: 600;
  font-size: 16px;
  color: #0f172a;
}

.m-wh-cell__code {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

.m-wh-cell__stock-take {
  font-size: 12px;
  color: #c62828;
  font-weight: 600;
  margin-top: 4px;
  line-height: 1.35;
  word-break: break-word;
}

.m-wh-cell__inv {
  font-size: 12px;
  color: #475569;
  margin-top: 6px;
}

.m-wh-cell__inv-total {
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 6px;
  line-height: 1.4;
}

.m-wh-cell__inv-line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 2px;
  line-height: 1.45;
  margin-top: 4px;
}

.m-wh-cell__inv-line--pending {
  color: #94a3b8;
}

.m-wh-cell__inv-line--pending .m-wh-cell__inv-code,
.m-wh-cell__inv-line--pending .m-wh-cell__inv-color,
.m-wh-cell__inv-line--pending .m-wh-cell__inv-qty,
.m-wh-cell__inv-line--pending .m-wh-cell__inv-rolls {
  color: #94a3b8;
}

.m-wh-cell__inv-line--unchecked {
  color: #dc2626;
  font-weight: 600;
}

.m-wh-cell__inv-line--unchecked .m-wh-cell__inv-code,
.m-wh-cell__inv-line--unchecked .m-wh-cell__inv-color,
.m-wh-cell__inv-line--unchecked .m-wh-cell__inv-qty,
.m-wh-cell__inv-line--unchecked .m-wh-cell__inv-rolls {
  color: #dc2626;
}

.m-wh-cell__st-pending {
  margin-left: 2px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.m-wh-cell__inv-line--rejected {
  background: #fef9c3;
  border-radius: 4px;
  padding: 1px 2px;
}

.m-wh-cell__inv-line--rejected .m-wh-cell__inv-code,
.m-wh-cell__inv-line--rejected .m-wh-cell__inv-color,
.m-wh-cell__inv-line--rejected .m-wh-cell__inv-qty,
.m-wh-cell__inv-line--rejected .m-wh-cell__inv-rolls {
  color: #a16207;
}

.m-wh-cell__st-rejected {
  margin-left: 2px;
  font-size: 12px;
  font-weight: 700;
  color: #b45309;
}

.m-wh-cell__inv-line:first-child {
  margin-top: 0;
}

.m-wh-cell__inv-code {
  font-weight: 600;
  color: #0f172a;
}

.m-wh-cell__inv-dot {
  color: #94a3b8;
  user-select: none;
}

.m-wh-cell__inv-color {
  color: #334155;
}

.m-wh-cell__inv-qty,
.m-wh-cell__inv-rolls {
  color: #475569;
}

.m-wh-cell__inv-notes {
  margin-left: 4px;
  color: #b45309;
  font-weight: 700;
  flex-shrink: 0;
}

.m-wh-cell__st-tick {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 4px;
  border-radius: 50%;
  background: #ecfdf5;
  color: #059669;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
  flex-shrink: 0;
}

.m-wh-cell__inv-more {
  margin-top: 4px;
  font-size: 11px;
  color: #94a3b8;
}

.m-wh-cell__empty {
  display: inline-block;
  font-size: 2.5rem;
  font-weight: 800;
  color: #16a34a;
  line-height: 1;
  letter-spacing: 0.12em;
  user-select: none;
}

.m-wh-cell__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}

.m-wh-cell__stamped {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: #059669;
  text-align: center;
  padding: 4px 0;
}

.m-inv-pane-tabs {
  display: flex;
  gap: 0;
  margin-top: 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
}

.m-inv-pane-tab {
  flex: 1;
  border: 0;
  background: #f8fafc;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 6px;
  cursor: pointer;
}

.m-inv-pane-tab.active {
  background: #2563eb;
  color: #fff;
}

.m-inv-pane-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
}

.m-inv-pane-swipe {
  margin-top: 4px;
}

.m-inv-pane--list {
  padding-bottom: 12px;
}

.m-inv-list-total {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.m-inv-list-line {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 4px;
  font-size: 13px;
  line-height: 1.45;
  padding: 6px 2px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  cursor: pointer;
}

.m-inv-list-line--pending {
  color: #94a3b8;
}

.m-inv-list-line--rejected {
  background: #fef9c3;
  border-radius: 6px;
  padding-left: 6px;
  padding-right: 6px;
  color: #854d0e;
  font-weight: 600;
  border-bottom-color: #fde68a;
}

.m-inv-list-line__rejected {
  margin-left: 4px;
  color: #b45309;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.m-inv-list-line--unchecked {
  color: #dc2626;
  font-weight: 600;
}

.m-inv-list-line--checked {
  color: #0f172a;
}

.m-inv-list-line__tick {
  margin-left: 4px;
  color: #16a34a;
  font-weight: 700;
}

.m-inv-list-line__pending {
  margin-left: 4px;
  color: #64748b;
  font-weight: 700;
}

.m-inv-list-line__loc {
  font-weight: 700;
  color: #0369a1;
  flex-shrink: 0;
}

.m-inv-list-line__notes {
  margin-left: 4px;
  color: #b45309;
  font-weight: 700;
  flex-shrink: 0;
}

.m-inv-card__notes-ro {
  margin-top: 8px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.4;
}

.m-inv-card__notes-ro p {
  margin: 0 0 4px;
}

.m-st-notes-block {
  margin-top: 12px;
  padding: 10px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.m-st-notes-block--in-st {
  margin-top: 10px;
}

.m-st-notes-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
}

.m-st-notes-form :deep(.el-form-item) {
  margin-bottom: 8px;
}

.m-inv-row-hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
}

.m-inv-list-tip {
  margin: 10px 0 0;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
}

.m-inv-back-list {
  align-self: flex-start;
}

.m-inv-pager {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 10px 0 4px;
  max-width: 100%;
}

.m-inv-pager__select {
  width: 100%;
}

.m-inv-pager__nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
}

.m-inv-pager__pos {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  flex: 1 1 auto;
  text-align: center;
  min-width: 4em;
  white-space: nowrap;
}

.m-drawer-head {
  margin-bottom: 12px;
}

.m-inv-scope {
  margin: 0 0 8px;
  font-size: 13px;
  color: #64748b;
}

.m-inv-last-take {
  margin: 0 0 10px;
  font-size: 13px;
  color: #c62828;
  font-weight: 600;
}

.m-drawer-toolbar {
  margin-bottom: 10px;
}

.m-st-mode {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.m-st-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.m-st-hint {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
}

.m-warn {
  font-size: 12px;
  color: #d97706;
  margin: 0 0 8px;
}

:deep(.m-inv-drawer.el-drawer) {
  width: 100% !important;
  max-width: 100vw;
  overflow: hidden;
  box-sizing: border-box;
}

:deep(.m-inv-drawer .el-drawer__body) {
  padding: 0;
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
  height: 100%;
}

/* 整抽屉一个滚动容器：工具栏+标签+清单/明细连成一页，避免中间截断 */
.m-inv-drawer-body {
  height: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior-y: contain;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0));
}

.m-drawer-head {
  padding: 4px 16px 8px;
  box-sizing: border-box;
}

.m-inv-pane-swipe {
  overflow: visible;
  padding: 0 16px 8px;
  box-sizing: border-box;
}

.m-inv-pane--list,
.m-inv-pane--detail {
  overflow: visible;
}

.m-inv-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: visible;
  max-width: 100%;
  min-height: 120px;
  padding: 8px 0 16px;
  box-sizing: border-box;
}

.m-inv-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.m-inv-card.st-on {
  background: #fff;
  border-color: #bfdbfe;
}

.m-inv-card.st-pending {
  background: #f1f5f9;
  border: 1px dashed #94a3b8;
  opacity: 0.92;
  color: #64748b;
}

.m-inv-card.st-rejected {
  background: #fefce8;
  border: 1px solid #facc15;
  color: #713f12;
}

.m-inv-pending-badge {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
  letter-spacing: 0.02em;
}

.m-inv-rejected-badge {
  font-size: 12px;
  font-weight: 600;
  color: #a16207;
  margin-bottom: 8px;
}

.m-inv-reject-link {
  border: none;
  background: none;
  padding: 0;
  color: #b45309;
  font-weight: 800;
  text-decoration: underline;
  cursor: pointer;
}

.m-inv-pending-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 10px;
}

.m-inv-rejected-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.m-rej-meta {
  margin: 0 0 10px;
  font-size: 14px;
  color: #334155;
  line-height: 1.45;
}

.m-rej-reason-label {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.m-rej-reason-box {
  padding: 10px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 14px;
  color: #713f12;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.m-st-summary {
  margin: 6px 0 0;
  font-size: 13px;
  color: #334155;
  line-height: 1.45;
}

.m-inv-card__line {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: #0f172a;
  min-width: 0;
}

.m-inv-card__muted {
  font-size: 13px;
  color: #64748b;
  margin-top: 6px;
}

.m-inv-card__spec {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.35;
  word-break: break-word;
}

.m-st-qty-block {
  margin-top: 12px;
  padding: 10px;
  border-radius: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}

.m-st-qty-book {
  font-size: 14px;
  color: #1e40af;
  margin-bottom: 8px;
}

.m-st-row--counted .m-st-label {
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
}

.m-st-row--counted {
  flex-wrap: wrap;
}

.m-st-unit {
  font-size: 13px;
  color: #475569;
  flex-shrink: 0;
}

.m-inv-card__branch {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e2e8f0;
}

.m-branch-enable-row {
  margin-top: 0;
}

.m-inv-branch-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  margin-top: 6px;
}

.m-branch-hint {
  margin-top: 6px;
  margin-bottom: 0;
  font-size: 12px;
}

.m-inv-code {
  font-weight: 600;
  min-width: 0;
  word-break: break-word;
}

.m-inv-qty {
  font-weight: 700;
  font-size: 15px;
  color: #059669;
  min-width: 0;
  word-break: break-word;
  text-align: right;
}

.m-qty-hint {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: #b45309;
  margin-left: 4px;
}

.muted {
  color: #64748b;
  font-size: 13px;
}

.m-st-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
  min-width: 0;
  max-width: 100%;
}

.m-meter-actual-hint {
  color: #c0392b;
  font-weight: 600;
}

.m-color-card-edit {
  margin-top: 8px;
}

.m-color-card-hint {
  color: #c0392b;
  font-weight: 600;
}

.m-st-label {
  flex-shrink: 0;
  width: 40px;
  font-size: 13px;
  color: #475569;
}

.m-st-input {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.m-st-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.m-st-tx-hint {
  margin-top: 6px;
}

.m-st-tx-drawer {
  padding: 0 4px 16px;
}

.m-st-tx-target {
  margin-bottom: 8px;
  font-weight: 600;
  color: #334155;
}

.m-st-tx-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fff;
}

.m-st-tx-card__head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}

.m-st-tx-card__head .muted,
.m-st-tx-line.muted {
  color: #64748b;
  font-weight: 400;
}

.m-st-tx-line {
  font-size: 13px;
  line-height: 1.45;
  color: #334155;
  margin-top: 2px;
}

.m-st-tx-balances {
  margin-top: 4px;
}

.m-st-tx-balance-line {
  padding-left: 8px;
}

.m-st-tx-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
}

.m-st-var {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
}

.m-st-var.var-plus {
  color: #059669;
}

.m-st-var.var-minus {
  color: #dc2626;
}

.m-qty-cur {
  margin: 0 0 10px;
  font-size: 14px;
}

.m-muted {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 10px;
}

.m-no-access {
  padding: 40px 16px;
}

.m-add-identity {
  margin-bottom: 10px;
  padding: 8px 10px;
  background: #f8fafc;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.45;
}
.m-add-identity-line {
  margin: 2px 0;
  color: #334155;
}
.m-add-preview {  margin-bottom: 12px;
  min-height: 24px;
}

.m-add-preview-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.m-add-preview-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.m-add-preview-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 13px;
}

.m-add-preview-loc {
  flex: 1;
  min-width: 0;
  color: #334155;
}

.m-add-preview-qty {
  font-weight: 600;
  color: #0f172a;
}

.m-add-loc-hint {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
}

.m-add-query-row {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
}

.m-add-query-row .el-input {
  flex: 1;
  min-width: 0;
}

.m-add-query-result {
  margin: 0 0 12px;
}

.m-add-not-found {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 600;
  color: #dc2626;
  line-height: 1.45;
}

.m-add-candidate {
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}

.m-add-candidate.is-active {
  border-color: #3b82f6;
  background: #eff6ff;
}

.m-add-candidate-codes {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.m-add-candidate-meta {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
}

.m-add-step2-title {
  margin: 4px 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.m-add-mode {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.m-add-mode :deep(.el-radio) {
  height: auto;
  white-space: normal;
  margin-right: 0;
}
.m-add-alert {
  margin-bottom: 10px;
}

.m-add-radio {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.m-add-src :deep(.el-radio) {
  margin-right: 0;
  white-space: normal;
  height: auto;
  line-height: 1.35;
}

.m-st-branch-block {
  margin-bottom: 14px;
  padding: 10px 0;
  border-top: 1px dashed #e2e8f0;
}

.m-st-branch-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.m-st-branch-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.m-st-branch-hint {
  margin: 0 0 10px;
  font-size: 12px;
  line-height: 1.45;
}

.m-st-branch-sum {
  margin: 0 0 10px;
  font-size: 14px;
  color: #0f172a;
}

.m-st-branch-sum-note {
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
}

.m-st-br-active-preview {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
  border: 1px solid #bfdbfe;
  min-height: 52px;
}

.m-st-br-active-label {
  font-size: 14px;
  font-weight: 700;
  color: #334155;
  flex-shrink: 0;
}

.m-st-br-active-value {
  flex: 1;
  text-align: right;
  font-size: clamp(22px, 6.5vw, 30px);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  color: #0f172a;
  word-break: break-all;
}

/* 与 MobileBatchBranchEditDrawer 同一套 11 列网格；minmax(0) 避免小屏撑破整页 */
.m-st-br-grid-wrap {
  --m-st-br-label-col: 2rem;
  display: grid;
  grid-template-columns: var(--m-st-br-label-col) repeat(10, minmax(0, 1fr));
  gap: 4px;
  align-items: stretch;
  max-width: 100%;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 6px;
  box-sizing: border-box;
}

.m-st-br-corner {
  min-width: 0;
}

.m-st-br-h {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  line-height: 1.2;
}

.m-st-br-row-lab {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  line-height: 1.2;
}

.m-st-br-cell-wrap {
  min-width: 0;
}

.m-st-br-input {
  width: 100%;
  min-height: 48px;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  background: #fff;
  padding: 6px 4px;
  line-height: 1.2;
}

.m-st-br-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
</style>

<!-- append-to-body 后 scoped 可能打不中抽屉高度链，用全局类保证可滚动 -->
<style>
.m-inv-drawer.el-drawer {
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  width: 100% !important;
  max-width: 100vw !important;
  box-sizing: border-box;
}

/* 盖过盘点 el-drawer（约 2000+）；iOS 合成层不吃内联 z-index 时靠 class */
.m-stock-take-front-overlay.el-overlay,
.el-overlay.m-stock-take-front-overlay {
  z-index: 5000 !important;
}
.m-stock-take-front-dialog.el-dialog {
  z-index: 5001 !important;
}

/* MessageBox 须高于 5000/5200 业务弹窗，否则移库/删除确认被挡住 */
.m-stock-take-msgbox-overlay.el-overlay,
.el-overlay.m-stock-take-msgbox-overlay,
.el-overlay.is-message-box.m-stock-take-msgbox-overlay {
  z-index: 5600 !important;
}
.m-stock-take-msgbox.el-message-box,
.el-message-box.m-stock-take-msgbox {
  z-index: 5601 !important;
}

/* 批量作废印记：日期面板须高于弹窗，否则无法点选 */
.m-stock-take-datepicker-popper.el-popper,
.el-picker__popper.m-stock-take-datepicker-popper,
.m-stock-take-datepicker-popper {
  z-index: 5700 !important;
}

/* 弹窗内下拉：避免 teleport 到 body 后被 z-index 5000 遮罩挡住无法点选 */
.m-stock-take-front-dialog .m-stock-take-select-popper,
.m-stock-take-select-popper {
  z-index: 5100 !important;
}

.m-inv-drawer .el-drawer__header {
  flex: 0 0 auto;
  margin-bottom: 0;
  padding: 12px 16px 8px;
}

.m-inv-drawer .el-drawer__body {
  flex: 1 1 0% !important;
  min-height: 0 !important;
  height: auto !important;
  max-height: none !important;
  padding: 0 !important;
  overflow: hidden !important;
  box-sizing: border-box;
}

/* 整抽屉单一滚动：头+标签+内容一起滑，取消中间截断与双滚动条 */
.m-inv-drawer .m-inv-drawer-body {
  height: 100%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior-y: contain;
  background: #fff;
  box-sizing: border-box;
}

.m-inv-drawer .m-drawer-head,
.m-inv-drawer .m-inv-pane-swipe,
.m-inv-drawer .m-inv-pane--list,
.m-inv-drawer .m-inv-pane--detail,
.m-inv-drawer .m-inv-cards {
  max-height: none !important;
  overflow: visible !important;
}
</style>
